import type { CalendarId, Meeting } from './data'
import { buildCallsListQuery, type CallsListQueryOpts } from './lib/calls/callsListParams'
// Dedup review-cluster contract adapter (worker→FE translation + FE-facing
// types). The worker emits a mismatched field/enum shape; this pure, tested
// module is the single transform used by fetchDedupReviewClusters below.
import {
  mapWorkerDedupResponse,
  tierToWorker,
  type DedupTier,
  type DedupMember,
  type DedupCluster,
  type DedupReviewResponse,
  type RawDedupReviewResponse,
} from './lib/contacts/dedupClusterAdapter'

// In production (ops.botpros.ai) use relative path; in dev use full URL.
// `VITE_API_BASE` lets PR-preview builds (.github/workflows/pr-preview.yml)
// point at a per-PR Worker on workers.dev so API changes are exercisable
// before merge — the var is baked at build time, no runtime branch.
const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ||
  (typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://ops.botpros.ai/api'
    : '/api')


// ---------------------------------------------------------------------------
// Raw API types
// ---------------------------------------------------------------------------

export type RawCalendarUser = {
  email: string
  userId: number
  section: number
  name: string
  role: string
}

type RawCalendarEvent = {
  id: string
  userId: number
  userName: string
  userEmail: string
  title: string
  description: string
  dateFrom: string
  dateTo: string
  location: string
  section: number
  isAllDay: boolean
  // The appointed host (organizer) of the event, set server-side. May differ
  // from userName, which reflects the scanned mailbox.
  host?: string
  organizerEmail?: string
  attendees?: Array<{ email: string; name?: string }>
  // Resolved (server-side) linked-contact email + name for the stored client link.
  clientEmail?: string | null
  clientName?: string | null
  // NULL = normal appointment; 'time_off' = approved time off; 'holiday' = company holiday banner.
  category?: 'time_off' | 'holiday' | null
  // Explicit calendar-source tag for SYNTHETIC server-merged events (e.g.
  // subcontractor project starts). When set to a CalendarId, convertCalendarEvent
  // assigns that calendar directly and BYPASSES title-keyword classification —
  // synthetic events must not be mis-bucketed by classifyEvent().
  calendarSource?: CalendarId
}

// ---------------------------------------------------------------------------
// Parse event date into {date, time}.
// Accepts two formats:
//   1. Legacy prior-CRM: "MM/DD/YYYY HH:MM:SS am/pm" (from calendar.event.get)
//   2. ISO 8601:      "YYYY-MM-DDTHH:MM:SS[Z|±HH:MM]" (from our D1 calendar_events)
// ---------------------------------------------------------------------------

function parseCalendarDate(raw: string): { date: string; time: string } {
  if (!raw) return { date: '2026-01-01', time: '00:00' }

  // ISO 8601 first — our D1-sourced events use this
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (iso) {
    const [, year, month, day, hour, min] = iso
    return { date: `${year}-${month}-${day}`, time: `${hour}:${min}` }
  }

  // Legacy prior-CRM format
  const match = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2}):\d{2}\s+(am|pm)$/i,
  )
  if (!match) return { date: '2026-01-01', time: '00:00' }

  const [, month, day, year, hourStr, min, ampm] = match
  let hour = parseInt(hourStr, 10)
  if (ampm.toLowerCase() === 'pm' && hour !== 12) hour += 12
  if (ampm.toLowerCase() === 'am' && hour === 12) hour = 0

  return {
    date: `${year}-${month}-${day}`,
    time: `${String(hour).padStart(2, '0')}:${min}`,
  }
}

function calcDuration(from: string, to: string): string {
  const f = parseCalendarDate(from)
  const t = parseCalendarDate(to)
  const diffMs =
    new Date(`${t.date}T${t.time}:00`).getTime() -
    new Date(`${f.date}T${f.time}:00`).getTime()
  const diffMin = Math.round(diffMs / 60000)

  if (diffMin <= 0) return '30m'
  if (diffMin >= 480) return `${Math.round(diffMin / 60)}h`
  if (diffMin >= 60 && diffMin % 60 === 0) return `${diffMin / 60}h`
  if (diffMin >= 60) return `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`
  return `${diffMin}m`
}

// ---------------------------------------------------------------------------
// Classify event by title prefix
// ---------------------------------------------------------------------------

const ESTIMATE_PREFIX = /^\((TD|DH|LC|MR|WC|L)\)\s*/i

// Estimator initials → names (these are people, not event types)
const ESTIMATOR_INITIALS_MAP: Record<string, string> = {
  TD: 'Tony',
  DH: 'Drew',
  LC: 'Leo',
  MR: 'Max Ritchie',
  WC: 'Will',
  L: 'Leo',
}

function classifyEvent(title: string): {
  calendar: CalendarId
  type: string
  cleanTitle: string
} {
  const match = title.match(ESTIMATE_PREFIX)
  if (match) {
    const prefix = match[1].toUpperCase()
    return {
      calendar: 'estimates',
      type: ESTIMATOR_INITIALS_MAP[prefix] ?? prefix,
      cleanTitle: title.replace(ESTIMATE_PREFIX, '').replace(/^-\s*/, '').trim(),
    }
  }

  if (/^start\s/i.test(title)) {
    return { calendar: 'projects', type: 'Project Start', cleanTitle: title }
  }

  // Keywords that signal project events
  if (/\b(punch list|inspection|walkthrough|closeout|sign-off|delivery)\b/i.test(title)) {
    return { calendar: 'projects', type: 'Project', cleanTitle: title }
  }

  return { calendar: 'internal', type: 'Meeting', cleanTitle: title }
}

// ---------------------------------------------------------------------------
// Extract account name from title / description
// ---------------------------------------------------------------------------

function extractAccount(title: string, description: string): string {
  // "(MR) Jermane Mack, 123 Main Street - MR"  →  "Jermane Mack"
  const afterPrefix = title.replace(/^\([A-Z]+\)\s*/, '').replace(/^-\s*/, '')
  const commaIdx = afterPrefix.indexOf(',')
  if (commaIdx > 0) return afterPrefix.substring(0, commaIdx).trim()

  // "Start Yolette Rosier paint project"  →  "Yolette Rosier"
  const startMatch = title.match(/^Start\s+(.+?)(?:\s+paint\s+project)?$/i)
  if (startMatch) return startMatch[1].trim()

  // "Complaint: Leo meeting with homeowner..." → just "Complaint"
  if (/^complaint/i.test(afterPrefix)) return 'Complaint'

  // Description: "Customer Name: Jermane Mack"
  const custMatch = description.match(/Customer Name:\s*(.+)/i)
  if (custMatch) return custMatch[1].trim()

  // Remove trailing " - XX" codes
  const dashIdx = afterPrefix.lastIndexOf(' - ')
  if (dashIdx > 0) return afterPrefix.substring(0, dashIdx).trim()

  // Truncate very long account names (likely raw descriptions)
  const cleaned = afterPrefix.trim() || title.trim()
  return cleaned.length > 60 ? cleaned.substring(0, 57) + '...' : cleaned
}

function cleanLocation(loc: string): string {
  if (!loc) return ''
  // Remove duplicated address in parens: "123 Main (123 Main, City, ST)" → "123 Main, City, ST"
  const parenMatch = loc.match(/^(.+?)\s*\((.+)\)\s*$/)
  if (parenMatch) {
    const inner = parenMatch[2].trim()
    return inner
  }
  // Extract address from Google Maps URL query param. Some legacy-sourced
  // events have addresses with stray single `%` characters that aren't
  // valid percent-encoded escapes — decodeURIComponent throws on those
  // with "URI malformed" and takes the whole calendar load down with it.
  // Fall back to the raw query fragment (with `+` normalized to spaces)
  // on failure.
  const mapsMatch = loc.match(/https?:\/\/maps\.google\.com\/?\?q=([^&\s]+)/)
  if (mapsMatch) {
    const beforeUrl = loc.replace(/https?:\/\/\S+/g, '').trim()
    const rawQ = mapsMatch[1].replace(/\+/g, ' ')
    let decoded = rawQ
    try { decoded = decodeURIComponent(rawQ) } catch { /* malformed — use raw */ }
    return beforeUrl || decoded
  }
  // Strip any other URLs
  const stripped = loc.replace(/https?:\/\/\S+/g, '').trim()
  return stripped || loc
}

// ---------------------------------------------------------------------------
// Convert raw API event → Meeting
// ---------------------------------------------------------------------------

// Shift a "YYYY-MM-DD" date string by n days, returning "YYYY-MM-DD".
// UTC arithmetic keeps the calendar date stable (no DST/local-tz drift).
function addDays(dateKey: string, n: number): string {
  const [y, m, d] = dateKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}

export function convertCalendarEvent(raw: RawCalendarEvent): Meeting {
  const { date, time } = parseCalendarDate(raw.dateFrom)
  const duration = raw.isAllDay ? 'All day' : calcDuration(raw.dateFrom, raw.dateTo)
  // Inclusive last day of a multi-day ALL-DAY span. All-day events store an
  // EXCLUSIVE end (midnight of the day AFTER the last day, e.g. a 20→22 span has
  // end "23T00:00"), so when the end time is exactly midnight we step back one
  // day to get the real last day. Only set when it actually spans >1 day, so
  // single-day all-day events and any timed/partial event keep endDate absent
  // (and are NOT expanded across multiple day cells).
  let endDate: string | undefined
  if (raw.isAllDay && raw.dateTo) {
    const to = parseCalendarDate(raw.dateTo)
    const inclusiveEnd = to.time === '00:00' ? addDays(to.date, -1) : to.date
    if (inclusiveEnd > date) endDate = inclusiveEnd
  }
  // Synthetic server-merged events (e.g. subcontractor project starts) carry an
  // explicit calendarSource tag — honor it and SKIP keyword classification so they
  // land in their own layer instead of being bucketed as estimates/projects/internal.
  const { calendar, type, cleanTitle } = raw.calendarSource
    ? { calendar: raw.calendarSource, type: (raw as any).eventType || 'Project Start', cleanTitle: raw.title }
    : classifyEvent(raw.title)
  const account = extractAccount(raw.title, raw.description)

  let location = raw.location
  if (!location && raw.description) {
    const locMatch = raw.description.match(/(?:Address|Location):\s*(.+)/i)
    if (locMatch) location = locMatch[1].trim()
  }

  return {
    id: `BX-${raw.id}`,
    calendar,
    title: cleanTitle || raw.title,
    account,
    owner: raw.userName,
    // Prefer the appointed host; fall back to the scanned mailbox owner.
    host: raw.host || raw.userName,
    organizerEmail: raw.organizerEmail || raw.userEmail,
    date,
    time,
    duration,
    type,
    location: cleanLocation(location || ''),
    parsedAddress: (raw as any).parsedAddress || null,
    matchedClientId: (raw as any).matchedClientId || null,
    storedClientId: (raw as any).storedClientId ?? null,
    clientInvited: (raw as any).clientInvited ?? false,
    clientEmail: (raw as any).clientEmail ?? null,
    clientName: (raw as any).clientName ?? null,
    endDate,
    attendeeEmails: (raw.attendees ?? []).map(a => a.email),
    dealId: (raw as any).dealId ?? null,
    description: raw.description || null,
    category: raw.category ?? null,
  }
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchCalendarUsers(): Promise<RawCalendarUser[]> {
  const resp = await fetch(`${API_BASE}/calendar/users`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchCalendarEvents(
  from: string,
  to: string,
): Promise<Meeting[]> {
  // Try enriched endpoint first (includes client matching + parsed addresses)
  try {
    const resp = await fetch(`${API_BASE}/calendar/events-enriched?from=${from}&to=${to}`)
    if (resp.ok) {
      const raw: RawCalendarEvent[] = await resp.json()
      return raw.map(convertCalendarEvent)
    }
  } catch { /* fall through to standard endpoint */ }
  // Fallback to standard endpoint
  const resp = await fetch(`${API_BASE}/calendar/events?from=${from}&to=${to}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const raw: RawCalendarEvent[] = await resp.json()
  return raw.map(convertCalendarEvent)
}

export async function fetchUserDayEvents(userId: number, date: string): Promise<Array<{ title: string; dateFrom: string; dateTo: string; isAllDay: boolean }>> {
  const resp = await fetch(`${API_BASE}/calendar/events/${userId}?from=${date}&to=${date}`)
  if (!resp.ok) return []
  return resp.json()
}

// Batched free/busy for the availability picker — ONE getSchedule round-trip for
// all requested estimators (replaces N single-mailbox fetchUserDayEvents calls).
// Returns busy-only blocks (title "Busy") keyed by userId; missing/unknown ids
// come back as empty arrays. Privacy-safe by construction (no titles/attendees).
export async function fetchFreeBusy(
  userIds: number[],
  from: string,
  to: string,
): Promise<Record<number, Array<{ title: string; dateFrom: string; dateTo: string; isAllDay: boolean }>>> {
  if (userIds.length === 0) return {}
  const resp = await fetch(`${API_BASE}/calendar/freebusy?userIds=${userIds.join(',')}&from=${from}&to=${to}`)
  if (!resp.ok) return {}
  return resp.json()
}

export async function createCalendarEvent(params: {
  ownerUserId: number; eventName?: string; clientName: string; address?: string;
  date: string; time: string; durationMinutes: number;
  attendeeIds?: number[]; notes?: string; dealId?: number;
  clientId?: string | null; clientEmail?: string | null; inviteClient?: boolean;
}): Promise<{ success: boolean; eventId: string }> {
  const resp = await fetch(`${API_BASE}/calendar/events`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateCalendarEvent(eventId: string, params: {
  ownerUserId: number; clientName?: string; address?: string;
  date?: string; time?: string; durationMinutes?: number; notes?: string;
  clientId?: string | null; clientEmail?: string | null; inviteClient?: boolean;
}): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/calendar/events/${eventId}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteCalendarEvent(eventId: string, ownerUserId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/calendar/events/${eventId}?ownerUserId=${ownerUserId}`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}',
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Time off + company holidays
// ---------------------------------------------------------------------------

export type TimeOffRequest = {
  id: number; requester_email: string; requester_name: string; start_date: string; end_date: string;
  segment: 'full' | 'partial'; start_time: string | null; end_time: string | null;
  reason: string | null; status: 'pending' | 'approved' | 'denied' | 'cancelled';
  reviewer_email: string | null; review_note: string | null; created_at: string; decided_at: string | null
}
export type CompanyEventType = 'holiday' | 'day_off' | 'closure' | 'maintenance' | 'event' | 'other'
export type CompanyHoliday = {
  id: number; name: string; start_date: string; end_date: string;
  status: 'pending' | 'approved' | 'cancelled'; requested_by_email: string; reviewer_email: string | null; created_at: string;
  event_type?: CompanyEventType
  is_all_day?: number | boolean; start_time?: string | null; end_time?: string | null
}

export async function createTimeOffRequest(input: {
  start_date: string; end_date: string; segment: 'full' | 'partial'; start_time?: string; end_time?: string; reason?: string
}): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/time-off/requests`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function listTimeOffRequests(params?: { scope?: 'mine' | 'all'; status?: string }): Promise<{ data: TimeOffRequest[] }> {
  const q = new URLSearchParams()
  if (params?.scope) q.set('scope', params.scope)
  if (params?.status) q.set('status', params.status)
  const qs = q.toString()
  const resp = await fetch(`${API_BASE}/time-off/requests${qs ? '?' + qs : ''}`)
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function approveTimeOff(id: number): Promise<{ success: boolean; outlookEventId?: string }> {
  const resp = await fetch(`${API_BASE}/time-off/requests/${id}/approve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function denyTimeOff(id: number, note?: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/time-off/requests/${id}/deny`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note ?? '' }),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function cancelTimeOff(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/time-off/requests/${id}/cancel`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export type RecurringEventRule = {
  id: number
  name: string
  event_type: CompanyEventType
  recurrence_freq: 'weekly' | 'monthly' | 'annual'
  anchor_date: string
  recurrence_end_date: string | null
  status: string
  is_all_day: number | boolean
  start_time: string | null
  end_time: string | null
  next_occurrence_date: string | null
  occurrence_count: number
}

export async function createHoliday(input: { name: string; start_date: string; end_date: string; event_type?: CompanyEventType; is_all_day?: boolean; start_time?: string; end_time?: string; recurrence_freq?: 'none' | 'weekly' | 'monthly' | 'annual'; recurrence_end_date?: string }): Promise<{ success: boolean; id: number; status: string; recurring?: boolean; recurrence_freq?: string; occurrences_created?: number }> {
  const resp = await fetch(`${API_BASE}/holidays`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function listRecurringEvents(): Promise<{ data: RecurringEventRule[] }> {
  const resp = await fetch(`${API_BASE}/holidays/recurring`)
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateRecurringEvent(id: number, body: { name?: string; event_type?: CompanyEventType; recurrence_freq?: 'weekly' | 'monthly' | 'annual'; recurrence_end_date?: string | null }): Promise<{ success: boolean; id: number; occurrences_created: number; occurrences_removed: number }> {
  const resp = await fetch(`${API_BASE}/holidays/recurring/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteRecurringEvent(id: number, body: { scope: 'this' | 'future' | 'all'; occurrence_date?: string; child_id?: number }): Promise<{ success: boolean; scope: string; removed?: number }> {
  const resp = await fetch(`${API_BASE}/holidays/recurring/${id}`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function listHolidays(status?: string): Promise<{ data: CompanyHoliday[] }> {
  const resp = await fetch(`${API_BASE}/holidays${status ? '?status=' + encodeURIComponent(status) : ''}`)
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function approveHoliday(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/holidays/${id}/approve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function cancelHoliday(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/holidays/${id}/cancel`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteHoliday(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/holidays/${id}`, { method: 'DELETE' })
  if (!resp.ok) { const err = await resp.json() as { error: string }; throw new Error(err.error || `API error: ${resp.status}`) }
  return resp.json()
}

// ---------------------------------------------------------------------------
// CRM data types — UI-facing (camelCase for App.tsx compatibility)
// ---------------------------------------------------------------------------

export type CrmDeal = {
  id: string
  title: string
  stage: string
  stageLabel: string
  categoryId: number
  pipelineName: string
  opportunity: number
  assignedTo: string
  assignedById: number | null
  contactId: number | null
  contact_name: string | null
  project_count: number
  ticket_count: number
  companyId: number | null
  leadId: number | null
  comments: string | null
  sourceId: string | null
  salesPerson: string | null
  communityName: string | null
  referralName: string | null
  unifiedClientId: string | null
  closeDate: string
  dateCreated: string
  dateModified: string
  estimateAppointment: string | null
  notes: string | null
  lossReason: string | null
  rescheduleCount: number
  // Timestamp the deal was last moved into its current stage (migration 188).
  // Drives the "since <date>" sub-line under the No-Show/Reschedule badge.
  stageChangedAt: string | null
  // Newer pass-through columns consumed by the Deal Record UI.
  deal_type?: string | null
  is_hoa?: number | null
  appointment_address_id?: number | null
  // Deal-level "pinned" contact/email selections (migration 048).
  primaryContactId?: string | null
  primaryEmailId?: number | null
  billingEmailId?: number | null
  // Derived objects from GET /api/crm/deals/:id — hydrated by the API, not
  // persisted. Falls back to the contact's own is_primary / is_billing row
  // when no pinned override exists.
  effective_primary_email?: { id: number; address: string; is_primary: number; is_billing: number } | null
  effective_billing_email?: { id: number; address: string; is_primary: number; is_billing: number } | null
}

export type CrmLeadSuggestedUser = {
  user_id: number
  name: string
  is_lead: boolean
}

export type CrmLeadUserState = {
  read_at: string | null
  last_note_at: string | null
  last_action: string | null
}

export type CrmLead = {
  id: string
  title: string
  name: string
  status: string
  statusLabel: string
  source: string
  sourceLabel: string
  opportunity: number
  assignedTo: string
  assignedById: number | null
  phone: string
  email: string
  comments: string | null
  address: string | null
  addressCity: string | null
  addressState?: string | null
  addressZip?: string | null
  salesPerson: string | null
  communityName: string | null
  referralName: string | null
  propertyType: string | null
  unifiedClientId: string | null
  dateCreated: string
  dateModified: string
  lastCalledAt?: string | null
  lastEmailedAt?: string | null
  overdue?: 'call' | 'email' | 'both' | null
  // Lead routing (Carly's 4/28 spec) — populated by GET /api/crm/leads.
  suggestedZone: { zoneCode: string; displayName: string } | null
  suggestedUsers: CrmLeadSuggestedUser[]
  suggestedUserIds: number[]
  userState: CrmLeadUserState | null
  // Conversion state — populated by GET /api/crm/leads/:id (Track A). When a
  // lead has been converted into a deal, `isConverted` is true and
  // `convertedDealId` carries the survivor deal's id so the LeadRecord view can
  // redirect to #/deals/record/<id> instead of showing the stale lead shell.
  isConverted?: boolean
  convertedDealId?: number | null
}

export type PipelineStage = {
  stageId: string
  label: string
  color: string
  sort: number
  semantics?: string
  count: number
  value: number
}

export type LeadStatus = {
  status_id: string
  name: string
  color: string
  sort: number
  semantics: string
}

export type CrmContact = {
  id: string
  name: string
  phone: string
  email: string
  assignedTo: string
  dateModified: string
}

export type CrmClient = {
  unified_client_id: string
  legacy_master_client_id: string | null
  match_status: string
  client_type: string
  preferred_name: string
  nickname?: string | null
  normalized_name: string | null
  preferred_street: string | null
  preferred_city: string | null
  preferred_state: string | null
  preferred_zip: string | null
  preferred_phone: string | null
  preferred_email: string | null
  contact_count: number
  lead_count: number
  deal_count: number
  latest_lead_id: string | null
  latest_lead_title: string | null
  latest_lead_status: string | null
  latest_deal_id: string | null
  latest_deal_title: string | null
  latest_deal_stage: string | null
  latest_pipeline_name: string | null
  legacy_sources: string | null
  legacy_total_invoiced: number
  legacy_total_paid: number
  legacy_balance: number
  legacy_total_invoices: number
  legacy_total_estimates: number
  legacy_total_docs: number
  legacy_job_count: number
  aliases: string | null
  contact_type: string | null
  contact_status: string | null
  industry?: string | null
  company_type?: string | null
  org_primary_sub_type?: string | null
  org_sub_types?: string[] | null
  accounting_role?: string | null
  employees?: number | null
  annual_revenue?: number | null
  description?: string | null
  owner_id?: number | null
  owner_contact_id?: string | null
  created_at?: string
  archived_at?: string | null
  // Dedup survivor pointer — set on a merged-away duplicate shell so the record
  // view can redirect to the surviving canonical record. Null/absent for a
  // normal (un-merged) record, including a legitimately-archived active one.
  merged_into?: string | null
  // canonical-client-redirect-v2: set by GET /api/crm/clients/:id when the
  // REQUESTED id no longer exists in the clients view but merge_log resolves it
  // to a surviving record — the server returns that SURVIVOR here with
  // `redirected: true` + `canonical_id` (== the survivor's own unified_client_id)
  // + `requested_id` (the originally-requested loser id). The record view compares
  // requested_id != canonical_id to redirect instead of hard-404ing; comparing
  // canonical_id to the returned row's own id can't (they're identical on this
  // path). Absent on a normal fetch (id found directly).
  redirected?: boolean
  canonical_id?: string | null
  requested_id?: string | null
  logo_url?: string | null
  logo_shape?: 'circle' | 'rect' | null
}

export type CrmActivity = {
  id: number
  provider_id: string
  provider_type: string | null
  direction: number
  subject: string | null
  description: string | null
  status: number
  responsible_id: number
  author_id: number
  owner_type_id: number
  owner_id: number
  start_time: string | null
  end_time: string | null
  origin_id: string | null
  files: string | null
  settings: string | null
  created: string
  last_updated: string
  contact_id: string | null
  unified_client_id?: string | null
  rc_call_id: string | null
  rc_recording_id: string | null
  rc_recording_uri: string | null
  rc_session_id: string | null
  transcript: string | null
  summary: string | null
  sentiment: 'positive' | 'neutral' | 'negative' | null
  call_outcome: string | null
  phone_from: string | null
  phone_to: string | null
  call_result: string | null
  duration_seconds: number | null
  contact_name?: string
  // Calls-list (GET /api/crm/calls) enrichment + Grok analysis fields. All
  // optional — un-analyzed / un-enriched calls omit them:
  //  - ai_subject: concise AI topic line distilled from the transcript (drives
  //    the SUBJECT column; falls back to `summary` then the raw `subject`).
  //  - contact_hash: deep-link hash for the resolved person (CONTACT column),
  //    e.g. `/contacts/record/BX-123`.
  //  - linked_*: the single linked deal / lead / project rendered in the
  //    "Deal or project" column (never the contact — that has its own column).
  ai_subject?: string | null
  contact_hash?: string | null
  linked_type?: 'deal' | 'lead' | 'project' | 'contact' | null
  linked_id?: number | string | null
  linked_label?: string | null
  linked_hash?: string | null
  contact_type?: 'person' | 'organization' | null
  responsible_name?: string | null
  responsible_initials?: string | null
  // Reply-tracking ids stamped by the email webhook + Sent-Items poll. Used to
  // group thread replies into a single timeline entry on Contact / Deal / Lead
  // record pages and to render the "Linked to Deal" chip on Inbox threads.
  graph_message_id?: string | null
  graph_conversation_id?: string | null
  graph_internet_message_id?: string | null
  // Threaded comment back-pointer used by record-page reply UIs.
  parent_id?: number | null
  // calls-assign-v1: immutable ASSIGNED-column stamp — who placed (outbound)
  // or answered (inbound) the call, resolved through rc_user_mappings at
  // ingest/backfill time and never rewritten by later re-syncs or extension
  // reallocation. NULL for queue/IVR/unmapped extensions — never guessed.
  assigned_user_id?: number | null
  assigned_ext?: string | null
  assigned_user_name?: string | null
  assigned_user_initials?: string | null
  // calls-disposition-v1: AI-computed display-layer disposition, distinct
  // from RC's carrier-reported call_result and the manual call_outcome
  // (neither of which this ever overwrites). One of the enum strings in
  // src/lib/calls/dispositions.ts (VALID_DISPOSITIONS), or null/absent when
  // un-analyzed. See src/lib/calls/dispositions.ts + voicemailHeuristic.ts.
  ai_disposition?: string | null
}

export type CrmStats = {
  clients: number
  leads: number
  deals: number
  activities: number
  clientBreakdown: {
    matched: number
    imported: number
    legacyOnly: number
  }
}

export type CrmUser = {
  id: number
  name: string
  email: string | null
  initials: string | null
  role: string
  /** 1 if the row is flagged as admin via the legacy `is_admin` column.
   * Combined with `role` by canonicalRoleClient() to determine the
   * permission tier — Ashley-style users have role='Office' + is_admin=1
   * which maps to 'admin' tier, not 'office'. */
  is_admin?: number | null
  last_active_at?: string | null
}

// ---------------------------------------------------------------------------
// User ID → name mapping
// ---------------------------------------------------------------------------

const USER_NAMES: Record<number, string> = {
  1: 'Admin',
  6: 'Leandro',
  8: 'Maxwell Ritchie',
  31: 'Kadell Labs Bot',
  35: 'Carly BotPros',
  41: 'Ira',
  42: 'Nancy',
  48: 'Drew',
  49: 'Tony',
  52: 'HiperDevs',
  73: 'Leo',
  79: 'Jermane',
}

function userName(id: number | null | undefined): string {
  if (id == null || id === 0) return 'Unassigned'
  return USER_NAMES[id] ?? `User ${id}`
}

// Some legacy deal rows have `estimate_appointment` populated with
// a JSON-stringified calendar-event-id array (e.g. `'["1409"]'`) instead of an
// ISO datetime. That string is truthy, so the appointment picker's fallback
// chain `estimateAppointment || closeDate` short-circuits to garbage and
// renders the empty "Pick a date and time" placeholder even though `closeDate`
// holds a perfectly good date. Sanitize here so any non-date string becomes
// null and the picker falls through to closeDate. (Bug fix 2026-04-17.)
function sanitizeApptString(v: unknown): string | null {
  if (typeof v !== 'string' || v.length === 0) return null
  // Accept anything starting with YYYY-MM-DD; reject everything else.
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? v : null
}

// ---------------------------------------------------------------------------
// D1 row → UI type transformers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDeal(d: any): CrmDeal {
  return {
    id: String(d.id),
    title: d.title ?? '',
    stage: d.stage_id ?? '',
    stageLabel: d.stage_label ?? d.stage_id ?? '',
    categoryId: d.category_id ?? 0,
    pipelineName: d.pipeline_name ?? '',
    opportunity: d.opportunity ?? 0,
    assignedTo: userName(d.assigned_by_id),
    assignedById: d.assigned_by_id ?? null,
    contactId: d.contact_id ?? null,
    contact_name: d.contact_name ?? null,
    project_count: d.project_count ?? 0,
    ticket_count: d.ticket_count ?? 0,
    companyId: d.company_id ?? null,
    leadId: d.lead_id ?? null,
    comments: d.comments ?? null,
    sourceId: d.source_id ?? null,
    salesPerson: d.sales_person ?? null,
    communityName: d.community_name ?? null,
    referralName: d.referral_name ?? null,
    unifiedClientId: d.unified_client_id ?? null,
    closeDate: d.close_date ?? '',
    dateCreated: d.date_create ?? '',
    dateModified: d.date_modify ?? '',
    estimateAppointment: sanitizeApptString(d.estimate_appointment),
    notes: d.notes ?? null,
    lossReason: d.loss_reason ?? null,
    rescheduleCount: d.reschedule_count ?? 0,
    stageChangedAt: d.stage_changed_at ?? null,
    // Pass-through of newer columns consumed by the Deal Record UI.
    deal_type: d.deal_type ?? 'residential',
    is_hoa: d.is_hoa ?? 0,
    appointment_address_id: d.appointment_address_id ?? null,
    // Deal-level "pinned" contact/email selections (migration 048).
    primaryContactId: d.primary_contact_id ?? null,
    primaryEmailId: d.primary_email_id ?? null,
    billingEmailId: d.billing_email_id ?? null,
    // Derived objects only present on GET /api/crm/deals/:id responses.
    effective_primary_email: d.effective_primary_email ?? null,
    effective_billing_email: d.effective_billing_email ?? null,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapLead(l: any): CrmLead {
  const sz = l.suggested_zone
  const sUsers: CrmLeadSuggestedUser[] = Array.isArray(l.suggested_users)
    ? l.suggested_users.map((u: any) => ({ user_id: Number(u.user_id), name: String(u.name || ''), is_lead: !!u.is_lead }))
    : []
  const sIds: number[] = Array.isArray(l.suggested_user_ids)
    ? l.suggested_user_ids.map((n: any) => Number(n)).filter((n: number) => Number.isFinite(n))
    : sUsers.map((u) => u.user_id)
  return {
    id: String(l.id),
    title: l.title ?? '',
    name: l.customer_name ?? '',
    status: l.status_id ?? '',
    statusLabel: l.status_label ?? l.status_id ?? '',
    source: l.source_id ?? '',
    sourceLabel: l.source_label ?? l.source_id ?? '',
    opportunity: l.opportunity ?? 0,
    assignedTo: userName(l.assigned_by_id),
    assignedById: l.assigned_by_id ?? null,
    phone: l.phone ?? '',
    email: l.email ?? '',
    comments: l.comments ?? null,
    address: l.address ?? null,
    addressCity: l.address_city ?? null,
    addressState: l.address_state ?? null,
    addressZip: l.address_zip ?? null,
    salesPerson: l.sales_person ?? null,
    communityName: l.community_name ?? null,
    referralName: l.referral_name ?? null,
    propertyType: l.property_type ?? null,
    unifiedClientId: l.unified_client_id ?? null,
    dateCreated: l.date_create ?? '',
    dateModified: l.date_modify ?? '',
    lastCalledAt: l.last_called_at ?? null,
    lastEmailedAt: l.last_emailed_at ?? null,
    overdue: (l.overdue as any) ?? null,
    suggestedZone: sz ? { zoneCode: String(sz.zone_code || ''), displayName: String(sz.display_name || '') } : null,
    suggestedUsers: sUsers,
    suggestedUserIds: sIds,
    userState: l.user_state
      ? {
          read_at: l.user_state.read_at ?? null,
          last_note_at: l.user_state.last_note_at ?? null,
          last_action: l.user_state.last_action ?? null,
        }
      : null,
    open_task_count: Number(l.open_task_count || 0),
    // Conversion state — the lead detail row (Track A) carries `is_converted`
    // and the survivor deal id under one of several aliases. Normalize here so
    // the LeadRecord redirect logic has a single typed field to read.
    isConverted: !!(l.is_converted ?? l.isConverted) ||
      String(l.status_id ?? l.status ?? '').toUpperCase() === 'CONVERTED',
    convertedDealId: (() => {
      const raw = l.converted_deal_id ?? l.convertedDealId ?? l.deal_id ?? l.dealId ?? null
      const n = Number(raw)
      return raw != null && Number.isFinite(n) && n > 0 ? n : null
    })(),
  } as CrmLead
}

// ---------------------------------------------------------------------------
// CRM API calls — D1-backed
// ---------------------------------------------------------------------------

export async function fetchDeals(
  category = 0,
  page = 1,
  limit = 50,
  opts?: { excludeClosed?: boolean; stage?: string; search?: string; community?: string },
): Promise<{ total: number; pages: number; deals: CrmDeal[] }> {
  // When viewing archived/closed deals, drop the category filter so legacy
  // deals (which often have no/odd category_id) come through, AND pass
  // exclude_closed=0 to the API so won/lost terminal stages are returned.
  // `stage` narrows to a single stage_id (used by the Closed Deals tab to
  // show only CONTRACT_SIGNED without the rest of the terminal bucket).
  const excludeClosed = opts?.excludeClosed !== false // default true, matches legacy behaviour
  const params: string[] = []
  if (excludeClosed && category >= 0) params.push(`category=${category}`)
  params.push(`limit=${limit}`)
  params.push(`page=${page}`)
  if (!excludeClosed) params.push('exclude_closed=0')
  if (opts?.stage) params.push(`stage=${encodeURIComponent(opts.stage)}`)
  if (opts?.search) params.push(`search=${encodeURIComponent(opts.search)}`)
  if (opts?.community) params.push(`community=${encodeURIComponent(opts.community)}`)
  const resp = await fetch(`${API_BASE}/crm/deals?${params.join('&')}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return { total: data.total, pages: data.pages ?? Math.ceil((data.total || 0) / limit), deals: data.deals.map(mapDeal) }
}

// Accepts number OR string id: real deals are numeric, but the walkthrough
// Test Deal fixture uses the string id `TUT-test-deal-deal`. Coercing that to
// Number() yields NaN → GET /api/crm/deals/NaN, which misses the walkthrough
// fetch-shim's single-record fixture → 404 → the deal-detail deep-link catch
// reverts to #/deals (the "click Test deal reverts to the list" bug). The URL
// interpolation works identically for a numeric string, so no change for real deals.
export async function fetchDeal(id: number | string): Promise<CrmDeal> {
  const resp = await fetch(`${API_BASE}/crm/deals/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return mapDeal(await resp.json())
}

export async function fetchDealActivities(dealId: number, opts?: { limit?: number; offset?: number }): Promise<CrmActivity[]> {
  const qs = new URLSearchParams()
  if (opts?.limit) qs.set('limit', String(opts.limit))
  if (opts?.offset) qs.set('offset', String(opts.offset))
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/activities${qs.size ? `?${qs}` : ''}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function getDealProject(dealId: number) {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/project`)
  if (!resp.ok) return null
  return resp.json()
}

export async function fetchDealProjects(dealId: number | string): Promise<{ projects: CrmProject[]; allocated_total: number }> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/projects`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function getDealLead(dealId: number) {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/lead`)
  if (!resp.ok) return null
  return resp.json()
}

export async function getDealActivitiesMerged(dealId: number, opts?: { limit?: number; offset?: number }) {
  const qs = new URLSearchParams()
  if (opts?.limit) qs.set('limit', String(opts.limit))
  if (opts?.offset) qs.set('offset', String(opts.offset))
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/activities-merged${qs.size ? `?${qs}` : ''}`)
  if (!resp.ok) return []
  return resp.json()
}

export async function fetchPipeline(
  category = 0,
  includeTerminal = false,
  subsidiary?: string,
  wonFrom?: string,
  wonTo?: string,
): Promise<PipelineStage[]> {
  const params = new URLSearchParams({ category: String(category) })
  if (includeTerminal) params.set('includeTerminal', '1')
  if (subsidiary) params.set('subsidiary', subsidiary)
  if (wonFrom) params.set('wonFrom', wonFrom)
  if (wonTo) params.set('wonTo', wonTo)
  const resp = await fetch(`${API_BASE}/crm/pipeline?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteDeal(dealId: number | string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
}

export async function deleteProject(projectId: number | string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/projects/${encodeURIComponent(String(projectId))}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
}

export async function updateDealStage(dealId: number | string, stageId: string, opts?: { lossReason?: string; estimateAppointment?: string; newAppointment?: string; noShowReason?: string; sendConfirmationEmail?: boolean; newOwnerUserId?: number }): Promise<{ success: boolean; previousStage: string; newStage: string }> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/stage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': getCurrentUserIdFromPrefs() },
    body: JSON.stringify({ stageId, ...opts, actorUserId: Number(getCurrentUserIdFromPrefs()) }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Read current user id from prefs localStorage (same logic App.tsx uses)
function getCurrentUserIdFromPrefs(): string {
  try {
    const p = JSON.parse(localStorage.getItem('prefsSettings') || '{}')
    return p.userId ? String(p.userId) : '8'
  } catch { return '8' }
}

export async function updateDeal(dealId: number | string, fields: Record<string, unknown>): Promise<CrmDeal> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': getCurrentUserIdFromPrefs() },
    body: JSON.stringify({ ...fields, actorUserId: Number(getCurrentUserIdFromPrefs()) }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return mapDeal(await resp.json())
}

export type DealHistoryEntry = {
  id: number
  action: string
  user_id: number | null
  user_name: string | null
  user_email: string | null
  changes_json: string | null
  created_at: string
}

export async function fetchDealHistory(dealId: number | string): Promise<DealHistoryEntry[]> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/history`)
  if (!resp.ok) return []
  return resp.json()
}

export async function updateLead(leadId: number | string, fields: Record<string, unknown>): Promise<CrmLead> {
  const resp = await fetch(`${API_BASE}/crm/leads/${leadId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return mapLead(await resp.json())
}

export async function fetchLeadStatuses(): Promise<LeadStatus[]> {
  const resp = await fetch(`${API_BASE}/crm/lead-statuses`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchLeads(): Promise<{ total: number; leads: CrmLead[] }> {
  const resp = await fetch(`${API_BASE}/crm/leads?limit=100&all=1`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return { total: data.total, leads: data.leads.map(mapLead) }
}

export async function fetchLeadsPaginated(
  page = 1,
  limit = 50,
  opts?: { search?: string; status?: string; sort?: string; order?: string; all?: boolean; includeConverted?: boolean; includeJunk?: boolean; badge?: string; community?: string },
): Promise<{ total: number; leads: CrmLead[]; pages: number }> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (opts?.search) params.set('search', opts.search)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.order) params.set('order', opts.order)
  if (opts?.all) params.set('all', '1')
  if (opts?.includeConverted) params.set('include_converted', '1')
  if (opts?.includeJunk) params.set('include_junk', '1')
  if (opts?.badge) params.set('badge', opts.badge)
  if (opts?.community) params.set('community', opts.community)
  const resp = await fetch(`${API_BASE}/crm/leads?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return { total: data.total, leads: data.leads.map(mapLead), pages: data.pages }
}

export async function fetchLead(id: number): Promise<CrmLead> {
  const resp = await fetch(`${API_BASE}/crm/leads/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return mapLead(await resp.json())
}

export async function fetchLeadActivities(leadId: number): Promise<CrmActivity[]> {
  const resp = await fetch(`${API_BASE}/crm/leads/${leadId}/activities`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchClients(
  page = 1,
  limit = 50,
  opts?: {
    search?: string; status?: string; sort?: string; order?: string; owner?: string; type?: string;
    hasLeads?: boolean; hasDeals?: boolean;
    // Quick filter params
    email?: string; state?: string; zip?: string; city?: string; legal_name?: string; phone?: string;
    match_status?: string; client_type_filter?: string; data_quality?: string;
    lead_status?: string; deal_stage?: string; pipeline?: string; data_sources?: string;
    min_deals?: string; max_deals?: string; min_leads?: string; max_leads?: string;
    min_invoiced?: string; max_invoiced?: string; min_paid?: string; max_paid?: string;
    min_balance?: string; max_balance?: string; min_invoices?: string; max_invoices?: string;
    min_estimates?: string; max_estimates?: string; min_docs?: string; max_docs?: string;
    min_jobs?: string; max_jobs?: string;
    date_from?: string; date_to?: string;
    archived?: boolean;
    community?: string;
    subtype?: string | string[]; accounting_role?: string;
  },
): Promise<{ total: number; clients: CrmClient[]; pages: number }> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (opts?.search) params.set('search', opts.search)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.order) params.set('order', opts.order)
  if (opts?.owner) params.set('owner', opts.owner)
  if (opts?.type) params.set('type', opts.type)
  if (opts?.hasLeads) params.set('has_leads', '1')
  if (opts?.hasDeals) params.set('has_deals', '1')
  if (opts?.subtype) {
    // Multi-select OR-filter: backend accepts comma-separated subtype keys.
    const st = Array.isArray(opts.subtype) ? opts.subtype.filter(Boolean).join(',') : opts.subtype
    if (st) params.set('subtype', st)
  }
  if (opts?.accounting_role) params.set('accounting_role', opts.accounting_role)
  // Quick filter params
  if (opts?.email) params.set('email', opts.email)
  if (opts?.state) params.set('state', opts.state)
  if (opts?.zip) params.set('zip', opts.zip)
  if (opts?.city) params.set('city', opts.city)
  if (opts?.legal_name) params.set('legal_name', opts.legal_name)
  if (opts?.phone) params.set('phone', opts.phone)
  if (opts?.match_status) params.set('match_status', opts.match_status)
  if (opts?.client_type_filter) params.set('client_type_filter', opts.client_type_filter)
  if (opts?.data_quality) params.set('data_quality', opts.data_quality)
  if (opts?.lead_status) params.set('lead_status', opts.lead_status)
  if (opts?.deal_stage) params.set('deal_stage', opts.deal_stage)
  if (opts?.pipeline) params.set('pipeline', opts.pipeline)
  if (opts?.data_sources) params.set('data_sources', opts.data_sources)
  if (opts?.min_deals) params.set('min_deals', opts.min_deals)
  if (opts?.max_deals) params.set('max_deals', opts.max_deals)
  if (opts?.min_leads) params.set('min_leads', opts.min_leads)
  if (opts?.max_leads) params.set('max_leads', opts.max_leads)
  if (opts?.min_invoiced) params.set('min_invoiced', opts.min_invoiced)
  if (opts?.max_invoiced) params.set('max_invoiced', opts.max_invoiced)
  if (opts?.min_paid) params.set('min_paid', opts.min_paid)
  if (opts?.max_paid) params.set('max_paid', opts.max_paid)
  if (opts?.min_balance) params.set('min_balance', opts.min_balance)
  if (opts?.max_balance) params.set('max_balance', opts.max_balance)
  if (opts?.min_invoices) params.set('min_invoices', opts.min_invoices)
  if (opts?.max_invoices) params.set('max_invoices', opts.max_invoices)
  if (opts?.min_estimates) params.set('min_estimates', opts.min_estimates)
  if (opts?.max_estimates) params.set('max_estimates', opts.max_estimates)
  if (opts?.min_docs) params.set('min_docs', opts.min_docs)
  if (opts?.max_docs) params.set('max_docs', opts.max_docs)
  if (opts?.min_jobs) params.set('min_jobs', opts.min_jobs)
  if (opts?.max_jobs) params.set('max_jobs', opts.max_jobs)
  if (opts?.date_from) params.set('date_from', opts.date_from)
  if (opts?.date_to) params.set('date_to', opts.date_to)
  if (opts?.archived) params.set('archived', '1')
  if (opts?.community) params.set('community', opts.community)
  const resp = await fetch(`${API_BASE}/crm/clients?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Distinct community names across leads + deals + contacts — powers the
// Community filter typeahead on the Contacts/Leads/Deals lists.
export async function getCommunities(search?: string, limit = 50): Promise<string[]> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (search) params.set('search', search)
  const resp = await fetch(`${API_BASE}/crm/communities?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return Array.isArray(data?.communities) ? data.communities : []
}

export async function archiveClients(ids: string[], archive: boolean): Promise<{ success: boolean; count: number }> {
  const resp = await fetch(`${API_BASE}/crm/clients/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, archive }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// The ONLY correct bare-record endpoint. On success returns the record; when the
// requested id resolves through a merge to a survivor, the server returns that
// survivor with `redirected: true` + `canonical_id` + `requested_id`
// (canonical-client-redirect-v2) so the caller (applyClientOrRedirect) can
// navigate to the canonical record.
export async function fetchClient(id: string): Promise<CrmClient> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(id)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateClient(id: string, fields: Record<string, unknown>): Promise<CrmClient> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ── Organizations: sub-types, role-types, relationships, contact roles, billing ──

export type OrgSubType = {
  key: string; label: string; sort_order: number; is_active: number; created_at: string
}
export type OrgRoleType = {
  id: number; key: string; label: string; applies_to_subtype: string | null
  sort_order: number; is_active: number
}
export type OrgRelationship = {
  id: number; from_org_id: string; to_org_id: string; rel_type: string
  start_date: string | null; end_date: string | null; is_active: number
  is_billing_master: number; note: string | null; created_at: string
  created_by_email: string | null
  to_org_name?: string | null; from_org_name?: string | null
}
export type ContactOrgRole = {
  id: number; contact_id: string; org_id: string; role_key: string | null
  role_label: string; start_date: string | null; end_date: string | null
  is_active: number; is_primary: number; note: string | null; created_at: string
  created_by_email: string | null
  org_name?: string | null; org_primary_sub_type?: string | null
  contact_name?: string | null; preferred_email?: string | null; preferred_phone?: string | null
}
export type JobBillingPeriod = {
  id: number; job_id: number; billing_org_id: string; start_date: string
  end_date: string | null; created_at: string; created_by_email: string | null
  billing_org_name?: string | null
}

// Vocab
export async function listOrgSubTypes(includeInactive = false): Promise<{ data: OrgSubType[] }> {
  const q = includeInactive ? '?includeInactive=1' : ''
  const resp = await fetch(`${API_BASE}/crm/org-sub-types${q}`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json() as Promise<{ data: OrgSubType[] }>
}
export async function createOrgSubType(input: { key: string; label: string; sort_order?: number }): Promise<{ success: boolean; key: string }> {
  const resp = await fetch(`${API_BASE}/crm/org-sub-types`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function updateOrgSubType(key: string, patch: { label?: string; sort_order?: number; is_active?: number }): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/org-sub-types/${encodeURIComponent(key)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function listOrgRoleTypes(subtype?: string, includeInactive = false): Promise<{ data: OrgRoleType[] }> {
  const q = new URLSearchParams()
  if (subtype) q.set('subtype', subtype)
  if (includeInactive) q.set('includeInactive', '1')
  const resp = await fetch(`${API_BASE}/crm/org-role-types${q.toString() ? '?' + q : ''}`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json() as Promise<{ data: OrgRoleType[] }>
}
export async function createOrgRoleType(input: { key: string; label: string; applies_to_subtype?: string | null; sort_order?: number }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/org-role-types`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function updateOrgRoleType(id: number, patch: { label?: string; sort_order?: number; is_active?: number }): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/org-role-types/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}

// Org relationships
export async function getOrgRelationships(orgId: string, includeInactive = false): Promise<{ outbound: OrgRelationship[]; inbound: OrgRelationship[] }> {
  const q = includeInactive ? '?includeInactive=1' : ''
  const resp = await fetch(`${API_BASE}/crm/orgs/${encodeURIComponent(orgId)}/relationships${q}`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function createOrgRelationship(orgId: string, input: { to_org_id: string; rel_type: string; start_date?: string; note?: string; is_billing_master?: boolean }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/orgs/${encodeURIComponent(orgId)}/relationships`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function updateOrgRelationship(relId: number, patch: { end_date?: string | null; is_active?: number; note?: string; is_billing_master?: number }): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/org-relationships/${relId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function endOrgRelationship(relId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/org-relationships/${relId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}

// Contact org roles
export async function getContactOrgRoles(contactId: string, includeInactive = false): Promise<{ data: ContactOrgRole[] }> {
  const q = includeInactive ? '?includeInactive=1' : ''
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/roles${q}`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json() as Promise<{ data: ContactOrgRole[] }>
}
export async function getOrgPeople(orgId: string, includeInactive = false): Promise<{ data: ContactOrgRole[] }> {
  const q = includeInactive ? '?includeInactive=1' : ''
  const resp = await fetch(`${API_BASE}/crm/orgs/${encodeURIComponent(orgId)}/people${q}`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json() as Promise<{ data: ContactOrgRole[] }>
}
export async function createContactOrgRole(input: { contact_id: string; org_id: string; role_label: string; role_key?: string; start_date?: string; is_primary?: boolean; note?: string }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/contact-org-roles`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}
export async function updateContactOrgRole(roleId: number, patch: { start_date?: string | null; end_date?: string | null; is_active?: number; is_primary?: number; role_label?: string; note?: string }): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contact-org-roles/${roleId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}

// Job billing periods
export async function getJobBillingPeriods(jobId: number): Promise<{ data: JobBillingPeriod[]; job: any }> {
  const resp = await fetch(`${API_BASE}/crm/jobs/${jobId}/billing-periods`)
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json() as Promise<{ data: JobBillingPeriod[]; job: any }>
}
export async function changeJobBilling(jobId: number, input: { billing_org_id: string; effective_date: string }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/jobs/${jobId}/billing-change`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) throw new Error(await resp.text())
  return resp.json()
}

export async function fetchClientLeads(clientId: string): Promise<CrmLead[]> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/leads`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return data.map(mapLead)
}

export async function fetchClientDeals(clientId: string): Promise<CrmDeal[]> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/deals`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  return data.map(mapDeal)
}

export async function fetchClientActivities(clientId: string, opts?: { limit?: number; offset?: number }): Promise<CrmActivity[]> {
  const qs = new URLSearchParams()
  if (opts?.limit) qs.set('limit', String(opts.limit))
  if (opts?.offset) qs.set('offset', String(opts.offset))
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/activities${qs.size ? `?${qs}` : ''}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateActivity(
  id: number | string,
  fields: { subject?: string | null; description?: string | null },
): Promise<CrmActivity> {
  const resp = await fetch(`${API_BASE}/crm/activities/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function getClientProjects(clientId: string) {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/projects`)
  if (!resp.ok) return []
  return resp.json()
}

export async function fetchClientTickets(clientId: string): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/tickets`)
  if (!resp.ok) return []
  return resp.json()
}

export type ClientAttachment = {
  r2Key: string
  name: string
  size?: number
  activityId?: number
  activitySubject: string
  source: 'lead' | 'deal' | 'document'
  created: string
  documentId?: number
  documentUrl?: string
  ebridgeSourceId?: string
  docType?: string | null
}

export async function fetchClientAttachments(clientId: string): Promise<ClientAttachment[]> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/attachments`)
  if (!resp.ok) return []
  return resp.json()
}

export async function uploadDealAttachments(dealId: number | string, files: File[], note?: string): Promise<{ success: boolean; activityId: number }> {
  const fd = new FormData()
  if (note) fd.append('note', note)
  files.forEach(f => fd.append('files', f))
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/attachments`, { method: 'POST', body: fd })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function fetchContacts(): Promise<{ total: number; contacts: CrmContact[] }> {
  const resp = await fetch(`${API_BASE}/crm/contacts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchCrmStats(): Promise<CrmStats> {
  const resp = await fetch(`${API_BASE}/crm/stats`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchCrmUsers(): Promise<CrmUser[]> {
  const resp = await fetch(`${API_BASE}/crm/users`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Financial data types & API calls
// ---------------------------------------------------------------------------

export type ClientInvoice = {
  id: number
  unified_client_id: string
  qb_txn_id: string | null
  txn_type: string
  txn_date: string | null
  ref_number: string | null
  customer_name: string | null
  job_name: string | null
  memo: string | null
  amount: number
  balance: number
  bill_address: string | null
  class: string | null
  payment_method: string | null
  document_url: string | null
}

export type ClientDocument = {
  id: number
  unified_client_id: string
  ebridge_source_id: string | null
  doc_type: string | null
  doc_date: string | null
  client_name: string | null
  address: string | null
  division: string | null
  status: string | null
  pages: number
  size_bytes: number
  document_url: string | null
  linked_qb_txn_id: string | null
  linked_qb_ref: string | null
  linked_qb_amount: number | null
}

export type ClientJob = {
  id: number
  unified_client_id: string
  act_contact_id: string | null
  contact_name: string | null
  job_date: string | null
  job_type: string | null
  job_amount: number | null
  raw_entry: string | null
}

export async function fetchClientInvoices(
  clientId: string,
  opts?: { type?: string; page?: number; limit?: number },
): Promise<{ invoices: ClientInvoice[]; total: number }> {
  const params = new URLSearchParams()
  if (opts?.type) params.set('type', opts.type)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/invoices?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchClientDocuments(
  clientId: string,
  opts?: { page?: number; limit?: number },
): Promise<{ documents: ClientDocument[]; total: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/documents?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Document Browser (global search across 108K+ documents)
// ---------------------------------------------------------------------------

export type DocTypeCount = { doc_type: string; cnt: number }

export async function fetchDocumentsBrowse(opts?: {
  page?: number; pageSize?: number; search?: string;
  type?: string; types?: string; address?: string; dateFrom?: string; dateTo?: string;
  clientId?: string; sort?: string; order?: string;
}): Promise<{
  documents: ClientDocument[];
  total: number;
  totalDocs?: number;
  page: number;
  pageSize: number;
  docTypes: DocTypeCount[];
}> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.type) params.set('type', opts.type)
  if (opts?.types) params.set('types', opts.types)
  if (opts?.address) params.set('address', opts.address)
  if (opts?.dateFrom) params.set('dateFrom', opts.dateFrom)
  if (opts?.dateTo) params.set('dateTo', opts.dateTo)
  if (opts?.clientId) params.set('clientId', opts.clientId)
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.order) params.set('order', opts.order)
  const resp = await fetch(`${API_BASE}/crm/documents?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchClientJobs(clientId: string): Promise<{ jobs: ClientJob[]; total: number }> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/jobs`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type RelatedContact = {
  id: string; name: string; phone: string | null; email: string | null
  street: string | null; city: string | null; state: string | null
  status: string; type: string; reasons: string[]
}

export async function fetchRelatedContacts(clientId: string): Promise<RelatedContact[]> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(clientId)}/related`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json() as { related: RelatedContact[] }
  return data.related
}

// ---------------------------------------------------------------------------
// Global search
// ---------------------------------------------------------------------------

export type SearchResultClient = {
  id: string; title: string; subtitle: string; type: string; deals: number; leads: number; invoiced: number
}
export type SearchResultLead = {
  id: number; title: string; subtitle: string; status: string; assignedTo: string
}
export type SearchResultDeal = {
  id: number; title: string; subtitle: string; opportunity: number; assignedTo: string
}
export type SearchResultEmployee = {
  id: number; title: string; subtitle: string; email: string; phone: string | null; status: string; badgeNumber: string | null; profilePhotoKey: string | null
}
export type SearchResults = {
  query: string
  timing_ms: number
  results: {
    clients: SearchResultClient[]
    leads: SearchResultLead[]
    deals: SearchResultDeal[]
    employees: SearchResultEmployee[]
  }
}

export async function searchAll(query: string, limit = 6): Promise<SearchResults> {
  const resp = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}&limit=${limit}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Workgroups (Projects)
// ---------------------------------------------------------------------------

export type Workgroup = {
  id: number
  name: string
  description: string
  dateCreate: string
  dateUpdate: string
  dateActivity: string
  ownerId: number
  ownerName: string
  membersCount: number
  isProject: boolean
  isVisible: boolean
  isOpened: boolean
  imageId: number | null
}

export type WorkgroupDetail = Workgroup & {
  members: { id: number; name: string; role: string }[]
}

export async function fetchWorkgroups(): Promise<{ total: number; workgroups: Workgroup[] }> {
  const resp = await fetch(`${API_BASE}/crm/workgroups`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchWorkgroup(id: number): Promise<WorkgroupDetail> {
  const resp = await fetch(`${API_BASE}/crm/workgroups/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export async function fetchUsers(): Promise<CrmUser[]> {
  const resp = await fetch(`${API_BASE}/crm/users`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Phase A: Create, Delete, Tasks, Notes, Convert, Audit
// ---------------------------------------------------------------------------

export async function createClient(fields: Record<string, unknown>): Promise<CrmClient> {
  const resp = await fetch(`${API_BASE}/crm/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Unified Search — Smart Create panel
// ---------------------------------------------------------------------------

export type UnifiedSearchResult = {
  contacts: Array<{
    unified_client_id: string
    preferred_name: string
    preferred_email: string | null
    preferred_phone: string | null
    preferred_street: string | null
    preferred_city: string | null
    preferred_state: string | null
    client_type: string
    org_primary_sub_type?: string | null
    /** cp-suggest-orgmap-v1: organization rows carry their primary contact
     *  person (name + that person's email/phone); NULL/absent for persons. */
    primary_person_name?: string | null
    primary_person_email?: string | null
    primary_person_phone?: string | null
    contact_status: string | null
    latest_deal_title: string | null
    latest_deal_stage: string | null
    latest_lead_title: string | null
    latest_lead_status: string | null
    deal_count: number
    lead_count: number
    last_activity: string | null
  }>
  leads: Array<{
    id: number
    title: string
    customer_name: string | null
    email: string | null
    phone: string | null
    address: string | null
    status_id: string
    status_label: string | null
    source_label: string | null
    unified_client_id: string | null
    date_create: string | null
  }>
  deals: Array<{
    id: number
    title: string
    stage_id: string
    stage_label: string | null
    opportunity: number
    pipeline_name: string | null
    unified_client_id: string | null
    close_date: string | null
    date_create: string | null
  }>
  total: number
}

export async function searchUnified(q: string): Promise<UnifiedSearchResult> {
  if (!q || q.length < 2) return { contacts: [], leads: [], deals: [], total: 0 }
  const resp = await fetch(`${API_BASE}/crm/search/unified?q=${encodeURIComponent(q)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteClient(id: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(id)}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createLead(fields: Record<string, unknown>): Promise<CrmLead> {
  const resp = await fetch(`${API_BASE}/crm/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return mapLead(await resp.json())
}

export async function createDeal(fields: Record<string, unknown>): Promise<CrmDeal> {
  const resp = await fetch(`${API_BASE}/crm/deals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': getCurrentUserIdFromPrefs() },
    body: JSON.stringify({ ...fields, actorUserId: Number(getCurrentUserIdFromPrefs()) }),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return mapDeal(await resp.json())
}

export async function convertLead(leadId: number | string, fields?: Record<string, unknown>): Promise<{ deal: CrmDeal; leadId: number; converted: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/leads/${leadId}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields ?? {}),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  const data = await resp.json()
  return { deal: mapDeal(data.deal), leadId: data.leadId, converted: data.converted }
}

export async function createNote(fields: { subject?: string; description?: string; ownerTypeId?: number; ownerId?: number | string; unifiedClientId?: string; responsibleId?: number; authorId?: number; providerType?: string; outlookEventId?: string; parentId?: number; callOutcome?: string; callType?: string }): Promise<CrmActivity> {
  const { parentId, ...rest } = fields
  const resp = await fetch(`${API_BASE}/crm/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...rest, ...(parentId != null ? { parent_id: parentId } : {}) }),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- Tasks ---

export type CrmTask = {
  id: number
  title: string
  body: string | null
  status: string
  priority: string
  due_date: string | null
  assigned_to: number | null
  owner_type: string | null
  owner_id: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export async function fetchTasks(
  opts?: { page?: number; limit?: number; status?: string; assignedTo?: number; ownerType?: string; ownerId?: string; priority?: string },
): Promise<{ total: number; tasks: CrmTask[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.status) params.set('status', opts.status)
  if (opts?.assignedTo) params.set('assigned_to', String(opts.assignedTo))
  if (opts?.ownerType) params.set('owner_type', opts.ownerType)
  if (opts?.ownerId) params.set('owner_id', opts.ownerId)
  if (opts?.priority) params.set('priority', opts.priority)
  const resp = await fetch(`${API_BASE}/crm/tasks?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTask(id: number): Promise<CrmTask> {
  const resp = await fetch(`${API_BASE}/crm/tasks/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createTask(fields: Record<string, unknown>): Promise<CrmTask> {
  const resp = await fetch(`${API_BASE}/crm/tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateTask(id: number, fields: Record<string, unknown>): Promise<CrmTask> {
  const resp = await fetch(`${API_BASE}/crm/tasks/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteTask(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/tasks/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Audit Log ---

export type AuditLogRow = {
  id: number
  object_type: string
  object_id: string
  action: string
  user_id: number | null
  changes_json: string | null
  created_at: string
  user_name: string | null
  user_email: string | null
}

export async function fetchAuditLog(
  opts: { page?: number; limit?: number; object_type?: string; action?: string; since?: string; user_id?: number },
): Promise<{ total: number; page: number; limit: number; pages: number; entries: AuditLogRow[]; object_types: string[]; actions: string[] }> {
  const params = new URLSearchParams()
  if (opts.page !== undefined) params.set('page', String(opts.page))
  if (opts.limit !== undefined) params.set('limit', String(opts.limit))
  if (opts.object_type) params.set('object_type', opts.object_type)
  if (opts.action) params.set('action', opts.action)
  if (opts.since) params.set('since', opts.since)
  if (opts.user_id !== undefined) params.set('user_id', String(opts.user_id))
  const resp = await fetch(`${API_BASE}/crm/audit-log?${params}`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Phase B: Engagements + Commerce
// ---------------------------------------------------------------------------

// --- B1: Email logging ---

export async function logEmail(fields: {
  subject: string; body_html?: string; from_email?: string; to_email?: string;
  direction?: number; ownerTypeId: number; ownerId: number; responsibleId?: number; authorId?: number;
}): Promise<CrmActivity> {
  const resp = await fetch(`${API_BASE}/crm/emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- B2: Calls listing ---

export async function fetchCallsPaginated(
  opts?: CallsListQueryOpts,
): Promise<{ total: number; calls: CrmActivity[]; pages: number }> {
  // calls-pg-contract-v1: build the query from the shared param-key contract so
  // the client-sent names can never drift from what GET /api/crm/calls reads.
  const params = buildCallsListQuery(opts)
  const resp = await fetch(`${API_BASE}/crm/calls?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- B2b: Meetings listing (activities.provider_type = MEETING) ---

export type MeetsWhen = 'upcoming' | 'past' | 'all'
export type MeetsListQueryOpts = {
  page?: number
  limit?: number
  search?: string
  when?: MeetsWhen
  sort?: 'start_time' | 'subject' | 'created' | string
  order?: 'ASC' | 'DESC' | string
}

export type CrmMeeting = CrmActivity & {
  provider_type: 'MEETING'
  contact_name?: string | null
  contact_hash?: string | null
  contact_type?: 'person' | 'organization' | null
  linked_id?: number | string | null
  linked_label?: string | null
  linked_hash?: string | null
  linked_type?: 'deal' | 'lead' | 'project' | null
  responsible_name?: string | null
  responsible_initials?: string | null
}

export async function fetchMeetsPaginated(
  opts?: MeetsListQueryOpts,
): Promise<{ total: number; meetings: CrmMeeting[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.when) params.set('when', opts.when)
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.order) params.set('order', opts.order)
  const resp = await fetch(`${API_BASE}/crm/meetings?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- B2: Call logging ---

export async function logCall(fields: {
  subject: string; body?: string; direction?: number; duration_seconds?: number;
  disposition?: string; to_number?: string; from_number?: string;
  ownerTypeId: number; ownerId: number; responsibleId?: number; authorId?: number;
  // calls-assign-v1: explicit ASSIGNED-column stamp for manually-logged calls.
  // The server falls back to the authenticated user when omitted — never a
  // hardcoded default.
  assignedUserId?: number;
}): Promise<CrmActivity> {
  const resp = await fetch(`${API_BASE}/crm/calls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- B3: Meeting logging ---

export async function logMeeting(fields: {
  title: string; body?: string; start_time?: string; end_time?: string;
  location?: string; attendees_json?: string;
  ownerTypeId?: number; ownerId?: number; responsibleId?: number; authorId?: number;
  unified_client_id?: string; contact_id?: string; contactId?: string;
}): Promise<CrmActivity> {
  const resp = await fetch(`${API_BASE}/crm/meetings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- B4: Tickets ---

export type CrmTicket = {
  id: number
  title: string
  description: string | null
  status: string
  priority: string
  category: string | null
  unified_client_id: string | null
  deal_id: number | null
  deal_title: string | null
  project_id: number | null
  project_name: string | null
  assigned_to: number | null
  created_at: string
  updated_at: string
  closed_at: string | null
  resolution: string | null
  resolution_category: string | null
}

export async function fetchTickets(
  opts?: { page?: number; limit?: number; status?: string; priority?: string; assignedTo?: number; clientId?: string; dealId?: number; category?: string; search?: string; sort?: string; order?: string },
): Promise<{ total: number; tickets: CrmTicket[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.status) params.set('status', opts.status)
  if (opts?.priority) params.set('priority', opts.priority)
  if (opts?.assignedTo) params.set('assigned_to', String(opts.assignedTo))
  if (opts?.clientId) params.set('client_id', opts.clientId)
  if (opts?.dealId) params.set('deal_id', String(opts.dealId))
  if (opts?.category) params.set('category', opts.category)
  if (opts?.search) params.set('search', opts.search)
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.order) params.set('order', opts.order)
  const resp = await fetch(`${API_BASE}/crm/tickets?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTicket(id: number): Promise<CrmTicket> {
  const resp = await fetch(`${API_BASE}/crm/tickets/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createTicket(fields: Record<string, unknown>): Promise<CrmTicket> {
  const resp = await fetch(`${API_BASE}/crm/tickets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateTicket(id: number, fields: Record<string, unknown>): Promise<CrmTicket> {
  const resp = await fetch(`${API_BASE}/crm/tickets/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTicketCounts(): Promise<{ all: number; open: number; closed: number }> {
  const resp = await fetch(`${API_BASE}/crm/tickets/counts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteTicket(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/tickets/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- B5: Products ---

export type CrmProduct = {
  id: number
  name: string
  description: string | null
  unit_price: number
  unit: string | null
  category: string | null
  sku: string | null
  is_active: number
  created_at: string
}

export async function fetchProducts(
  opts?: { page?: number; limit?: number; category?: string; active?: string; search?: string },
): Promise<{ total: number; products: CrmProduct[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.category) params.set('category', opts.category)
  if (opts?.active) params.set('active', opts.active)
  if (opts?.search) params.set('search', opts.search)
  const resp = await fetch(`${API_BASE}/crm/products?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchProduct(id: number): Promise<CrmProduct> {
  const resp = await fetch(`${API_BASE}/crm/products/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createProduct(fields: Record<string, unknown>): Promise<CrmProduct> {
  const resp = await fetch(`${API_BASE}/crm/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateProduct(id: number, fields: Record<string, unknown>): Promise<CrmProduct> {
  const resp = await fetch(`${API_BASE}/crm/products/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteProduct(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/products/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- B6: Line Items ---

export type CrmLineItem = {
  id: number
  deal_id: number
  product_id: number | null
  quantity: number
  unit_price: number
  discount_pct: number
  total: number
  description: string | null
  sort_order: number
  product_name?: string
  product_sku?: string
}

export async function fetchLineItems(dealId: number): Promise<{ deal_id: number; line_items: CrmLineItem[] }> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/line-items`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createLineItem(dealId: number, fields: Record<string, unknown>): Promise<CrmLineItem> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/line-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateLineItem(dealId: number, id: number, fields: Record<string, unknown>): Promise<CrmLineItem> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/line-items/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteLineItem(dealId: number, id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/line-items/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- B7: Quotes ---

export type CrmQuote = {
  id: number
  title: string
  deal_id: number | null
  unified_client_id: string | null
  status: string
  expiration_date: string | null
  subtotal: number
  discount: number
  tax: number
  total: number
  terms_text: string | null
  notes: string | null
  template_id: number | null
  created_by: number | null
  created_at: string
  approved_at: string | null
}

export async function fetchQuotes(
  opts?: { page?: number; limit?: number; status?: string; dealId?: number; clientId?: string },
): Promise<{ total: number; quotes: CrmQuote[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.status) params.set('status', opts.status)
  if (opts?.dealId) params.set('deal_id', String(opts.dealId))
  if (opts?.clientId) params.set('client_id', opts.clientId)
  const resp = await fetch(`${API_BASE}/crm/quotes?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchQuote(id: number): Promise<CrmQuote> {
  const resp = await fetch(`${API_BASE}/crm/quotes/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createQuote(fields: Record<string, unknown>): Promise<CrmQuote> {
  const resp = await fetch(`${API_BASE}/crm/quotes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateQuote(id: number, fields: Record<string, unknown>): Promise<CrmQuote> {
  const resp = await fetch(`${API_BASE}/crm/quotes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteQuote(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/quotes/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function approveQuote(id: number): Promise<CrmQuote> {
  const resp = await fetch(`${API_BASE}/crm/quotes/${id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- B8: Contact Merge ---

export async function mergeClients(primaryId: string, secondaryId: string): Promise<{ success: boolean; primary: CrmClient; secondaryId: string }> {
  const resp = await fetch(`${API_BASE}/crm/clients/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ primaryId, secondaryId }),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- Dedup review (admin "Possible Duplicates" view) ---
// The FE-facing shape (DedupTier/DedupMember/DedupCluster/DedupReviewResponse)
// and the worker→FE translation live in the pure, tested adapter
// (src/lib/contacts/dedupClusterAdapter.ts). Re-exported here so the
// Duplicates view keeps importing these types from '../../../api'.
export type { DedupTier, DedupMember, DedupCluster, DedupReviewResponse }

export async function fetchDedupReviewClusters(
  params: { page?: number; pageSize?: number; tier?: DedupTier | 'all'; entity?: 'org' | 'person' | 'all' } = {}
): Promise<DedupReviewResponse> {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.pageSize) qs.set('pageSize', String(params.pageSize))
  if (params.tier && params.tier !== 'all') qs.set('tier', tierToWorker(params.tier))
  // Backend defaults to 'org' when `entity` is omitted, preserving the prior
  // org-only behaviour. Pass it through to review person/all clusters too.
  if (params.entity) qs.set('entity', params.entity)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const resp = await fetch(`${API_BASE}/crm/dedup/review-clusters${suffix}`)
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  // The worker emits a mismatched field/enum shape (multi_billed,
  // unified_client_id, inv_count, legacy_total_invoiced, proposed_survivor_id,
  // string data_quality, no cluster_id/is_survivor). Translate to the FE shape
  // via the pure adapter — WITHOUT this the cluster merge UI crashes on
  // multi-billed clusters and the Merge button stays disabled.
  const raw = (await resp.json()) as RawDedupReviewResponse
  return mapWorkerDedupResponse(raw)
}

export async function dismissDedupCluster(
  payload: { cluster_id: string; member_ids: string[]; reason?: string }
): Promise<{ success: boolean }> {
  // The worker keys dismissals by `normalized_name` (400s without it). The FE
  // cluster_id IS the normalized_name (see the adapter), so map it across.
  const resp = await fetch(`${API_BASE}/crm/dedup/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      normalized_name: payload.cluster_id,
      member_ids: payload.member_ids,
      ...(payload.reason ? { reason: payload.reason } : {}),
    }),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Entity 360 — relationship graph + rollups resolver (Track A backend)
// ---------------------------------------------------------------------------
// GET /api/crm/360/:entityType/:id?depth=1 returns a sectioned graph of related
// records (people, org-rels, deals, projects, invoices, …) plus pre-computed
// rollups. The <Entity360> renderer (src/views/entity360) walks this against
// ENTITY360_REGISTRY. Shape mirrors src/lib/entity360/registry.ts:Entity360Payload.
// This is ADDITIVE: the caller renders nothing on error/empty, so it never
// blocks the underlying record.
export type {
  Entity360Type,
  Entity360Payload,
  Entity360ResolverSection,
  Entity360Item,
  Entity360Rollup,
} from './lib/entity360/registry'

export async function getEntity360(
  entityType: import('./lib/entity360/registry').Entity360Type,
  id: string,
  opts: { depth?: number; signal?: AbortSignal } = {}
): Promise<import('./lib/entity360/registry').Entity360Payload> {
  const qs = new URLSearchParams()
  if (opts.depth != null) qs.set('depth', String(opts.depth))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const resp = await fetch(
    `${API_BASE}/crm/360/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}${suffix}`,
    { signal: opts.signal }
  )
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// GET /api/crm/360/:entityType/:id/insight returns a REAL (LLM-generated) record
// summary + next-best-actions for the entity. Distinct from getEntity360 (which
// returns the deterministic relationship graph). The endpoint may legitimately
// return { unavailable: true } (e.g. feature off, model offline, nothing to
// summarize) — callers MUST treat that (and any thrown error) as "fall back to
// the deterministic templated summary", never as a hard failure. Pass
// { refresh: true } to force regeneration (?refresh=1).
export type EntityInsight = {
  unavailable?: boolean
  summary?: string
  nextBestActions?: string[]
  generatedAt?: string
  model?: string
}

export async function fetchEntityInsight(
  entityType: 'org' | 'person',
  id: string,
  opts: { refresh?: boolean; signal?: AbortSignal } = {}
): Promise<EntityInsight> {
  const qs = new URLSearchParams()
  if (opts.refresh) qs.set('refresh', '1')
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const resp = await fetch(
    `${API_BASE}/crm/360/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}/insight${suffix}`,
    { signal: opts.signal }
  )
  if (!resp.ok) {
    // A 404/501 means the endpoint isn't live yet (Track A not deployed) — the
    // caller should fall back. Surface a normalized unavailable marker rather
    // than throwing for those, but throw on genuine server errors so the caller
    // can distinguish (it falls back either way).
    if (resp.status === 404 || resp.status === 501) return { unavailable: true }
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Phase C: Search, Reports, Lists, Tags
// ---------------------------------------------------------------------------

// --- C2: Saved Views ---

export type SavedView = {
  id: number
  name: string
  object_type: string
  filters_json: string | null
  sorts_json: string | null
  columns_json: string | null
  owner_id: number | null
  is_shared: number
  created_at: string | null
  // Added by migration 093_saved_views.sql.
  is_default?: number
  density?: string | null
  updated_at?: string | null
}

// --- C3-C7: Reports ---

export type PipelineReportRow = {
  stage_name: string
  status_id: string
  deals_entered: number
  avg_days: number
  total_value: number
  conversion_rate: number
}

export async function fetchPipelineReport(opts?: { category?: number; from?: string; to?: string }): Promise<PipelineReportRow[]> {
  const params = new URLSearchParams()
  if (opts?.category !== undefined) params.set('category', String(opts.category))
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/pipeline?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type RevenueReportRow = {
  period: string
  deals_closed: number
  total_revenue: number
  avg_deal_size: number
}

export async function fetchRevenueReport(opts?: { period?: string; from?: string; to?: string }): Promise<RevenueReportRow[]> {
  const params = new URLSearchParams()
  if (opts?.period) params.set('period', opts.period)
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/revenue?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type ActivitiesReportRow = {
  period: string
  notes: number
  emails: number
  calls: number
  meetings: number
  tasks_completed: number
}

export async function fetchActivitiesReport(opts?: { period?: string; user?: number; from?: string; to?: string }): Promise<ActivitiesReportRow[]> {
  const params = new URLSearchParams()
  if (opts?.period) params.set('period', opts.period)
  if (opts?.user) params.set('user', String(opts.user))
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/activities?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type LeadSourceReportRow = {
  source: string
  lead_count: number
  conversion_rate: number
  avg_value: number
}

export async function fetchLeadSourcesReport(opts?: { from?: string; to?: string }): Promise<LeadSourceReportRow[]> {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/lead-sources?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type LeaderboardRow = {
  user_id: number
  user_name: string
  deals_won: number
  revenue: number
  activities_count: number
}

export async function fetchLeaderboard(opts?: { period?: string; from?: string; to?: string }): Promise<LeaderboardRow[]> {
  const params = new URLSearchParams()
  if (opts?.period) params.set('period', opts.period)
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/leaderboard?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- C7b-k: Enhanced Report Endpoints ---

export type SummaryKPIs = {
  total_deals: number
  deals_won: number
  deals_lost: number
  revenue: number
  total_revenue: number
  win_rate: number
  open_leads: number
  total_clients: number
  total_activities: number
  avg_deal_size: number
  period: { from: string; to: string; range: string }
}

export async function fetchSummaryKPIs(range?: string): Promise<SummaryKPIs> {
  const params = new URLSearchParams()
  if (range) params.set('range', range)
  const resp = await fetch(`${API_BASE}/crm/reports/summary-kpis?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchDealsCreated(opts?: { period?: string; from?: string; to?: string; group_by?: string }): Promise<any[]> {
  const params = new URLSearchParams()
  if (opts?.period) params.set('period', opts.period)
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  if (opts?.group_by) params.set('group_by', opts.group_by)
  const resp = await fetch(`${API_BASE}/crm/reports/deals-created?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAvgDealSize(opts?: { from?: string; to?: string }): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/avg-deal-size?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchDealVelocity(opts?: { from?: string }): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  const resp = await fetch(`${API_BASE}/crm/reports/deal-velocity?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchActivityByType(opts?: { from?: string; to?: string }): Promise<any[]> {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/activity-by-type?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTeamActivity(opts?: { from?: string; to?: string }): Promise<any[]> {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  const resp = await fetch(`${API_BASE}/crm/reports/team-activity?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Leads by Estimator report ---
// Per-estimator lead volume: total leads, converted count, and a by-source
// breakdown. The estimator is derived from the lead's assigned owner / the
// estimator initials in the title (backend GET /api/crm/reports/leads-by-estimator).

export type LeadsByEstimatorSource = {
  source: string
  count: number
}

// Who assigned the leads to this estimator. Backend may not yet populate this
// (the acting user is not stored on the lead — see endpoint `note`). When it
// does, each entry is a { assigner_name, count } pair, mirroring by_source.
export type LeadsByEstimatorAssigner = {
  assigner_name: string
  count: number
}

export type LeadsByEstimatorRow = {
  estimator: string
  estimator_id?: number | null
  total_leads: number
  converted: number
  conversion_rate?: number
  by_source: LeadsByEstimatorSource[]
  // "Assigned by" breakdown — empty until the backend captures the acting user.
  by_assigner?: LeadsByEstimatorAssigner[]
  // How many of this estimator's leads have a known assigner (attributed/total).
  assigner_coverage?: { attributed: number; total: number }
}

// Normalize a {key: count} record OR an array of {key, count}-ish entries into
// a sorted [{ <labelKey>, count }] array (desc by count). The backend returns
// by_source/by_assigner as Record<label, count> objects; the card renders chips
// from an array, so we adapt here.
function normalizeCountBreakdown<K extends string>(
  raw: unknown,
  labelKey: K,
): Array<{ count: number } & Record<K, string>> {
  let pairs: Array<[string, number]> = []
  if (Array.isArray(raw)) {
    pairs = raw
      .map((e: any) => [String(e?.[labelKey] ?? e?.source ?? e?.label ?? 'Unknown'), Number(e?.count ?? 0)] as [string, number])
  } else if (raw && typeof raw === 'object') {
    pairs = Object.entries(raw as Record<string, number>).map(([k, v]) => [k, Number(v) || 0])
  }
  return pairs
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ [labelKey]: label, count } as { count: number } & Record<K, string>))
}

export async function fetchLeadsByEstimator(
  range?: string | { range?: string; from?: string; to?: string },
): Promise<LeadsByEstimatorRow[]> {
  const params = new URLSearchParams()
  if (typeof range === 'string') {
    if (range) params.set('range', range)
  } else if (range) {
    if (range.range) params.set('range', range.range)
    if (range.from) params.set('from', range.from)
    if (range.to) params.set('to', range.to)
  }
  const resp = await fetch(`${API_BASE}/crm/reports/leads-by-estimator?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json()
  // Endpoint may return either a bare array or { rows: [...] }.
  const rawRows: any[] = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : [])
  return rawRows.map((r): LeadsByEstimatorRow => {
    const byAssigner = normalizeCountBreakdown(r?.by_assigner, 'assigner_name')
    const cov = r?.assigner_coverage
    return {
      estimator: r?.estimator ?? r?.estimator_name ?? 'Unassigned',
      estimator_id: r?.estimator_id ?? null,
      total_leads: Number(r?.total_leads ?? 0),
      converted: Number(r?.converted ?? 0),
      conversion_rate: r?.conversion_rate != null ? Number(r.conversion_rate) : undefined,
      by_source: normalizeCountBreakdown(r?.by_source, 'source'),
      by_assigner: byAssigner,
      assigner_coverage: cov && typeof cov === 'object'
        ? { attributed: Number(cov.attributed ?? 0), total: Number(cov.total ?? r?.total_leads ?? 0) }
        : undefined,
    }
  })
}

export async function fetchDashboardAggregate(opts?: {
  range?: string
  from?: string
  to?: string
  ownerId?: number
  revenuePeriod?: 'weekly' | 'monthly'
  refresh?: boolean
}): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.range) params.set('range', opts.range)
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  if (opts?.ownerId) params.set('owner_id', String(opts.ownerId))
  if (opts?.revenuePeriod) params.set('revenue_period', opts.revenuePeriod)
  if (opts?.refresh) params.set('refresh', '1')
  const resp = await fetch(`${API_BASE}/crm/dashboard/aggregate?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}


// --- C9: Tags ---

export type Tag = {
  id: number
  name: string
  color: string | null
  object_type: string | null
  created_at: string
}

export async function fetchRecordTags(objectType: string, recordId: string): Promise<Tag[]> {
  const resp = await fetch(`${API_BASE}/crm/${objectType}/${encodeURIComponent(recordId)}/tags`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}


// ---------------------------------------------------------------------------
// Phase D: Automation + Integration
// ---------------------------------------------------------------------------

// --- D1-D2: Workflows ---

export type Workflow = {
  id: number
  name: string
  description: string | null
  object_type: string
  is_active: number
  trigger_type: string
  trigger_config_json: string | null
  created_by: number | null
  created_at: string
  updated_at: string
}

export type WorkflowAction = {
  id: number
  workflow_id: number
  action_order: number
  action_type: string
  action_config_json: string | null
}

export type WorkflowRun = {
  id: number
  workflow_id: number
  trigger_record_type: string | null
  trigger_record_id: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

export async function fetchWorkflows(
  opts?: { page?: number; limit?: number; objectType?: string; isActive?: number; triggerType?: string },
): Promise<{ total: number; workflows: Workflow[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.objectType) params.set('object_type', opts.objectType)
  if (opts?.isActive !== undefined) params.set('is_active', String(opts.isActive))
  if (opts?.triggerType) params.set('trigger_type', opts.triggerType)
  const resp = await fetch(`${API_BASE}/crm/workflows?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchWorkflow(id: number): Promise<Workflow & { actions: WorkflowAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createWorkflow(fields: Record<string, unknown>): Promise<Workflow> {
  const resp = await fetch(`${API_BASE}/crm/workflows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateWorkflow(id: number, fields: Record<string, unknown>): Promise<Workflow> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteWorkflow(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Workflow Actions ---

export async function fetchWorkflowActions(workflowId: number): Promise<{ workflowId: number; actions: WorkflowAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/actions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addWorkflowAction(workflowId: number, fields: Record<string, unknown>): Promise<WorkflowAction> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function replaceWorkflowActions(
  workflowId: number,
  actions: { actionType: string; actionOrder: number; actionConfigJson?: unknown }[],
): Promise<{ workflowId: number; actions: WorkflowAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/actions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ actions }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteWorkflowAction(workflowId: number, actionId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/actions/${actionId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Workflow Runs ---

export async function fetchWorkflowRuns(
  workflowId: number,
  opts?: { page?: number; limit?: number },
): Promise<{ total: number; runs: WorkflowRun[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/runs?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function executeWorkflow(
  workflowId: number,
  fields?: { recordType?: string; recordId?: string },
): Promise<WorkflowRun> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${workflowId}/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields ?? {}),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}


// --- D5: Notifications ---

export type CrmNotification = {
  id: number
  user_id: number
  title: string
  body: string | null
  link_url: string | null
  is_read: number
  created_at: string
}

export async function fetchNotifications(
  opts?: { page?: number; limit?: number; userId?: number },
): Promise<{ total: number; notifications: CrmNotification[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.userId) params.set('user_id', String(opts.userId))
  const resp = await fetch(`${API_BASE}/crm/notifications?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchUnreadCount(userId: number): Promise<{ userId: number; unreadCount: number }> {
  const resp = await fetch(`${API_BASE}/crm/notifications/unread-count?user_id=${userId}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createNotification(fields: { userId: number; title: string; body?: string; linkUrl?: string }): Promise<CrmNotification> {
  const resp = await fetch(`${API_BASE}/crm/notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function markNotificationRead(id: number, isRead = true): Promise<CrmNotification> {
  const resp = await fetch(`${API_BASE}/crm/notifications/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isRead }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function markAllNotificationsRead(userId: number): Promise<{ success: boolean; updated: number }> {
  const resp = await fetch(`${API_BASE}/crm/notifications/mark-all-read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- D6: Batch Operations ---

export type BatchResult<T = unknown> = {
  total: number
  created?: number
  updated?: number
  found?: number
  archived?: number
  failed: number
  results: T[]
  errors: { index: number; error: string }[]
  status?: string
}

// Leads batch
export async function batchArchiveLeads(ids: (number | string)[]): Promise<BatchResult> {
  const resp = await fetch(`${API_BASE}/crm/leads/batch/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inputs: ids.map(id => ({ id })) }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- D7: Import CSV ---
export type CrmImport = {
  id: number
  object_type: string
  file_name: string | null
  total_rows: number
  processed_rows: number
  created_rows: number
  updated_rows: number
  failed_rows: number
  status: string
  errors_json: string | null
  column_mapping_json: string | null
  created_by: number | null
  created_at: string
  completed_at: string | null
}

export async function fetchImports(
  opts?: { page?: number; limit?: number },
): Promise<{ total: number; imports: CrmImport[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/imports?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchImport(id: number): Promise<CrmImport> {
  const resp = await fetch(`${API_BASE}/crm/imports/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createImport(fields: {
  objectType: string
  data: Record<string, unknown>[]
  columnMapping?: Record<string, string>
  fileName?: string
  createdBy?: number
}): Promise<CrmImport> {
  const resp = await fetch(`${API_BASE}/crm/imports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// --- D8: Export Data ---

export type CrmExport = {
  id: number
  object_type: string
  filters_json: string | null
  columns_json: string | null
  format: string
  record_count: number
  status: string
  result_json: string | null
  created_by: number | null
  created_at: string
  completed_at: string | null
}

export async function fetchExports(
  opts?: { page?: number; limit?: number },
): Promise<{ total: number; exports: CrmExport[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/exports?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchExport(id: number): Promise<CrmExport> {
  const resp = await fetch(`${API_BASE}/crm/exports/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createExport(fields: {
  objectType: string
  filters?: Record<string, unknown>
  columns?: string[]
  format?: string
  createdBy?: number
}): Promise<CrmExport> {
  const resp = await fetch(`${API_BASE}/crm/exports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Phase E: Advanced Features
// ---------------------------------------------------------------------------

// --- E1: Custom Properties ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchProperties(objectType: string, active = true): Promise<{ objectType: string; properties: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/properties/${objectType}?active=${active ? '1' : '0'}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createProperty(objectType: string, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/properties/${objectType}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateProperty(objectType: string, id: number, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/properties/${objectType}/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteProperty(objectType: string, id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/properties/${objectType}/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- E2: Roles ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchRoles(): Promise<{ roles: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/roles`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- E3: Teams ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchTeams(): Promise<{ teams: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/teams`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- E6: Forms ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchForms(opts?: { active?: boolean; page?: number; limit?: number; status?: string; search?: string; pageSize?: number }): Promise<{ total: number; forms: any[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.active === false) params.set('active', '0')
  if (opts?.status) params.set('status', opts.status)
  if (opts?.search) params.set('search', opts.search)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  const resp = await fetch(`${API_BASE}/crm/forms?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchForm(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/forms/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createForm(fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/forms`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateForm(id: number, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/forms/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteForm(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/forms/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function submitForm(formId: number, data: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/forms/${formId}/submit`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchFormSubmissions(formId: number, opts?: { page?: number; limit?: number }): Promise<{ total: number; submissions: any[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/forms/${formId}/submissions?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- E8: Conversations ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchInboxes(): Promise<{ inboxes: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/inboxes`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createInbox(fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/inboxes`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchConversations(opts?: { status?: string; inboxId?: number; assignedTo?: number; clientId?: string; page?: number; limit?: number }): Promise<{ total: number; threads: any[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.inboxId) params.set('inbox_id', String(opts.inboxId))
  if (opts?.assignedTo) params.set('assigned_to', String(opts.assignedTo))
  if (opts?.clientId) params.set('client_id', opts.clientId)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/conversations?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchConversation(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/conversations/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createConversation(fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/conversations`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateConversation(id: number, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/conversations/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteConversation(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/conversations/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchMessages(threadId: number, opts?: { page?: number; limit?: number }): Promise<{ total: number; messages: any[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/crm/conversations/${threadId}/messages?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function sendMessage(threadId: number, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/conversations/${threadId}/messages`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as { error?: string }).error || `API error: ${resp.status}`) }
  return resp.json()
}

// --- E10: Dashboards, Reports, Preferences, Settings ---

export async function fetchMyPreferences(): Promise<{ userId: number | null; preferences: Record<string, string> }> {
  const resp = await fetch(`${API_BASE}/crm/me/preferences`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function setMyPreference(key: string, value: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/me/preferences`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preferenceKey: key, preferenceValue: value }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAccountSettings(): Promise<{ settings: Record<string, string> }> {
  const resp = await fetch(`${API_BASE}/crm/settings`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function setAccountSetting(key: string, value: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/settings`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settingKey: key, settingValue: value }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteAccountSetting(key: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/settings/${key}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// RingCentral Settings
// =========================================================================

// --- Types ---

export type RcUserMapping = {
  id: number
  user_id: number
  rc_extension_id: string
  rc_extension_number: string | null
  rc_phone_number: string | null
  rc_name: string | null
  is_active: number
  created_at: string
  updated_at: string
  user_name?: string
  user_email?: string
  user_initials?: string
}

export type RcConfig = Record<string, string>

export type RcExtension = {
  id: string
  extensionNumber: string
  name: string
  phoneNumber: string
}

export type PendingCallAssignment = {
  id: number
  activity_id: number
  contact_id: string | null
  deal_ids: string | null
  assigned_by: number | null
  assigned_deal_id: number | null
  status: 'pending' | 'resolved' | 'dismissed'
  created_at: string
  resolved_at: string | null
  // Fields surfaced by the GET /api/crm/rc/pending JOIN onto activities
  call_subject?: string | null
  phone_from?: string | null
  phone_to?: string | null
  duration_seconds?: number | null
  call_time?: string | null
}

export type CallOutcome = {
  id: number
  label: string
  color: string
  sort_order: number
  is_active: number
  is_system: number
  created_at: string
}

export type BlockedNumber = {
  id: number
  phone_number: string
  reason: string | null
  blocked_by: number | null
  blocked_by_name?: string
  created_at: string
}

export type PhoneMatch = {
  client: {
    unified_client_id: string
    preferred_name: string
    preferred_phone: string
    preferred_email: string
    client_type: string
  }
  deals: Array<{
    id: number
    title: string
    stage_label: string
    pipeline_name: string
    opportunity: number
    date_modify: string
  }>
}

// --- RC User Mappings ---

export async function fetchRcUserMappings(): Promise<{ mappings: RcUserMapping[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/user-mappings`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createRcUserMapping(fields: { userId: number; rcExtensionId: string; rcExtensionNumber?: string; rcPhoneNumber?: string; rcName?: string }): Promise<RcUserMapping> {
  const resp = await fetch(`${API_BASE}/crm/rc/user-mappings`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateRcUserMapping(id: number, fields: Record<string, unknown>): Promise<RcUserMapping> {
  const resp = await fetch(`${API_BASE}/crm/rc/user-mappings/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteRcUserMapping(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/user-mappings/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Call Outcomes ---

export async function fetchCallOutcomes(activeOnly = false): Promise<{ outcomes: CallOutcome[] }> {
  const url = activeOnly ? `${API_BASE}/crm/rc/call-outcomes?active=1` : `${API_BASE}/crm/rc/call-outcomes`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createCallOutcome(fields: { label: string; color?: string; sortOrder?: number }): Promise<CallOutcome> {
  const resp = await fetch(`${API_BASE}/crm/rc/call-outcomes`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateCallOutcome(id: number, fields: Record<string, unknown>): Promise<CallOutcome> {
  const resp = await fetch(`${API_BASE}/crm/rc/call-outcomes/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteCallOutcome(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/call-outcomes/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Blocked Numbers ---

export async function fetchBlockedNumbers(): Promise<{ blockedNumbers: BlockedNumber[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/blocked-numbers`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function blockNumber(fields: { phoneNumber: string; reason?: string; blockedBy?: number }): Promise<BlockedNumber> {
  const resp = await fetch(`${API_BASE}/crm/rc/blocked-numbers`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unblockNumber(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/blocked-numbers/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- RC Config ---

export async function fetchRcConfig(): Promise<{ config: Record<string, string> }> {
  const resp = await fetch(`${API_BASE}/crm/rc/config`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateRcConfig(config: Record<string, string>): Promise<{ config: Record<string, string> }> {
  const resp = await fetch(`${API_BASE}/crm/rc/config`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Phone Matching ---

export async function matchPhoneNumber(phone: string): Promise<{ phone: string; normalized: string; matches: PhoneMatch[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/match-phone?phone=${encodeURIComponent(phone)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export type ContactLookupMatch = { id: string; name: string; email: string | null; phone: string | null }

// Look up CRM contacts (clients + leads, via the shared lookup endpoint) by
// phone number, returning the FULL matches array — not just the first — so
// callers can apply the iPhone-style ambiguous-match rule (ambiguousContactLabel
// in src/lib/phone/format.ts). The endpoint normalizes a leading 1 and caps the
// array, so the "N others" ceiling is the server cap minus one. Pass an
// AbortSignal to cancel a stale debounced lookup as the user keeps typing.
export async function lookupContactsByPhone(phone: string, signal?: AbortSignal): Promise<{ matches: ContactLookupMatch[] }> {
  const digits = (phone || '').replace(/\D/g, '')
  if (digits.length < 4) return { matches: [] }
  const resp = await fetch(`${API_BASE}/crm/contacts/lookup?phone=${encodeURIComponent(digits)}`, { signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Phone favorites ---

export type PhoneFavorite = {
  id: number
  contact_identifier: string
  display_name: string | null
  phone_number: string | null
  contact_type: string | null
  linked_contact_id: string | null
  linked_user_email: string | null
  added_at: string
  sort_order: number
}

export async function fetchPhoneFavorites(): Promise<PhoneFavorite[]> {
  const resp = await fetch(`${API_BASE}/crm/phone/favorites`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json() as { favorites?: PhoneFavorite[] }
  return data.favorites ?? []
}

export async function addPhoneFavorite(body: {
  contactIdentifier: string
  displayName?: string
  phoneNumber?: string
  contactType?: 'crm_contact' | 'team_member' | 'external'
  linkedContactId?: string | number
  linkedUserEmail?: string
}): Promise<{ success: boolean; id: number | null }> {
  const resp = await fetch(`${API_BASE}/crm/phone/favorites`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deletePhoneFavorite(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/phone/favorites/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function reorderPhoneFavorites(ids: number[]): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/phone/favorites/reorder`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchLiveTranscriptionCapability(): Promise<{ enabled: boolean; source: string; note: string }> {
  const resp = await fetch(`${API_BASE}/crm/phone/capabilities/live-transcription`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- RC Extensions ---

export async function fetchRcExtensions(): Promise<{ extensions: RcExtension[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/extensions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- RC Sync ---

export async function triggerRcSync(): Promise<{ status: string; synced: number }> {
  const resp = await fetch(`${API_BASE}/crm/rc/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Pending Call Assignments ---

export async function fetchPendingAssignments(): Promise<{ pending: PendingCallAssignment[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/pending`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function resolvePendingAssignment(id: number, dealId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/pending/${id}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dealId }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function dismissPendingAssignment(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/pending/${id}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// User Management
// =========================================================================

// fetchUsers() already exists at line ~779 (GET /api/crm/users)

export async function createUser(data: Partial<CrmUser>): Promise<CrmUser> {
  const resp = await fetch(`${API_BASE}/crm/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateUser(id: number, data: Partial<CrmUser>): Promise<CrmUser> {
  const resp = await fetch(`${API_BASE}/crm/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteUser(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/users/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// RingCentral — Tier 3: Recording URL
// =========================================================================

export function getRecordingUrl(recordingId: string): string {
  return `${API_BASE}/crm/rc/recording/${recordingId}`
}

// =========================================================================
// RingCentral — WebRTC SIP Provisioning
// =========================================================================

export interface SipProvisionResponse {
  sipInfo: {
    transport: string
    username: string
    password: string
    authorizationId: string
    domain: string
    outboundProxy: string
    outboundProxyBackup: string
    stunServers?: string[]
  } | null
  device: { id: string; uri: string } | null
  extensionId: string
  extensionNumber: string | null
  phoneNumber: string | null
}

export async function fetchSipProvision(userId: number): Promise<SipProvisionResponse> {
  const resp = await fetch(`${API_BASE}/crm/rc/sip-provision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error((err as { error?: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// =========================================================================
// RingCentral — Voicemail
// =========================================================================

export type Voicemail = {
  id: string
  creationTime: string
  readStatus: 'Read' | 'Unread'
  priority: 'Normal' | 'High'
  from: { phoneNumber: string | null; name: string | null; extensionNumber: string | null; location: string | null }
  to: { phoneNumber: string | null; name: string | null; extensionNumber: string | null } | null
  durationSeconds: number
  transcriptionStatus: 'NotAvailable' | 'InProgress' | 'TimedOut' | 'Completed' | 'CompletedPartially' | 'Failed'
  transcription: string | null
  audioAttachmentId: string | null
}

export async function fetchVoicemails(userId: number, opts?: { perPage?: number; readStatus?: 'Read' | 'Unread' }): Promise<{ voicemails: Voicemail[]; unprovisioned?: boolean }> {
  const qs = new URLSearchParams()
  if (opts?.perPage) qs.set('perPage', String(opts.perPage))
  if (opts?.readStatus) qs.set('readStatus', opts.readStatus)
  const resp = await fetch(`${API_BASE}/crm/rc/voicemails/${userId}${qs.toString() ? `?${qs}` : ''}`)
  // A 404 (or any non-ok) means "no voicemail box for this user" — e.g. the
  // softphone isn't provisioned. Treat it as an empty result instead of
  // throwing, so the 60s poller doesn't console.error → D1 spam every tick.
  if (!resp.ok) return { voicemails: [], unprovisioned: true }
  return resp.json()
}

export function voicemailAudioUrl(userId: number, messageId: string, attachmentId: string): string {
  return `${API_BASE}/crm/rc/voicemails/${userId}/${messageId}/audio?attachmentId=${encodeURIComponent(attachmentId)}`
}

export async function updateVoicemailReadStatus(userId: number, messageId: string, readStatus: 'Read' | 'Unread'): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/rc/voicemails/${userId}/${messageId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ readStatus }),
  })
  if (!resp.ok) throw new Error(`Voicemail update failed: ${resp.status}`)
}

export async function deleteVoicemail(userId: number, messageId: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/rc/voicemails/${userId}/${messageId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`Voicemail delete failed: ${resp.status}`)
}

// =========================================================================
// RingCentral — per-extension call log
// Powers the emulated iPhone "Recents" tab. Server resolves the extension from
// the authed session, so this mirrors the user's own deskphone / RC app call
// history (including internal extension-to-extension calls) with no sync lag.
// =========================================================================

export type DeviceCall = {
  id: string
  direction: 'Inbound' | 'Outbound'
  phoneNumber: string | null
  name: string | null
  extensionNumber: string | null
  durationSeconds: number
  result: string
  missed: boolean
  startTime: string
}

export async function fetchRcExtensionCallLog(opts?: { perPage?: number }): Promise<{ calls: DeviceCall[] }> {
  const qs = new URLSearchParams()
  if (opts?.perPage) qs.set('perPage', String(opts.perPage))
  const resp = await fetch(`${API_BASE}/crm/rc/call-log${qs.toString() ? `?${qs}` : ''}`)
  if (!resp.ok) throw new Error(`Call-log fetch failed: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// RingCentral — Tier 4: Click-to-Call (RingOut)
// =========================================================================

export async function startRingOut(data: {
  userId: number
  toNumber: string
  dealId?: number
  contactId?: string
}): Promise<{ ringoutId: string; status: string }> {
  const resp = await fetch(`${API_BASE}/crm/rc/ringout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* no body */ }
    const err: any = new Error(detail?.error || `API error: ${resp.status}`)
    err.status = resp.status
    err.code = detail?.code
    throw err
  }
  return resp.json()
}

export async function getRingOutStatus(ringoutId: string): Promise<{
  status: string
  callerStatus: string
  calleeStatus: string
}> {
  const resp = await fetch(`${API_BASE}/crm/rc/ringout/${ringoutId}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Cancel an in-progress RingOut (desk-mirror "End call"). Best-effort — the
// caller tears down its local mirror regardless of the result.
export async function cancelRingOut(ringoutId: string): Promise<{ cancelled: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/ringout/${ringoutId}`, { method: 'DELETE' })
  if (!resp.ok) return { cancelled: false }
  return resp.json()
}

// Live desk-phone mirror: the user's currently-active call on ANY device on
// their RC extension (the physical desk phone included), or null. Best-effort —
// returns null on any error so the phone-panel HUD just stays hidden.
export type RcActiveCall = {
  direction: 'Inbound' | 'Outbound'
  phoneNumber: string | null
  name: string | null
  startTime: string | null
  sessionId: string
  result: string
}

export async function fetchRcActiveCall(): Promise<RcActiveCall | null> {
  try {
    const resp = await fetch(`${API_BASE}/crm/rc/active-call`, { credentials: 'include' })
    if (!resp.ok) return null
    const data = await resp.json() as { activeCall?: RcActiveCall | null }
    return data?.activeCall ?? null
  } catch {
    return null
  }
}

// =========================================================================
// RingCentral — Tier 5: Transcription
// =========================================================================

export async function submitTranscription(activityId: number): Promise<{
  status: string
  jobId?: string
  transcript?: string
  summary?: string
  sentiment?: string
}> {
  const resp = await fetch(`${API_BASE}/crm/rc/transcribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activityId }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTranscriptionJob(jobId: string): Promise<{
  jobId: string
  status: string
  transcript: string | null
  summary: string | null
  sentiment: string | null
}> {
  const resp = await fetch(`${API_BASE}/crm/rc/transcribe/${jobId}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// HR Center — Employee Profile & Admin
// =========================================================================

export type HrProfileSection = {
  [key: string]: any
}

export type HrProfile = {
  id: number
  email: string
  badgeNumber: string | null
  firstName: string
  lastName: string
  preferredName: string | null
  position: string | null
  department: string | null
  status: string
  phone: string | null
  profilePhotoKey: string | null
  photoUrl?: string | null
  pipelineId: string | null
  stageId: string | null
  managerId: number | null
  personalInfo: HrProfileSection | null
  emergencyContacts: any[] | null
  employmentInfo: HrProfileSection | null
  compensationInfo: HrProfileSection | null
  taxInfo: HrProfileSection | null
  bankingInfo: HrProfileSection | null
  educationInfo: HrProfileSection | null
  trainingInfo: HrProfileSection | null
  equipmentInfo: HrProfileSection | null
  licensingInfo: HrProfileSection | null
  onboardingInfo: HrProfileSection | null
  performanceInfo: HrProfileSection | null
  dependents: any[] | null
  ssn: string | null
  bankRouting: string | null
  bankAccount: string | null
  createdAt: string
  updatedAt: string
}

export type HrEmployeeSummary = {
  id: number
  email: string
  badgeNumber: string | null
  firstName: string
  lastName: string
  preferredName: string | null
  position: string | null
  department: string | null
  status: string
  phone: string | null
  profilePhotoKey: string | null
  pipelineId: string | null
  stageId: string | null
  hireDate: string
  startDate?: string
  employmentType?: string
}

export async function fetchHrProfile(): Promise<HrProfile> {
  const resp = await fetch(`${API_BASE}/hr/profile`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrProfile(sections: Record<string, any>): Promise<HrProfile> {
  const resp = await fetch(`${API_BASE}/hr/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function revealHrPii(field: string): Promise<{ value: string | null }> {
  const resp = await fetch(`${API_BASE}/hr/profile/pii/${field}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function uploadHrProfilePhoto(file: File): Promise<{ key: string; photoUrl: string | null }> {
  const formData = new FormData()
  formData.append('file', file)
  const resp = await fetch(`${API_BASE}/hr/profile/photo`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })
  if (!resp.ok) {
    const body = await resp.text().catch(() => '')
    throw new Error(body || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function fetchHrEmployees(params: {
  search?: string
  department?: string
  status?: string
  page?: number
  limit?: number
  pipelineId?: string
} = {}): Promise<{ employees: HrEmployeeSummary[]; total: number; page: number; totalPages: number }> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.department) qs.set('department', params.department)
  if (params.status) qs.set('status', params.status)
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.pipelineId) qs.set('pipelineId', params.pipelineId)
  const resp = await fetch(`${API_BASE}/hr/employees?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrEmployee(id: number): Promise<HrProfile> {
  const resp = await fetch(`${API_BASE}/hr/employees/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrEmployeeStage(id: number, stageId: string, pipelineId?: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/employees/${id}/stage`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stageId, pipelineId }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrEmployee(id: number, sections: Record<string, any>): Promise<HrProfile> {
  const resp = await fetch(`${API_BASE}/hr/employees/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createHrEmployee(data: {
  email: string; firstName: string; lastName: string;
  badgeNumber?: string; position?: string; department?: string; phone?: string;
}): Promise<HrProfile> {
  const resp = await fetch(`${API_BASE}/hr/employees`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrDocumentTemplates(): Promise<{ templates: any[] }> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrDocumentAssignments(status?: string): Promise<{ assignments: any[] }> {
  const qs = status ? `?status=${status}` : ''
  const resp = await fetch(`${API_BASE}/hr/documents/assignments${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// HR Document Management
// =========================================================================

export async function uploadHrDocumentTemplate(formData: FormData): Promise<{ success: boolean; id: string; key: string }> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates`, {
    method: 'POST',
    body: formData,
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function downloadHrDocumentTemplate(id: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates/${id}/download`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}

export async function updateHrDocumentTemplate(id: string, data: Record<string, any>): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function saveHrTemplateFields(id: string, fieldPositions: any[]): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates/${id}/fields`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ field_positions: fieldPositions }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteHrDocumentTemplate(id: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/documents/templates/${id}`, {
    method: 'DELETE',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createHrDocumentAssignment(data: {
  template_id: string; employee_ids: number[]; priority?: string; due_date?: string; notes?: string;
}): Promise<{ success: boolean; created: number; ids: number[] }> {
  const resp = await fetch(`${API_BASE}/hr/documents/assignments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function cancelHrDocumentAssignment(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/documents/assignments/${id}`, {
    method: 'DELETE',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function signHrDocumentNative(data: {
  assignmentId: number; signatureDataUrl: string; signerName?: string;
}): Promise<{ success: boolean; signatureKey: string }> {
  const resp = await fetch(`${API_BASE}/hr/signatures/sign-native`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPendingEmployerDocs(): Promise<{ documents: any[] }> {
  const resp = await fetch(`${API_BASE}/hr/admin/onboarding-docs/pending-employer`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function employerSignDoc(id: number, signatureDataUrl?: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/admin/onboarding-docs/${id}/employer-sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signatureDataUrl }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function dismissEmployerDoc(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/admin/onboarding-docs/${id}`, {
    method: 'DELETE',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchFieldNotificationRules(): Promise<{ rules: any[] }> {
  const resp = await fetch(`${API_BASE}/hr/field-notification-rules`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createFieldNotificationRule(data: Record<string, any>): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/hr/field-notification-rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateFieldNotificationRule(id: number, data: Record<string, any>): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/field-notification-rules/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteFieldNotificationRule(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/field-notification-rules/${id}`, {
    method: 'DELETE',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchEmployeeFieldAlerts(id: number): Promise<{ alerts: any[]; total: number }> {
  const resp = await fetch(`${API_BASE}/hr/employees/${id}/field-alerts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrEmailSettings(): Promise<{ settings: any }> {
  const resp = await fetch(`${API_BASE}/hr/settings/email`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrEmailSettings(data: Record<string, any>): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/settings/email`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminStats(): Promise<{
  totalEmployees: number; activeEmployees: number; totalTemplates: number;
  pendingSignatures: number; completedToday: number; overdue: number;
}> {
  const resp = await fetch(`${API_BASE}/hr/admin/stats`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchOnboardingChecklist(id: number): Promise<{ checklist: any[] }> {
  const resp = await fetch(`${API_BASE}/hr/onboarding-checklist/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateOnboardingChecklist(id: number, checklist: any[]): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/hr/onboarding-checklist/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ checklist }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// SMS — Conversations & Messaging
// =========================================================================

export type SmsConversation = {
  id: number
  channel: string
  phone_number: string
  rc_conversation_id: string | null
  unified_client_id: string | null
  subject: string | null
  status: string
  latest_message_preview: string | null
  unread_count: number
  assigned_to: number | null
  created_at: string
  updated_at: string
  client_name?: string | null
}

export type SmsMessage = {
  id: number
  thread_id: number
  sender_type: string
  sender_id: number | null
  body_text: string | null
  direction: string
  rc_message_id: string | null
  message_status: string
  phone_from: string | null
  phone_to: string | null
  read_status: string
  attachments: string | null
  created_at: string
}

export async function fetchSmsConversations(params?: { search?: string; limit?: number; offset?: number }): Promise<{ conversations: SmsConversation[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset) qs.set('offset', String(params.offset))
  const resp = await fetch(`${API_BASE}/crm/rc/sms/conversations?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSmsMessages(threadId: number, limit?: number): Promise<{ messages: SmsMessage[] }> {
  const qs = limit ? `?limit=${limit}` : ''
  const resp = await fetch(`${API_BASE}/crm/rc/sms/conversations/${threadId}/messages${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function sendSmsMessage(from: string, to: string, text: string): Promise<{ success: boolean; messageId: string; threadId: number }> {
  const payload: Record<string, string> = { to, text }
  if (from) payload.from = from
  const resp = await fetch(`${API_BASE}/crm/rc/sms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({ error: 'Unknown error' })) as any
    throw new Error(errBody.error || `SMS failed (${resp.status})`)
  }
  return resp.json()
}

export async function markSmsConversationRead(threadId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/sms/conversations/${threadId}/read`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function syncSmsMessages(): Promise<{ synced: number; errors: string[] }> {
  const resp = await fetch(`${API_BASE}/crm/rc/sms/sync`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSmsNumbers(): Promise<{ numbers: Array<{ phoneNumber: string; usageType: string; features: string[] }> }> {
  const resp = await fetch(`${API_BASE}/crm/rc/sms/numbers`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// CRM Features: Snippets, Message Templates, Playbooks, Orders
// ---------------------------------------------------------------------------

// --- Snippets ---

export type CrmSnippet = {
  id: number
  name: string
  content: string
  shortcut: string
  owner_id: number | null
  use_count: number
  created_at: string
  updated_at: string
}

export async function fetchSnippets(
  opts?: { page?: number; limit?: number; search?: string },
): Promise<{ total: number; snippets: CrmSnippet[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.search) params.set('search', opts.search)
  const resp = await fetch(`${API_BASE}/crm/snippets?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSnippet(id: number): Promise<CrmSnippet> {
  const resp = await fetch(`${API_BASE}/crm/snippets/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createSnippet(fields: Record<string, unknown>): Promise<CrmSnippet> {
  const resp = await fetch(`${API_BASE}/crm/snippets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateSnippet(id: number, fields: Record<string, unknown>): Promise<CrmSnippet> {
  const resp = await fetch(`${API_BASE}/crm/snippets/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteSnippet(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/snippets/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function searchSnippetsByShortcut(q: string): Promise<{ snippets: CrmSnippet[] }> {
  const resp = await fetch(`${API_BASE}/crm/snippets/search?q=${encodeURIComponent(q)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Message Templates ---

export type CrmTemplate = {
  id: number
  name: string
  category: string
  subject: string | null
  body: string
  sharing: string
  folder_id: number | null
  owner_id: number | null
  use_count: number
  created_at: string
  updated_at: string
}

export async function fetchTemplates(
  opts?: { page?: number; limit?: number; search?: string; category?: string },
): Promise<{ total: number; templates: CrmTemplate[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.category) params.set('category', opts.category)
  const resp = await fetch(`${API_BASE}/crm/templates?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTemplate(id: number): Promise<CrmTemplate> {
  const resp = await fetch(`${API_BASE}/crm/templates/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createTemplate(fields: Record<string, unknown>): Promise<CrmTemplate> {
  const resp = await fetch(`${API_BASE}/crm/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateTemplate(id: number, fields: Record<string, unknown>): Promise<CrmTemplate> {
  const resp = await fetch(`${API_BASE}/crm/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteTemplate(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/templates/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Playbooks ---

export type CrmPlaybook = {
  id: number
  title: string
  content: string | null
  status: string
  engagement_type: string | null
  log_submissions: number
  owner_id: number | null
  folder_id: number | null
  total_views: number
  last_viewed_at: string | null
  created_at: string
  updated_at: string
  questions?: CrmPlaybookQuestion[]
}

export type CrmPlaybookQuestion = {
  id: number
  playbook_id: number
  question_text: string
  question_type: string
  options: string | null
  sort_order: number
  required: number
  created_at: string
}

export type CrmPlaybookSubmission = {
  id: number
  playbook_id: number
  entity_type: string
  entity_id: string
  submitted_by: number | null
  answers: string | null
  notes: string | null
  activity_id: number | null
  created_at: string
}

export async function fetchPlaybooks(
  opts?: { page?: number; limit?: number; search?: string; owner?: string; status?: string },
): Promise<{ total: number; playbooks: CrmPlaybook[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.owner) params.set('owner', opts.owner)
  if (opts?.status) params.set('status', opts.status)
  const resp = await fetch(`${API_BASE}/crm/playbooks?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPlaybook(id: number): Promise<CrmPlaybook> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createPlaybook(fields: Record<string, unknown>): Promise<CrmPlaybook> {
  const resp = await fetch(`${API_BASE}/crm/playbooks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updatePlaybook(id: number, fields: Record<string, unknown>): Promise<CrmPlaybook> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deletePlaybook(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function publishPlaybook(id: number): Promise<CrmPlaybook> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${id}/publish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unpublishPlaybook(id: number): Promise<CrmPlaybook> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${id}/unpublish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPlaybookQuestions(playbookId: number): Promise<{ questions: CrmPlaybookQuestion[] }> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/questions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addPlaybookQuestion(playbookId: number, fields: Record<string, unknown>): Promise<CrmPlaybookQuestion> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updatePlaybookQuestion(playbookId: number, questionId: number, fields: Record<string, unknown>): Promise<CrmPlaybookQuestion> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/questions/${questionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deletePlaybookQuestion(playbookId: number, questionId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/questions/${questionId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function submitPlaybook(playbookId: number, fields: Record<string, unknown>): Promise<CrmPlaybookSubmission> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPlaybookSubmissions(playbookId: number): Promise<{ submissions: CrmPlaybookSubmission[] }> {
  const resp = await fetch(`${API_BASE}/crm/playbooks/${playbookId}/submissions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- CRM Projects ---

export type CrmProjectStaff = {
  user_id: string
  name: string
  is_lead: boolean
}

export type CrmProject = {
  id: string | number
  name: string
  client_id: string | null
  client_name: string | null
  contact_id?: string | null
  deal_id?: number | null
  pipeline_stage?: string | null
  estimator_id?: number | null
  estimator_name?: string | null
  services_requested?: string | null
  po_number?: string | null
  contract_value?: number
  status: string
  progress: number
  start_date: string | null
  end_date: string | null
  value: number
  assigned_to: string | null
  description: string | null
  address: string | null
  created_at: string
  updated_at: string
  source?: string | null
  owner_id?: string | null
  owner_name?: string | null
  owner_approval_status?: 'pending' | 'approved' | null
  owner_discount_percent?: number
  senior_id?: string | null
  senior_name?: string | null
  staff?: CrmProjectStaff[]
  drawn_amount?: number
  pending_amount?: number
  pending_count?: number
  value_after_discount?: number
  remaining_amount?: number
}

export async function fetchCrmProjects(
  opts?: {
    page?: number; pageSize?: number; search?: string; status?: string;
    sortField?: string; sortOrder?: string; assignedTo?: string;
    pipelineStages?: string[]; includeCompleted?: boolean; includeCancelled?: boolean;
    holdTypes?: string[]; // Hold type sub-filter (only applies when P_ON_HOLD is in pipelineStages)
    // Rich pill filters (multi-select arrays and single-value pills)
    ownerIds?: string[]; statuses?: string[]; valueRange?: string;
    startDateRange?: string; endDateRange?: string; city?: string; zip?: string;
  },
): Promise<{ data: CrmProject[]; total: number; page: number; pageSize: number; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.sortField) params.set('sortField', opts.sortField)
  if (opts?.sortOrder) params.set('sortOrder', opts.sortOrder)
  if (opts?.assignedTo) params.set('assignedTo', opts.assignedTo)
  if (opts?.pipelineStages && opts.pipelineStages.length > 0) params.set('pipelineStages', opts.pipelineStages.join(','))
  if (opts?.holdTypes && opts.holdTypes.length > 0) params.set('holdTypes', opts.holdTypes.join(','))
  if (opts?.includeCompleted) params.set('includeCompleted', '1')
  if (opts?.includeCancelled) params.set('includeCancelled', '1')
  if (opts?.ownerIds && opts.ownerIds.length > 0) params.set('ownerIds', opts.ownerIds.join(','))
  if (opts?.statuses && opts.statuses.length > 0) params.set('statuses', opts.statuses.join(','))
  if (opts?.valueRange) params.set('valueRange', opts.valueRange)
  if (opts?.startDateRange) params.set('startDateRange', opts.startDateRange)
  if (opts?.endDateRange) params.set('endDateRange', opts.endDateRange)
  if (opts?.city) params.set('city', opts.city)
  if (opts?.zip) params.set('zip', opts.zip)
  const resp = await fetch(`${API_BASE}/crm/projects?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchCrmProject(id: number | string): Promise<CrmProject> {
  const resp = await fetch(`${API_BASE}/crm/projects/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---- Project Work Orders + Files (shared with sub portal via SUB_DB) ----

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  let bin = ''
  const bytes = new Uint8Array(buf)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode(...bytes.subarray(i, i + chunk))
  return btoa(bin)
}

export async function listProjectWorkOrders(projectId: string | number): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const j = await resp.json() as any
  return j.workOrders || []
}

export async function getProjectWorkOrder(projectId: string | number, woId: string): Promise<{ workOrder: any; files: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders/${woId}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createProjectWorkOrder(projectId: string | number, fields: { title: string; description?: string; created_by?: string }): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  const j = await resp.json() as any
  return j.workOrder
}

export async function updateProjectWorkOrder(projectId: string | number, woId: string, fields: Record<string, unknown>): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders/${woId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  const j = await resp.json() as any
  return j.workOrder
}

export async function deleteProjectWorkOrder(projectId: string | number, woId: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders/${woId}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
}

export async function setWorkOrderFeatured(projectId: string | number, woId: string, isFeatured: boolean): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders/${woId}/feature`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_featured: isFeatured }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  const j = await resp.json() as any
  return j.workOrder
}

export async function uploadWorkOrderFile(projectId: string | number, woId: string, file: File): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/work-orders/${woId}/files`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_name: file.name, file_content: await fileToBase64(file), content_type: file.type || 'application/octet-stream' }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function listProjectFiles(projectId: string | number, folder?: string): Promise<any[]> {
  const qs = folder ? `?folder=${encodeURIComponent(folder)}` : ''
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/files${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const j = await resp.json() as any
  return j.files || []
}

export async function uploadProjectFile(projectId: string | number, file: File, folder: 'admin_docs' | 'pre_work' | 'post_work' | 'field_notes', docType?: string): Promise<any> {
  const payload: Record<string, any> = { file_name: file.name, file_content: await fileToBase64(file), content_type: file.type || 'application/octet-stream', folder_type: folder }
  if (docType) payload.doc_type = docType
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/files`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteProjectFile(projectId: string | number, fileId: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/files/${fileId}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
}

export async function renameProjectFile(projectId: string | number, fileId: string, fileName: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/files/${fileId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file_name: fileName }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
}

export async function setProjectFileWorkOrder(projectId: string | number, fileId: string, isWorkOrder: boolean): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/files/${fileId}/work-order`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_work_order: isWorkOrder }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
}

export async function createCrmProject(fields: Record<string, unknown>): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateCrmProject(id: number | string, fields: Record<string, unknown>): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/projects/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteCrmProject(id: number | string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/projects/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateProjectStage(projectId: number, stageId: string) {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/stage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stageId }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateProjectStageWithHold(projectId: number, stageId: string, holdType: string, holdReason: string) {
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/stage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stageId, holdType, holdReason }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function getProjectActivities(projectId: number, opts?: { limit?: number; offset?: number }) {
  const qs = new URLSearchParams()
  if (opts?.limit) qs.set('limit', String(opts.limit))
  if (opts?.offset) qs.set('offset', String(opts.offset))
  const resp = await fetch(`${API_BASE}/crm/projects/${projectId}/activities${qs.size ? `?${qs}` : ''}`)
  if (!resp.ok) return []
  return resp.json()
}

// --- Orders ---

export type CrmOrder = {
  id: number
  order_number: string
  title: string
  unified_client_id: string | null
  deal_id: number | null
  status: string
  start_date: string | null
  end_date: string | null
  assigned_to: number | null
  notes: string | null
  subtotal: number
  tax_rate: number
  tax_amount: number
  discount: number
  total: number
  owner_id: number | null
  created_at: string
  updated_at: string
  client_name?: string | null
  line_items?: CrmOrderLineItem[]
}

export type CrmOrderLineItem = {
  id: number
  order_id: number
  product_id: number | null
  description: string
  quantity: number
  unit_price: number
  total: number
  sort_order: number
  created_at: string
}

export async function fetchOrders(
  opts?: { page?: number; limit?: number; search?: string; status?: string; assigned_to?: string },
): Promise<{ total: number; orders: CrmOrder[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.search) params.set('search', opts.search)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.assigned_to) params.set('assigned_to', opts.assigned_to)
  const resp = await fetch(`${API_BASE}/crm/orders?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchOrder(id: number): Promise<CrmOrder> {
  const resp = await fetch(`${API_BASE}/crm/orders/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createOrder(fields: Record<string, unknown>): Promise<CrmOrder> {
  const resp = await fetch(`${API_BASE}/crm/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateOrder(id: number, fields: Record<string, unknown>): Promise<CrmOrder> {
  const resp = await fetch(`${API_BASE}/crm/orders/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` })) as any
    throw new Error(err.error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteOrder(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/orders/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateOrderStatus(id: number, status: string): Promise<CrmOrder> {
  const resp = await fetch(`${API_BASE}/crm/orders/${id}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addOrderLineItem(orderId: number, fields: Record<string, unknown>): Promise<CrmOrderLineItem> {
  const resp = await fetch(`${API_BASE}/crm/orders/${orderId}/line-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateOrderLineItem(orderId: number, lineItemId: number, fields: Record<string, unknown>): Promise<CrmOrderLineItem> {
  const resp = await fetch(`${API_BASE}/crm/orders/${orderId}/line-items/${lineItemId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteOrderLineItem(orderId: number, lineItemId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/orders/${orderId}/line-items/${lineItemId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchNextOrderNumber(): Promise<{ nextNumber: string }> {
  const resp = await fetch(`${API_BASE}/crm/orders/next-number`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Sequences ---

export type Sequence = {
  id: number
  name: string
  description: string | null
  sequence_type: string
  is_active: number
  created_by: number | null
  settings_json: string | null
  stats_json: string | null
  folder_id: number | null
  created_at: string
  updated_at: string
}

export type SequenceStep = {
  id: number
  sequence_id: number
  step_order: number
  step_type: string
  delay_amount: number
  delay_unit: string
  config_json: string | null
  template_id: number | null
  created_at: string
}

export type SequenceEnrollment = {
  id: number
  sequence_id: number
  contact_id: number
  enrolled_by: number | null
  status: string
  current_step: number
  enrolled_at: string
  completed_at: string | null
  unenrolled_at: string | null
  unenroll_reason: string | null
  next_step_at: string | null
  contact_name?: string
  contact_email?: string
}

export type EmailTemplate = {
  id: number
  name: string
  subject: string
  body_html: string | null
  body_text: string | null
  template_type: string
  created_by: number | null
  is_active: number
  stats_json: string | null
  created_at: string
  updated_at: string
}

export async function fetchSequences(
  opts?: { page?: number; limit?: number; is_active?: number; search?: string },
): Promise<{ total: number; sequences: Sequence[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.is_active !== undefined) params.set('is_active', String(opts.is_active))
  if (opts?.search) params.set('search', opts.search)
  const resp = await fetch(`${API_BASE}/crm/sequences?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSequence(id: number): Promise<Sequence & { steps: SequenceStep[] }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createSequence(fields: Record<string, unknown>): Promise<Sequence> {
  const resp = await fetch(`${API_BASE}/crm/sequences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateSequence(id: number, fields: Record<string, unknown>): Promise<Sequence> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteSequence(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addSequenceStep(seqId: number, fields: Record<string, unknown>): Promise<SequenceStep> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/steps`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function replaceSequenceSteps(seqId: number, steps: Record<string, unknown>[]): Promise<{ sequenceId: number; steps: SequenceStep[] }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/steps`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ steps }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateSequenceStep(seqId: number, stepId: number, fields: Record<string, unknown>): Promise<SequenceStep> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/steps/${stepId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteSequenceStep(seqId: number, stepId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/steps/${stepId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSequenceEnrollments(
  seqId: number, opts?: { page?: number; limit?: number; status?: string },
): Promise<{ total: number; enrollments: SequenceEnrollment[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.status) params.set('status', opts.status)
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/enrollments?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function enrollInSequence(seqId: number, contactIds: number[], opts?: { enrolled_by?: number; start_at?: string }): Promise<{ success: boolean; enrolled_count: number }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/enroll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact_ids: contactIds, ...opts }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unenrollFromSequence(seqId: number, contactIds: number[], reason?: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/unenroll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact_ids: contactIds, reason }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSequenceStats(seqId: number): Promise<{ total_enrolled: number; active: number; completed: number; replied: number; bounced: number }> {
  const resp = await fetch(`${API_BASE}/crm/sequences/${seqId}/stats`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Chatflows ---

export type Chatflow = {
  id: number
  name: string
  chatflow_type: string
  is_active: number
  welcome_message: string | null
  availability_mode: string
  availability_json: string | null
  targeting_json: string | null
  appearance_json: string | null
  assignment_json: string | null
  language: string
  created_by: number | null
  created_at: string
  updated_at: string
}

export type ChatflowBotAction = {
  id: number
  chatflow_id: number
  action_order: number
  action_type: string
  config_json: string | null
  parent_action_id: number | null
  branch_label: string | null
  next_action_id: number | null
}

export type ChatflowConversation = {
  id: number
  chatflow_id: number
  visitor_name: string | null
  visitor_email: string | null
  contact_id: number | null
  status: string
  assigned_to: number | null
  started_at: string
  closed_at: string | null
  rating: number | null
}

export type ChatflowMessage = {
  id: number
  conversation_id: number
  sender_type: string
  sender_id: string | null
  message_text: string | null
  message_html: string | null
  attachments_json: string | null
  sent_at: string
}

export async function fetchChatflows(
  opts?: { page?: number; limit?: number; chatflow_type?: string; is_active?: number },
): Promise<{ total: number; chatflows: Chatflow[]; pages: number }> {
  const params = new URLSearchParams()
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.chatflow_type) params.set('chatflow_type', opts.chatflow_type)
  if (opts?.is_active !== undefined) params.set('is_active', String(opts.is_active))
  const resp = await fetch(`${API_BASE}/crm/chatflows?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchChatflow(id: number): Promise<Chatflow & { actions: ChatflowBotAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createChatflow(fields: Record<string, unknown>): Promise<Chatflow> {
  const resp = await fetch(`${API_BASE}/crm/chatflows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
    throw new Error((err as { error: string }).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateChatflow(id: number, fields: Record<string, unknown>): Promise<Chatflow> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteChatflow(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function replaceChatflowActions(cfId: number, actions: Record<string, unknown>[]): Promise<{ chatflowId: number; actions: ChatflowBotAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${cfId}/actions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ actions }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addChatflowAction(cfId: number, fields: Record<string, unknown>): Promise<ChatflowBotAction> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${cfId}/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchChatflowStats(cfId: number): Promise<{ total_conversations: number; active_now: number; resolved: number }> {
  const resp = await fetch(`${API_BASE}/crm/chatflows/${cfId}/stats`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// --- Enhanced Workflow endpoints ---

export async function saveWorkflowCanvas(id: number, data: Record<string, unknown>): Promise<Workflow & { actions: WorkflowAction[] }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${id}/canvas`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchWorkflowStats(id: number): Promise<{ total_runs: number; active_runs: number; completed_runs: number; errored_runs: number }> {
  const resp = await fetch(`${API_BASE}/crm/workflows/${id}/stats`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Marketing — Contact Lists
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchContactLists(): Promise<{ lists: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/contact-lists`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchContactList(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/contact-lists/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createContactList(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/contact-lists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateContactList(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contact-lists/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteContactList(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contact-lists/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Marketing — Marketing Emails
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchMarketingEmails(opts?: { status?: string; search?: string; page?: number; pageSize?: number }): Promise<{ emails: any[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.search) params.set('search', opts.search)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  const resp = await fetch(`${API_BASE}/crm/marketing-emails?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Marketing Hub read-only collection helpers used by the Home pulse. The
// module is optional in some deployments, so callers should treat 401/404/503
// as an unavailable widget rather than a dashboard-wide error.
export async function fetchMarketingRuns(opts?: { limit?: number; status?: string }): Promise<{ runs: any[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams()
  qs.set('limit', String(opts?.limit ?? 5))
  if (opts?.status) qs.set('status', opts.status)
  const resp = await fetch(`${API_BASE}/marketing/runs?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchMarketingDeliverables(opts?: { limit?: number }): Promise<{ deliverables: any[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams({ limit: String(opts?.limit ?? 5) })
  const resp = await fetch(`${API_BASE}/marketing/deliverables?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchMarketingEmail(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/marketing-emails/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createMarketingEmail(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/marketing-emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateMarketingEmail(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/marketing-emails/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteMarketingEmail(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/marketing-emails/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchEmailAnalytics(): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/marketing-emails/analytics`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchEmailTemplates(): Promise<{ templates: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/email-templates`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createEmailTemplate(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/email-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteEmailTemplate(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/email-templates/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchAdAccounts(): Promise<{ accounts: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/ad-accounts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createAdAccount(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/ad-accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteAdAccount(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/ad-accounts/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchSocialAccounts(): Promise<{ accounts: any[] }> {
  const resp = await fetch(`${API_BASE}/crm/social-accounts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchLandingPage(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createLandingPage(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLandingPage(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteLandingPage(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function publishLandingPage(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages/${id}/publish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unpublishLandingPage(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/landing-pages/${id}/unpublish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchAdCampaigns(opts?: { network?: string; status?: string; page?: number; pageSize?: number }): Promise<{ campaigns: any[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  if (opts?.network) params.set('network', opts.network)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  const resp = await fetch(`${API_BASE}/crm/ad-campaigns?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createAdCampaign(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/ad-campaigns`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateAdCampaign(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/ad-campaigns/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteAdCampaign(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/ad-campaigns/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchAdAnalytics(): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/ad-campaigns/analytics`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchSocialPosts(opts?: { status?: string; platform?: string; page?: number; pageSize?: number }): Promise<{ posts: any[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.platform) params.set('platform', opts.platform)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  const resp = await fetch(`${API_BASE}/crm/social-posts?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSocialPost(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/social-posts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSocialPost(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/social-posts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteSocialPost(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/social-posts/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function publishSocialPost(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/social-posts/${id}/publish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchLandingPages(opts?: { status?: string; search?: string; page?: number; pageSize?: number }): Promise<{ pages: any[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.search) params.set('search', opts.search)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize))
  const resp = await fetch(`${API_BASE}/crm/landing-pages?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Sales Forecasts
// ---------------------------------------------------------------------------

export async function fetchForecasts(params: { pipeline?: string; period_start?: string; period_end?: string }): Promise<any> {
  const qs = new URLSearchParams()
  if (params.pipeline) qs.set('pipeline', params.pipeline)
  if (params.period_start) qs.set('period_start', params.period_start)
  if (params.period_end) qs.set('period_end', params.period_end)
  const resp = await fetch(`${API_BASE}/sales/forecasts?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchForecastGoals(pipeline: string, periodStart?: string): Promise<any> {
  const qs = new URLSearchParams({ pipeline })
  if (periodStart) qs.set('period_start', periodStart)
  const resp = await fetch(`${API_BASE}/sales/forecast-goals?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function saveForecastGoal(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/sales/forecast-goals`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateForecastGoal(id: number, goalAmount: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/sales/forecast-goals/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ goal_amount: goalAmount }) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchForecastMappings(pipeline: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/sales/forecast-category-mappings?pipeline=${encodeURIComponent(pipeline)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateForecastMappings(pipelineName: string, mappings: any[]): Promise<any> {
  const resp = await fetch(`${API_BASE}/sales/forecast-category-mappings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pipeline_name: pipelineName, mappings }) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Commerce: Payment Links
// ---------------------------------------------------------------------------

export async function fetchPaymentLinks(params?: { search?: string; status?: string; page?: number; pageSize?: number }): Promise<any> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.status) qs.set('status', params.status)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/commerce/payment-links?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPaymentLink(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/commerce/payment-links/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createPaymentLink(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/commerce/payment-links`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updatePaymentLink(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/commerce/payment-links/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deletePaymentLink(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/commerce/payment-links/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Commerce: Payments
export async function fetchPayments(params?: { status?: string; page?: number; pageSize?: number }): Promise<any> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/commerce/payments?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createPayment(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/commerce/payments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Service: Knowledge Base
// ---------------------------------------------------------------------------

export async function fetchKbArticles(params?: { search?: string; status?: string; category_id?: number; page?: number; pageSize?: number }): Promise<any> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.status) qs.set('status', params.status)
  if (params?.category_id) qs.set('category_id', String(params.category_id))
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/service/kb/articles?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchKbArticle(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createKbArticle(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateKbArticle(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function publishKbArticle(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles/${id}/publish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unpublishKbArticle(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles/${id}/unpublish`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteKbArticle(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/articles/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchKbCategories(): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/categories`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createKbCategory(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/categories`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateKbCategory(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/categories/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteKbCategory(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/kb/categories/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Service: Customer Portal
// ---------------------------------------------------------------------------

export async function fetchPortalSettings(): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/settings`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updatePortalSettings(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPortalTickets(params?: { search?: string; status?: string; priority?: string; page?: number; pageSize?: number }): Promise<any> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.status) qs.set('status', params.status)
  if (params?.priority) qs.set('priority', params.priority)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/service/portal/tickets?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchPortalTicket(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/tickets/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createPortalTicket(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/tickets`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updatePortalTicket(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/tickets/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addPortalTicketComment(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/portal/tickets/${id}/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Service: Feedback Surveys
// ---------------------------------------------------------------------------

export async function fetchSurveys(params?: { search?: string; survey_type?: string; status?: string; page?: number; pageSize?: number }): Promise<any> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.survey_type) qs.set('survey_type', params.survey_type)
  if (params?.status) qs.set('status', params.status)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/service/surveys?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSurvey(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createSurvey(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateSurvey(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function activateSurvey(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}/activate`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function pauseSurvey(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}/pause`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function closeSurvey(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}/close`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSurveyResponses(id: number, page?: number, pageSize?: number): Promise<any> {
  const qs = new URLSearchParams()
  if (page) qs.set('page', String(page))
  if (pageSize) qs.set('pageSize', String(pageSize))
  const resp = await fetch(`${API_BASE}/service/surveys/${id}/responses?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSurveyAnalytics(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/service/surveys/${id}/analytics`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Accounting Module
// ---------------------------------------------------------------------------

export async function fetchAcctAccounts(params?: { type?: string; is_active?: string; search?: string }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.type) qs.set('type', params.type)
  if (params?.is_active !== undefined) qs.set('is_active', params.is_active)
  if (params?.search) qs.set('search', params.search)
  const resp = await fetch(`${API_BASE}/accounting/accounts?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAcctAccountsSummary(): Promise<{ data: Record<string, number> }> {
  const resp = await fetch(`${API_BASE}/accounting/accounts/summary`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createAcctAccount(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/accounts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateAcctAccount(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/accounts/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteAcctAccount(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/accounts/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchJournalEntries(params?: { status?: string; search?: string; page?: number; pageSize?: number; signal?: AbortSignal }): Promise<{ data: any[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/accounting/journal-entries?${qs}`, { signal: params?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchJournalEntry(id: number): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/journal-entries/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createJournalEntry(data: any): Promise<{ success: boolean; id: number; entry_number: string }> {
  const resp = await fetch(`${API_BASE}/accounting/journal-entries`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function postJournalEntry(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/journal-entries/${id}/post`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function voidJournalEntry(id: number, reason?: string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/journal-entries/${id}/void`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAcctVendors(params?: { search?: string; type?: string }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.type) qs.set('type', params.type)
  const resp = await fetch(`${API_BASE}/accounting/vendors?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createAcctVendor(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/vendors`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateAcctVendor(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/vendors/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteAcctVendor(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/vendors/${id}`, { method: 'DELETE' })
  const data = await resp.json().catch(() => ({})) as any
  if (!resp.ok) throw new Error(data?.error || `API error: ${resp.status}`)
  return data
}

// Create an org-vendor (AP "Add Vendor" button → creates a contacts organization + bridges to acct_vendors).
export async function createOrgVendor(data: { name: string; vendor_type?: string; email?: string; phone?: string; is_1099?: number; payment_terms?: string; default_expense_account_id?: number | null; notes?: string }): Promise<{ success: boolean; unified_client_id: string; vendor_id: number | null; vendor_name: string }> {
  const resp = await fetch(`${API_BASE}/accounting/vendors/org`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  const out = await resp.json().catch(() => ({})) as any
  if (!resp.ok) throw new Error(out?.error || `API error: ${resp.status}`)
  return out
}

// Toggle whether an organization IS a vendor (org record header switch). enabled=true
// creates/reactivates the bridged acct_vendors row; enabled=false deactivates it.
export async function setOrganizationVendor(orgId: string, enabled: boolean): Promise<{ success: boolean; is_vendor: boolean; vendor_id: number | null }> {
  const resp = await fetch(`${API_BASE}/crm/organizations/${encodeURIComponent(orgId)}/vendor`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }) })
  const out = await resp.json().catch(() => ({})) as any
  if (!resp.ok) throw new Error(out?.error || `API error: ${resp.status}`)
  return out
}

// Create an org-vendor from a scan and auto-link the scan to it (returns vendor_id for picker auto-select).
export async function createVendorFromScan(data: { scan_id: number; name: string; vendor_type?: string; email?: string; phone?: string; is_1099?: number }): Promise<{ success: boolean; unified_client_id: string; vendor_id: number | null; vendor_name: string }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/create-vendor-from-scan`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  const out = await resp.json().catch(() => ({})) as any
  if (!resp.ok) throw new Error(out?.error || `API error: ${resp.status}`)
  return out
}

// Batch-approve scanned invoices — pass explicit ids[] or a filter (pending + vendor-matched + confidence>=0.8).
export async function batchApproveScans(payload: { ids?: number[]; filter?: { division?: string; status?: string; min_confidence?: number }; reviewed_by?: string }): Promise<{ success: boolean; total: number; approved: number; skipped: number; no_vendor: number; errors: number; results: Array<{ scan_id: number; result: string; bill_id?: number; journal_entry_id?: number | null; error?: string }> }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/batch-approve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
  const out = await resp.json().catch(() => ({})) as any
  if (!resp.ok) throw new Error(out?.error || `API error: ${resp.status}`)
  return out
}

export async function fetchAcctBills(params?: { status?: string; search?: string; page?: number }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  const resp = await fetch(`${API_BASE}/accounting/bills?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createAcctBill(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/bills`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function voidAcctBill(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/bills/${id}/void`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchApSummary(options?: { signal?: AbortSignal }): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/ap/summary`, { signal: options?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createBillPayment(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/bill-payments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAcctInvoices(params?: { status?: string; search?: string; page?: number }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  const resp = await fetch(`${API_BASE}/accounting/invoices?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createAcctInvoice(data: any): Promise<{ success: boolean; id: number; invoice_number: string }> {
  const resp = await fetch(`${API_BASE}/accounting/invoices`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createAcctJob(data: any): Promise<{ success: boolean; id: number; job_number: string }> {
  const resp = await fetch(`${API_BASE}/accounting/jobs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function sendAcctInvoice(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/invoices/${id}/send`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function voidAcctInvoice(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/invoices/${id}/void`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchArSummary(options?: { signal?: AbortSignal }): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/ar/summary`, { signal: options?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createCustomerPayment(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/customer-payments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// AR invoice detail (header + line items + payments/allocations) for the
// read-only invoice drawer. Backed by GET /api/accounting/invoices/:id.
export async function fetchAcctInvoiceDetail(id: number): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/invoices/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Read-only paginated expenses list over acct_expenses (accounting-gated).
export async function fetchAcctExpenses(params?: { search?: string; page?: number; pageSize?: number }): Promise<{ data: any[]; total: number; totalAmount: number; page: number; pageSize: number }> {
  const qs = new URLSearchParams()
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/accounting/expenses?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchBankAccounts(options?: { signal?: AbortSignal }): Promise<{ data: any[] }> {
  const resp = await fetch(`${API_BASE}/accounting/bank-accounts`, { signal: options?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchBankTransactions(params?: { bank_account_id?: string; is_reconciled?: string }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.bank_account_id) qs.set('bank_account_id', params.bank_account_id)
  if (params?.is_reconciled !== undefined) qs.set('is_reconciled', params.is_reconciled)
  const resp = await fetch(`${API_BASE}/accounting/bank-transactions?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createBankTransaction(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/bank-transactions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createReconciliation(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/reconciliations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function completeReconciliation(id: number, cleared_transaction_ids: number[]): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/reconciliations/${id}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cleared_transaction_ids }) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createPlaidLinkToken(connectionId?: number): Promise<{ link_token: string }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/plaid/link-token`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(connectionId ? { connection_id: connectionId } : {}) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function exchangePlaidToken(payload: { public_token: string; label?: string; account_map: Array<{ provider_account_id: string; bank_account_id: number }> }): Promise<{ success: boolean; connection_id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/plaid/exchange`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchFeedConnections(): Promise<{ data: any[] }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/connections`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function triggerFeedSync(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/connections/${id}/sync`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchFeedReview(): Promise<{ data: any[] }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/review`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function acceptFeedTxn(id: number, body: { account_id?: number; transfer_account_id?: number; vendor_id?: number; job_id?: number }): Promise<{ success: boolean; journal_entry_id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/review/${id}/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function excludeFeedTxn(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/review/${id}/exclude`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchFeedRules(): Promise<{ data: any[] }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/rules`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createFeedRule(rule: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/rules`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rule) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteFeedRule(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/feeds/rules/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAcctJobs(params?: { status?: string; search?: string; page?: number }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  const resp = await fetch(`${API_BASE}/accounting/jobs?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAcctJob(id: number): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/jobs/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchProfitLoss(params?: { start_date?: string; end_date?: string; signal?: AbortSignal }): Promise<{ data: any[]; start_date: string; end_date: string }> {
  const qs = new URLSearchParams()
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const resp = await fetch(`${API_BASE}/accounting/reports/profit-loss?${qs}`, { signal: params?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchBalanceSheet(params?: { as_of_date?: string }): Promise<{ data: any[]; as_of_date: string }> {
  const qs = new URLSearchParams()
  if (params?.as_of_date) qs.set('as_of_date', params.as_of_date)
  const resp = await fetch(`${API_BASE}/accounting/reports/balance-sheet?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchTrialBalance(params?: { as_of_date?: string; show_zero?: boolean }): Promise<{ data: any[]; as_of_date: string }> {
  const qs = new URLSearchParams()
  if (params?.as_of_date) qs.set('as_of_date', params.as_of_date)
  if (params?.show_zero) qs.set('show_zero', '1')
  const resp = await fetch(`${API_BASE}/accounting/reports/trial-balance?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchArAging(params?: { as_of_date?: string }): Promise<{ data: any[]; as_of_date: string }> {
  const qs = new URLSearchParams()
  if (params?.as_of_date) qs.set('as_of_date', params.as_of_date)
  const resp = await fetch(`${API_BASE}/accounting/reports/ar-aging?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchArAgingSummary(params?: { as_of_date?: string; signal?: AbortSignal }): Promise<{ data: Record<string, { count: number; total: number }>; as_of_date: string }> {
  const qs = new URLSearchParams()
  if (params?.as_of_date) qs.set('as_of_date', params.as_of_date)
  const resp = await fetch(`${API_BASE}/accounting/reports/ar-aging/summary?${qs}`, { signal: params?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAccountingAlerts(options?: { signal?: AbortSignal }): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/overview/alerts`, { signal: options?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchInvoiceScanSummary(options?: { signal?: AbortSignal }): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/summary`, { signal: options?.signal })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchAccountLedger(id: number, params?: { start_date?: string; end_date?: string; page?: number; pageSize?: number }): Promise<{ data: any; total: number; page: number; pageSize: number; start_date: string | null; end_date: string | null }> {
  const qs = new URLSearchParams()
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
  const resp = await fetch(`${API_BASE}/accounting/accounts/${id}/ledger?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchWipSchedule(params?: { include_completed?: boolean }): Promise<{ data: any[] }> {
  const qs = new URLSearchParams()
  if (params?.include_completed) qs.set('include_completed', '1')
  const resp = await fetch(`${API_BASE}/accounting/reports/wip-schedule?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// =========================================================================
// Invoice Scanner (n8n AP Bot Integration)
// =========================================================================

export async function fetchInvoiceScans(params?: { status?: string; search?: string; page?: number; pageSize?: number; includeApproved?: boolean; includeRejected?: boolean; dateFrom?: string; dateTo?: string; vendorId?: number; jobId?: number; dealId?: string; subProjectId?: string; division?: string; sortField?: string; sortOrder?: 'ASC' | 'DESC' }): Promise<{ data: any[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.division) qs.set('division', params.division)
  if (params?.sortField) qs.set('sort_field', params.sortField)
  if (params?.sortOrder) qs.set('sort_order', params.sortOrder)
  if (params?.search) qs.set('search', params.search)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.includeApproved) qs.set('include_approved', '1')
  if (params?.includeRejected) qs.set('include_rejected', '1')
  if (params?.dateFrom) qs.set('date_from', params.dateFrom)
  if (params?.dateTo) qs.set('date_to', params.dateTo)
  if (params?.vendorId !== undefined && params?.vendorId !== null) qs.set('vendor_id', String(params.vendorId))
  if (params?.jobId !== undefined && params?.jobId !== null) qs.set('job_id', String(params.jobId))
  if (params?.dealId) qs.set('deal_id', params.dealId)
  if (params?.subProjectId) qs.set('sub_project_id', params.subProjectId)
  qs.set('pageSize', String(params?.pageSize ?? 500))
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/queue?${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchInvoiceScanDetail(id: number): Promise<{ data: any }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export function invoiceScanPdfUrl(id: number): string {
  return `${API_BASE}/accounting/invoice-scanner/${id}/pdf`
}

// Server-side conversion of a CRM R2 file (e.g. .docx/.doc) to PDF for lossless
// in-browser preview. Mirrors the inline `/api/crm/files?key=` URL pattern but
// hits the dedicated PDF-conversion route. `key` is the raw (un-encoded) R2 key.
export function crmFilePdfUrl(key: string): string {
  return `${API_BASE}/crm/files/pdf?key=${encodeURIComponent(key)}`
}

export async function approveInvoiceScan(id: number, data: any): Promise<{ success: boolean; bill_id: number }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/${id}/approve`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function rejectInvoiceScan(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/${id}/reject`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateInvoiceScanMatch(id: number, data: any): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/accounting/invoice-scanner/${id}/match`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Subcontractor Portal Admin API
// ---------------------------------------------------------------------------

export async function fetchSubAdminUsers(): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/users`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Phase 2: Projects + Calendar
export async function fetchSubAdminProjects(params?: URLSearchParams, signal?: AbortSignal): Promise<any> {
  const qs = params?.toString() ? '?' + params.toString() : ''
  const resp = await fetch(`${API_BASE}/sub-admin/projects${qs}`, signal ? { signal } : undefined); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function fetchSubAdminAssignableUsers(projectId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${projectId}/assignable-users`); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function updateSubAdminProjectOwners(id: string, ownerIds: string[], actorId?: string|null): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${id}/owner`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({owner_ids: ownerIds, actor_id: actorId ?? null}) }); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function updateSubAdminProjectSeniors(id: string, seniorIds: string[], actorId?: string|null): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${id}/senior`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({senior_ids: seniorIds, actor_id: actorId ?? null}) }); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function updateSubAdminProjectStaff(id: string, staffIds: string[]): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${id}/staff`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({staff_ids:staffIds}) }); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function fetchSubAdminProjectChat(projectId: string, opts?: { as?: 'admin' | 'owner' }): Promise<any> {
  const q = opts?.as ? `?as=${opts.as}` : ''
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${projectId}/chat${q}`); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
export async function sendSubAdminChatMessage(projectId: string, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/sub-admin/projects/${projectId}/chat`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(data) }); if (!resp.ok) throw new Error(`API error: ${resp.status}`); return resp.json()
}
// ---------------------------------------------------------------------------
// Target Accounts
// ---------------------------------------------------------------------------

export type TargetAccount = {
  id: number; name: string; company_name: string; address: string; city: string; state: string;
  phone: string; email: string; is_target_account: number; target_tier: number | null;
  target_status: string | null; engagement_score: number; created_at: string; updated_at: string;
  deal_count: number; total_deal_value: number; activity_count: number; last_activity: string | null;
}

export async function fetchTargetAccounts(params?: Record<string, string>): Promise<{ data: TargetAccount[]; total: number }> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  const resp = await fetch(`${API_BASE}/crm/target-accounts${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function markTargetAccount(id: number, data: { tier?: number; status?: string }): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/target-accounts/${id}/mark`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function unmarkTargetAccount(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/target-accounts/${id}/unmark`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateTargetAccount(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/target-accounts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function recalculateEngagementScores(): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/target-accounts/recalculate`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Sales Documents
// ---------------------------------------------------------------------------

export type SalesDocument = {
  id: number; title: string; doc_type: string; description: string | null; file_url: string | null;
  content_html: string | null; created_by: string; share_slug: string; views_count: number;
  unique_views: number; last_viewed_at: string | null; is_active: number; created_at: string; updated_at: string;
}

export type SalesDocView = {
  id: number; document_id: number; viewer_email: string | null; viewer_name: string | null;
  duration_seconds: number; pages_viewed: number; created_at: string;
}

export async function fetchSalesDocuments(params?: Record<string, string>): Promise<{ data: SalesDocument[]; total: number }> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  const resp = await fetch(`${API_BASE}/crm/sales-documents${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchSalesDocument(id: number): Promise<{ data: SalesDocument & { views: SalesDocView[] } }> {
  const resp = await fetch(`${API_BASE}/crm/sales-documents/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createSalesDocument(data: any): Promise<{ success: boolean; id: number; share_slug: string }> {
  const resp = await fetch(`${API_BASE}/crm/sales-documents`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateSalesDocument(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/sales-documents/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteSalesDocument(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/sales-documents/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Activity Feed
// ---------------------------------------------------------------------------

export async function fetchActivityFeed(params?: Record<string, string>): Promise<{ data: any[]; total: number; page: number; pageSize: number }> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  const resp = await fetch(`${API_BASE}/crm/activity-feed${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Meeting Types / Scheduler
// ---------------------------------------------------------------------------

export type MeetingType = {
  id: number; name: string; slug: string; duration_minutes: number; description: string | null;
  location: string; availability_json: string; buffer_minutes: number; owner_name: string;
  color: string; is_active: number; created_at: string; updated_at: string; bookings_count: number;
}

export type MeetingBooking = {
  id: number; meeting_type_id: number; guest_name: string; guest_email: string; guest_phone: string | null;
  booking_date: string; start_time: string; end_time: string; notes: string | null;
  status: string; created_at: string; meeting_type_name: string; duration_minutes: number;
}

export async function fetchMeetingTypes(): Promise<{ data: MeetingType[]; total: number }> {
  const resp = await fetch(`${API_BASE}/crm/meeting-types`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchMeetingType(id: number): Promise<{ data: MeetingType & { bookings: MeetingBooking[] } }> {
  const resp = await fetch(`${API_BASE}/crm/meeting-types/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createMeetingType(data: any): Promise<{ success: boolean; id: number; slug: string }> {
  const resp = await fetch(`${API_BASE}/crm/meeting-types`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateMeetingType(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/meeting-types/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteMeetingType(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/meeting-types/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchMeetingBookings(params?: Record<string, string>): Promise<{ data: MeetingBooking[]; total: number }> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  const resp = await fetch(`${API_BASE}/crm/meeting-bookings${qs}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createMeetingBooking(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/meeting-bookings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Coaching Playlists
// ---------------------------------------------------------------------------

export type CoachingPlaylist = {
  id: number; name: string; description: string | null; created_by: string; is_public: number;
  items_count: number; total_duration_seconds: number; created_at: string; updated_at: string;
}

export type PlaylistItem = {
  id: number; playlist_id: number; activity_id: number | null; title: string; notes: string | null;
  recording_url: string | null; duration_seconds: number; sort_order: number; added_by: string; created_at: string;
}

export async function fetchCoachingPlaylists(): Promise<{ data: CoachingPlaylist[]; total: number }> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchCoachingPlaylist(id: number): Promise<{ data: CoachingPlaylist & { items: PlaylistItem[] } }> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createCoachingPlaylist(data: any): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateCoachingPlaylist(id: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteCoachingPlaylist(id: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function addPlaylistItem(playlistId: number, data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists/${playlistId}/items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function removePlaylistItem(playlistId: number, itemId: number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/coaching-playlists/${playlistId}/items/${itemId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ---------------------------------------------------------------------------
// Prospecting Workspace
// ---------------------------------------------------------------------------

export async function fetchProspectingSummary(): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/prospecting-summary`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ========== HR CENTER ADMIN PORTAL API ==========

export async function fetchHrAdminEmployees(opts?: { search?: string; department?: string; subsidiary?: string; status?: string; pipelineId?: string; page?: number; limit?: number }): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.search) params.set('search', opts.search)
  if (opts?.department) params.set('department', opts.department)
  if (opts?.subsidiary) params.set('subsidiary', opts.subsidiary)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.pipelineId) params.set('pipelineId', opts.pipelineId)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/hr-admin/employees?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminEmployee(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrAdminEmployee(id: string, updates: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminPositions(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/positions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

/** Email a contact the HR application link (ops.botpros.ai/employees).
 *  Wires the "Send application" button on a contact record — Carly's
 *  4/30 ask. */
export async function sendHrApplicationLink(opts: { to: string; recipientName?: string; senderName?: string }): Promise<{ success: boolean; sent_to: string }> {
  const resp = await fetch(`${API_BASE}/hr-admin/applications/send-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

/** Allocate the next ID in a numbering sequence. Used at deal / project
 *  / invoice create time so records like 26BP0001 are auto-assigned.
 *  See `numbering_sequences` table + `/api/admin/numbering` endpoint. */
export async function allocateNextNumber(sequenceKey: string): Promise<{ id: string; sequence_key: string; year: number; serial: number; format: string }> {
  const resp = await fetch(`${API_BASE}/admin/numbering/next/${encodeURIComponent(sequenceKey)}`, {
    method: 'POST',
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

/** List configured numbering sequences (for the Preferences → Numbering
 *  page). Returns one row per (sequence_key, year). */
export async function fetchNumberingSequences(): Promise<{ sequences: Array<{ sequence_key: string; year: number; last_serial: number; format: string; description: string; updated_at: string }> }> {
  const resp = await fetch(`${API_BASE}/admin/numbering`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ─── Territories ──────────────────────────────────────────────────────
export type TerritoryRosterEntry = {
  roster_id: number
  user_id: number
  name: string
  email: string | null
  role: string | null
  is_lead: boolean
}
export type TerritoryCity = {
  id: number
  city: string
  state: string
  zip_prefix: string | null
  notes: string | null
}
export type Territory = {
  id: number
  zone_code: string
  display_name: string
  description: string | null
  active: number
  sort_order: number
  commercial: TerritoryRosterEntry[]
  residential: TerritoryRosterEntry[]
  cities: TerritoryCity[]
}

export async function fetchTerritories(): Promise<{ territories: Territory[] }> {
  const resp = await fetch(`${API_BASE}/admin/territories`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createTerritory(opts: { zone_code: string; display_name?: string; description?: string; sort_order?: number }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/admin/territories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateTerritory(id: number, opts: { display_name?: string; description?: string; sort_order?: number; active?: number | boolean }): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/admin/territories/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteTerritory(id: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/admin/territories/${id}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function addTerritoryCity(territoryId: number, opts: { city: string; state?: string; zip_prefix?: string; notes?: string }): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/admin/territories/${territoryId}/cities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteTerritoryCity(territoryId: number, cityId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/admin/territories/${territoryId}/cities/${cityId}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function addTerritoryRosterEntry(territoryId: number, input: { user_id: number; segment: 'commercial' | 'residential'; is_lead?: boolean }): Promise<TerritoryRosterEntry> {
  const resp = await fetch(`${API_BASE}/admin/territories/${territoryId}/roster`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  const result: { entry: TerritoryRosterEntry & { roster_id: number } } = await resp.json()
  return result.entry
}

export async function removeTerritoryRosterEntry(territoryId: number, entryId: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/territories/${territoryId}/roster/${entryId}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
}

// Cached territory roster (cities + commercial/residential assignees). The
// roster rarely changes within a session, so the first fetch is memoized and
// reused by every estimate/lead picker that needs a city→territory→estimator
// lookup. A failed fetch clears the cache so a later open can retry.
let _territoriesCachePromise: Promise<Territory[]> | null = null
export function loadTerritoriesCached(): Promise<Territory[]> {
  if (!_territoriesCachePromise) {
    _territoriesCachePromise = fetchTerritories()
      .then((r) => r.territories || [])
      .catch((err) => { _territoriesCachePromise = null; throw err })
  }
  return _territoriesCachePromise
}

// Resolve a location (city/state, optionally zip) to the territory that covers
// it and the estimator (user) ids assigned to that territory's roster for the
// given segment. This is the SAME basis the lead-routing list uses to highlight
// the matching salesperson on incoming leads (city-keyed match over
// territory_cities, joined to user_territories) — reused here so a residential
// estimate's picker can green-highlight whoever owns that zone.
//   • Pass 1: case-insensitive city match (when both sides carry a state, they
//     must agree so a same-named city elsewhere can't false-match).
//   • Pass 2: zip_prefix fallback when the city is unknown/unmatched.
// Returns { territory: null, userIds: [] } when nothing matches — callers then
// highlight nothing (no error).
export function matchTerritoryEstimators(
  territories: Territory[],
  loc: { city?: string | null; state?: string | null; zip?: string | null },
  segment: 'residential' | 'commercial' = 'residential',
): { territory: Territory | null; userIds: number[] } {
  const city = (loc.city || '').trim().toLowerCase()
  const state = (loc.state || '').trim().toLowerCase()
  const zip = (loc.zip || '').trim()
  const rosterIds = (t: Territory): number[] =>
    (segment === 'commercial' ? t.commercial : t.residential).map((r) => r.user_id)
  if (city) {
    for (const t of territories) {
      if (t.active === 0) continue
      const hit = t.cities.some((c) => {
        if ((c.city || '').trim().toLowerCase() !== city) return false
        const cState = (c.state || '').trim().toLowerCase()
        return !(state && cState && state !== cState)
      })
      if (hit) return { territory: t, userIds: rosterIds(t) }
    }
  }
  if (zip) {
    for (const t of territories) {
      if (t.active === 0) continue
      const hit = t.cities.some((c) => {
        const zp = (c.zip_prefix || '').trim()
        return zp.length > 0 && zip.startsWith(zp)
      })
      if (hit) return { territory: t, userIds: rosterIds(t) }
    }
  }
  return { territory: null, userIds: [] }
}

// ─────────────────────────────────────────────────────────────────────
// Inspection Module — Carly + Eddie's 4/30 spec, foundation for the
// Mon 5/11 deliverable. Catalog management + inspection records +
// auto-generated proposals. iPad walkthrough UI ships in a later PR.
// ─────────────────────────────────────────────────────────────────────

export type InspectionPricingModel = 'per-foot' | 'flat' | 'notes-only'
export type InspectionStatus = 'draft' | 'in-progress' | 'completed' | 'sent' | 'signed'
export type InspectionDistributionMode = 'per-homeowner' | 'hoa-rollup'

export type InspectionPhoto = {
  id: number
  item_id: number
  r2_key: string
  original_filename: string | null
  content_type: string | null
  byte_size: number | null
  taken_at: string | null
  drawn_overlay_r2_key: string | null
  created_at: string | null
}

export type InspectionItem = {
  id: number
  inspection_id: number
  catalog_id: number
  item_key: string
  label: string
  category: string | null
  pricing_model: InspectionPricingModel
  unit_label: string | null
  default_unit_price: number | null
  requires_footage: number
  requires_photo: number
  location: string | null
  footage: number | null
  condition_flags: string | null
  notes: string | null
  created_at: string | null
  photos: InspectionPhoto[]
}

export type Inspection = {
  id: number
  property_address: string
  property_city: string | null
  hoa_id: number | null
  territory_id: number | null
  zone_code: string | null
  territory_name: string | null
  due_date: string | null
  status: InspectionStatus
  inspector_id: number | null
  inspector_name: string | null
  distribution_mode: InspectionDistributionMode
  notes: string | null
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
  item_count?: number
}

export type InspectionProposal = {
  id: number
  inspection_id: number
  proposal_number: string
  status: 'draft' | 'sent' | 'signed' | 'declined' | 'expired'
  sent_to_email: string | null
  sent_at: string | null
  signed_at: string | null
  signature_data: string | null
  deadline: string | null
  total_amount: number
  created_at: string | null
  updated_at: string | null
}

export type InspectionProposalLine = {
  id: number
  proposal_id: number
  inspection_item_id: number | null
  label: string
  quantity: number
  unit: string | null
  unit_price: number
  line_total: number
  accepted: number | null
  decline_reason: string | null
  sort_order: number
  created_at: string | null
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json() as Promise<T>
}

// ─── Inspections (auth-only) ──────────────────────────────────────────
export async function fetchInspections(opts?: {
  status?: InspectionStatus
  inspectorId?: number
}): Promise<{ inspections: Inspection[] }> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.inspectorId != null) params.set('inspector_id', String(opts.inspectorId))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const resp = await fetch(`${API_BASE}/inspections${qs}`)
  return jsonOrThrow<{ inspections: Inspection[] }>(resp)
}

export async function createInspection(opts: {
  property_address: string
  property_city?: string
  hoa_id?: number | null
  territory_id?: number | null
  due_date?: string | null
  status?: InspectionStatus
  inspector_id?: number | null
  distribution_mode?: InspectionDistributionMode
  notes?: string | null
}): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/inspections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean; id: number }>(resp)
}

export async function fetchInspection(id: number): Promise<{ inspection: Inspection; items: InspectionItem[] }> {
  const resp = await fetch(`${API_BASE}/inspections/${id}`)
  return jsonOrThrow<{ inspection: Inspection; items: InspectionItem[] }>(resp)
}

export async function updateInspection(
  id: number,
  opts: Partial<{
    property_address: string
    property_city: string | null
    hoa_id: number | null
    territory_id: number | null
    due_date: string | null
    status: InspectionStatus
    inspector_id: number | null
    distribution_mode: InspectionDistributionMode
    notes: string | null
  }>,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/inspections/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean }>(resp)
}

export async function addInspectionItem(
  inspectionId: number,
  opts: {
    catalog_id: number
    location?: string
    footage?: number
    condition_flags?: string[] | string
    notes?: string
  },
): Promise<{ success: boolean; id: number }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean; id: number }>(resp)
}

export async function updateInspectionItem(
  inspectionId: number,
  itemId: number,
  opts: Partial<{
    location: string | null
    footage: number | null
    condition_flags: string[] | string | null
    notes: string | null
  }>,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean }>(resp)
}

export async function deleteInspectionItem(
  inspectionId: number,
  itemId: number,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/items/${itemId}`, {
    method: 'DELETE',
  })
  return jsonOrThrow<{ success: boolean }>(resp)
}

export async function uploadInspectionItemPhoto(
  inspectionId: number,
  itemId: number,
  file: File,
  takenAt?: string,
): Promise<{ success: boolean; id: number; r2_key: string }> {
  const fd = new FormData()
  fd.append('file', file)
  if (takenAt) fd.append('taken_at', takenAt)
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/items/${itemId}/photo`, {
    method: 'POST',
    body: fd,
  })
  return jsonOrThrow<{ success: boolean; id: number; r2_key: string }>(resp)
}

export async function generateInspectionProposal(
  inspectionId: number,
  opts?: { deadline?: string; sent_to_email?: string },
): Promise<{ success: boolean; id: number; proposal_number: string; total_amount: number; line_count: number }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/generate-proposal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts || {}),
  })
  return jsonOrThrow<{ success: boolean; id: number; proposal_number: string; total_amount: number; line_count: number }>(resp)
}

export type InspectionProposalDetail = InspectionProposal & {
  property_address: string
  property_city: string | null
  distribution_mode: InspectionDistributionMode
  zone_code: string | null
  territory_name: string | null
}

export async function fetchInspectionProposal(
  inspectionId: number,
  proposalId: number,
): Promise<{ proposal: InspectionProposalDetail; lines: InspectionProposalLine[] }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/proposal/${proposalId}`)
  return jsonOrThrow<{ proposal: InspectionProposalDetail; lines: InspectionProposalLine[] }>(resp)
}

export async function updateInspectionProposal(
  inspectionId: number,
  proposalId: number,
  opts: Partial<{
    status: 'draft' | 'sent' | 'signed' | 'declined' | 'expired'
    sent_to_email: string | null
    deadline: string | null
  }>,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/proposal/${proposalId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean }>(resp)
}

export async function signInspectionProposal(
  inspectionId: number,
  proposalId: number,
  opts: {
    signature_data: string
    lines?: Array<{ id: number; accepted: boolean | null; decline_reason?: string | null }>
  },
): Promise<{ success: boolean; status: 'signed' | 'declined'; signed_at: string }> {
  const resp = await fetch(`${API_BASE}/inspections/${inspectionId}/proposal/${proposalId}/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return jsonOrThrow<{ success: boolean; status: 'signed' | 'declined'; signed_at: string }>(resp)
}

export async function deleteInspectionItemPhoto(
  inspectionId: number,
  itemId: number,
  photoId: number,
): Promise<{ success: boolean }> {
  const resp = await fetch(
    `${API_BASE}/inspections/${inspectionId}/items/${itemId}/photo/${photoId}`,
    { method: 'DELETE' },
  )
  return jsonOrThrow<{ success: boolean }>(resp)
}

// Mint a public-link for a homeowner to view + sign a proposal without
// SSO. Returns the URL the admin can copy/email. See PUBLIC_API_INVENTORY
// for the security model.
export async function createProposalPublicLink(
  inspectionId: number,
  proposalId: number,
  opts?: { expires_in_days?: number },
): Promise<{ token: string; url: string; expires_at: string }> {
  const resp = await fetch(
    `${API_BASE}/inspections/${inspectionId}/proposal/${proposalId}/public-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts || {}),
    },
  )
  return jsonOrThrow<{ token: string; url: string; expires_at: string }>(resp)
}

// Send the public-link email to the homeowner via env.EMAIL. The worker
// auto-appends the sender's user_email_signatures row. The admin can
// alternately just use the URL returned from createProposalPublicLink
// in an external mail client.
export async function sendProposalPublicLink(
  inspectionId: number,
  proposalId: number,
  opts: { to: string; url: string; subject?: string; recipientName?: string },
): Promise<{ success: boolean; sent_to: string }> {
  const resp = await fetch(
    `${API_BASE}/inspections/${inspectionId}/proposal/${proposalId}/send-public-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    },
  )
  return jsonOrThrow<{ success: boolean; sent_to: string }>(resp)
}

// URL helper for <img src> — server validates auth via cookie. The trailing
// `?v=…` query string is a safe cache buster the caller can pass when the
// photo is freshly uploaded so the browser doesn't show a stale 404.
export function inspectionPhotoUrl(inspectionId: number, photoId: number): string {
  return `${API_BASE}/inspections/${inspectionId}/photo/${photoId}`
}

// ─────────────────────────────────────────────────────────────────────
// Lead routing config + per-user state — Carly's 4/28 spec.
// ─────────────────────────────────────────────────────────────────────
export type LeadRoutingConfig = {
  routing_model: 'shared-inbox' | 'round-robin'
  require_note_on_touch: number
  show_notepad_icon: number
  show_unread_state: number
  base_code_assignee_group: string | null
}

export async function fetchLeadRoutingConfig(): Promise<LeadRoutingConfig> {
  const resp = await fetch(`${API_BASE}/admin/lead-routing/config`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const data = await resp.json() as any
  return {
    routing_model: (data.routing_model === 'round-robin' ? 'round-robin' : 'shared-inbox'),
    require_note_on_touch: data.require_note_on_touch ? 1 : 0,
    show_notepad_icon: data.show_notepad_icon ? 1 : 0,
    show_unread_state: data.show_unread_state ? 1 : 0,
    base_code_assignee_group: data.base_code_assignee_group ?? null,
  }
}

export async function updateLeadRoutingConfig(opts: Partial<{
  routing_model: 'shared-inbox' | 'round-robin'
  require_note_on_touch: boolean | number
  show_notepad_icon: boolean | number
  show_unread_state: boolean | number
  base_code_assignee_group: string | null
}>): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/admin/lead-routing/config`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function markLeadRead(leadId: number | string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/leads/${leadId}/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ read: true, action: 'opened' }),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function markLeadNoteLogged(leadId: number | string): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/leads/${leadId}/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note_logged: true, action: 'noted' }),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ─────────────────────────────────────────────────────────────────────
// Shared inboxes (Preferences → Inboxes). Migration 092.
// ─────────────────────────────────────────────────────────────────────
export type SharedInbox = {
  id: number
  name: string
  source_label: string | null
  assignee_user_ids: string | null   // JSON array of numbers + sentinel strings
  claim_model: 'shared-inbox' | 'round-robin'
  active: number
  sort_order: number
  created_at: string | null
  updated_at: string | null
}

export async function fetchSharedInboxes(): Promise<SharedInbox[]> {
  const resp = await fetch(`${API_BASE}/admin/shared-inboxes`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  const data = await resp.json() as { inboxes: SharedInbox[] }
  return data.inboxes || []
}

export async function createSharedInbox(input: {
  name: string
  source_label?: string | null
  assignee_user_ids?: Array<number | string> | null
  claim_model?: 'shared-inbox' | 'round-robin'
  active?: boolean | number
  sort_order?: number
}): Promise<SharedInbox> {
  const resp = await fetch(`${API_BASE}/admin/shared-inboxes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateSharedInbox(id: number, input: Partial<{
  name: string
  source_label: string | null
  assignee_user_ids: Array<number | string> | null
  claim_model: 'shared-inbox' | 'round-robin'
  active: boolean | number
  sort_order: number
}>): Promise<SharedInbox> {
  const resp = await fetch(`${API_BASE}/admin/shared-inboxes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteSharedInbox(id: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/shared-inboxes/${id}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

// ─────────────────────────────────────────────────────────────────────
// RingCentral org-level Calling settings (Preferences → Calling). Migration 096.
// KV pattern: GET returns a flat key->value map (`settings`) plus the
// raw row metadata (`rows`); PATCH accepts a partial object of pairs.
// ─────────────────────────────────────────────────────────────────────
export type RcOrgSettings = Record<string, string | null>

export async function fetchRcOrgSettings(): Promise<{ settings: RcOrgSettings; rows: Array<{ key: string; value: string | null; updated_at: string | null; updated_by: number | null }> }> {
  const resp = await fetch(`${API_BASE}/admin/rc-settings`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateRcOrgSettings(patch: Record<string, string | null>): Promise<{ success: boolean; updated: number }> {
  const resp = await fetch(`${API_BASE}/admin/rc-settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

// ─────────────────────────────────────────────────────────────────────
// Marketing email templates (Preferences → Marketing → Email). Migration 097.
// Distinct from the legacy /api/crm/email-templates endpoints used by the
// sequences view — same underlying table, but the admin surface uses the
// new `category` column for filtering and never exposes sequence stats.
// ─────────────────────────────────────────────────────────────────────
export type AdminEmailTemplate = {
  id: number
  name: string
  subject: string
  body_html: string | null
  body_text: string | null
  category: string | null
  is_active: number
  created_at: string
  updated_at: string
}

export async function fetchAdminEmailTemplates(): Promise<AdminEmailTemplate[]> {
  const resp = await fetch(`${API_BASE}/admin/email-templates`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  const data = await resp.json() as { templates: AdminEmailTemplate[] }
  return data.templates || []
}

export async function createAdminEmailTemplate(input: Partial<Omit<AdminEmailTemplate, 'id' | 'created_at' | 'updated_at'>>): Promise<AdminEmailTemplate> {
  const resp = await fetch(`${API_BASE}/admin/email-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateAdminEmailTemplate(id: number, input: Partial<Omit<AdminEmailTemplate, 'id' | 'created_at' | 'updated_at'>>): Promise<AdminEmailTemplate> {
  const resp = await fetch(`${API_BASE}/admin/email-templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteAdminEmailTemplate(id: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/email-templates/${id}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

// ─────────────────────────────────────────────────────────────────────
// Marketing forms (Preferences → Marketing → Forms). Migration 098.
// Listing returns each form with its `fields[]` array inlined so the
// editor can render without a second fetch.
// ─────────────────────────────────────────────────────────────────────
export type MarketingFormField = {
  id: number
  form_id: number
  label: string
  field_key: string
  field_type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox'
  required: number
  sort_order: number
  options_json: string | null
  created_at: string
}

export type MarketingForm = {
  id: number
  slug: string
  name: string
  redirect_url: string | null
  notification_emails: string | null
  is_active: number
  created_at: string
  updated_at: string
  fields?: MarketingFormField[]
}

export async function fetchMarketingForms(): Promise<MarketingForm[]> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  const data = await resp.json() as { forms: MarketingForm[] }
  return data.forms || []
}

export async function createMarketingForm(input: { slug: string; name: string; redirect_url?: string | null; notification_emails?: string | null; is_active?: boolean | number }): Promise<MarketingForm> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateMarketingForm(id: number, input: Partial<{ slug: string; name: string; redirect_url: string | null; notification_emails: string | null; is_active: boolean | number }>): Promise<MarketingForm> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteMarketingForm(id: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms/${id}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

export async function createMarketingFormField(formId: number, input: { label: string; field_key: string; field_type: MarketingFormField['field_type']; required?: boolean | number; sort_order?: number; options_json?: string | null }): Promise<MarketingFormField> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms/${formId}/fields`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateMarketingFormField(formId: number, fieldId: number, input: Partial<{ label: string; field_key: string; field_type: MarketingFormField['field_type']; required: boolean | number; sort_order: number; options_json: string | null }>): Promise<MarketingFormField> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms/${formId}/fields/${fieldId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteMarketingFormField(formId: number, fieldId: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/marketing-forms/${formId}/fields/${fieldId}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

// ─────────────────────────────────────────────────────────────────────
// Object field definitions (Preferences → Data → Objects). Migration 099.
// One client per object type — admins manage every type's fields
// through the same UI shell parameterized by `objectType`.
// ─────────────────────────────────────────────────────────────────────
export type ObjectField = {
  id: number
  object_type: string
  field_key: string
  label: string
  field_type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox' | 'number' | 'date' | 'currency'
  required: number
  default_value: string | null
  options_json: string | null
  sort_order: number
  is_system: number
  created_at: string
  updated_at: string
}

export async function fetchObjectFields(objectType: string): Promise<ObjectField[]> {
  const resp = await fetch(`${API_BASE}/admin/object-fields/${encodeURIComponent(objectType)}`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  const data = await resp.json() as { object_type: string; fields: ObjectField[] }
  return data.fields || []
}

export async function createObjectField(objectType: string, input: Partial<Omit<ObjectField, 'id' | 'object_type' | 'is_system' | 'created_at' | 'updated_at'>>): Promise<ObjectField> {
  const resp = await fetch(`${API_BASE}/admin/object-fields/${encodeURIComponent(objectType)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateObjectField(objectType: string, fieldId: number, input: Partial<Omit<ObjectField, 'id' | 'object_type' | 'is_system' | 'created_at' | 'updated_at'>>): Promise<ObjectField> {
  const resp = await fetch(`${API_BASE}/admin/object-fields/${encodeURIComponent(objectType)}/${fieldId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteObjectField(objectType: string, fieldId: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/object-fields/${encodeURIComponent(objectType)}/${fieldId}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

// ─────────────────────────────────────────────────────────────────────
// Saved views — per-user column / sort / filter / density. Migration 093.
// `SavedView` type is declared above (legacy /api/crm/views section).
// ─────────────────────────────────────────────────────────────────────
export async function fetchSavedViews(objectType?: string): Promise<SavedView[]> {
  const qs = objectType ? `?objectType=${encodeURIComponent(objectType)}` : ''
  const resp = await fetch(`${API_BASE}/crm/saved-views${qs}`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  const data = await resp.json() as { views: SavedView[] }
  return data.views || []
}

export async function createSavedView(input: {
  name: string
  objectType: string
  filtersJson?: unknown
  sortsJson?: unknown
  columnsJson?: unknown
  density?: string | null
  isDefault?: boolean | number
  isShared?: boolean | number
}): Promise<SavedView> {
  const resp = await fetch(`${API_BASE}/crm/saved-views`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateSavedView(id: number, input: Partial<{
  name: string
  objectType: string
  filtersJson: unknown
  sortsJson: unknown
  columnsJson: unknown
  density: string | null
  isDefault: boolean | number
  isShared: boolean | number
}>): Promise<SavedView> {
  const resp = await fetch(`${API_BASE}/crm/saved-views/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteSavedView(id: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/saved-views/${id}`, { method: 'DELETE' })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
}

// ─────────────────────────────────────────────────────────────────────
// Org-level KV settings (Tax / Forms / Accounting Defaults). Migration 094.
// ─────────────────────────────────────────────────────────────────────
export type OrgSettings = {
  settings: Record<string, string | null>
  rows: Array<{ key: string; value: string | null; updated_at: string | null; updated_by: number | null }>
}

export async function fetchOrgSettings(): Promise<OrgSettings> {
  const resp = await fetch(`${API_BASE}/admin/org-settings`)
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function updateOrgSettings(patch: Record<string, string | number | boolean | null>): Promise<{ success: boolean; updated: number }> {
  const body: Record<string, string | null> = {}
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined) body[k] = null
    else if (typeof v === 'boolean') body[k] = v ? '1' : '0'
    else body[k] = String(v)
  }
  const resp = await fetch(`${API_BASE}/admin/org-settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!resp.ok) {
    const e = await resp.json().catch(() => ({}))
    throw new Error((e as any).error || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function fetchHrAdminPipelines(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/pipelines`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminProfiles(pipelineId?: string): Promise<any> {
  const params = new URLSearchParams()
  if (pipelineId) params.set('pipelineId', pipelineId)
  const resp = await fetch(`${API_BASE}/hr-admin/profiles?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrAdminProfileStage(id: string, stageId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${id}/stage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stageId }) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateHrAdminProfileSubsidiary(id: string, subsidiary: string | null): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${id}/subsidiary`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subsidiary }) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateHrAdminProfileStartDate(id: string, startDate: string | null): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${id}/start-date`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ startDate }) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminApplications(opts?: { search?: string; status?: string; page?: number; limit?: number }): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.search) params.set('search', opts.search)
  if (opts?.status) params.set('status', opts.status)
  if (opts?.page) params.set('page', String(opts.page))
  if (opts?.limit) params.set('limit', String(opts.limit))
  const resp = await fetch(`${API_BASE}/hr-admin/applications?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminIncompleteApps(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/applications/incomplete`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminApplication(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/applications/${id}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteHrAdminApplication(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/applications/${id}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminAdmins(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/admins`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function promoteHrAdmin(employeeId: string, notes?: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/admins`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ employeeId, notes }) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function removeHrAdmin(employeeId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/admins/${employeeId}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminEmailSettings(days?: number): Promise<any> {
  const params = days ? `?days=${days}` : ''
  const resp = await fetch(`${API_BASE}/hr-admin/settings/email${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrAdminEmailSettings(settings: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/settings/email`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminFieldRules(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/field-notification-rules`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createHrAdminFieldRule(rule: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/field-notification-rules`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rule) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateHrAdminFieldRule(id: string, rule: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/field-notification-rules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rule) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteHrAdminFieldRule(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/field-notification-rules/${id}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminEmailTemplates(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/email-templates`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrAdminEmailTemplate(type: string, data: { subject: string; body: string }): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/email-templates/${type}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminEmailLogs(opts?: { status?: string; page?: number }): Promise<any> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.page) params.set('page', String(opts.page))
  const resp = await fetch(`${API_BASE}/hr-admin/settings/email/logs?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminEmployeeDocuments(employeeId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${employeeId}/documents`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// File operations
export async function downloadHrAdminFile(employeeId: string, field: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${employeeId}/file/${field}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const contentType = resp.headers.get('content-type') || ''
  if (contentType.includes('application/json')) return resp.json()
  return resp.blob()
}

export async function uploadHrAdminFile(employeeId: string, field: string, file: File, label?: string): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)
  if (label) formData.append('label', label)
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${employeeId}/file/${field}`, { method: 'POST', body: formData })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteHrAdminFile(employeeId: string, field: string, index?: number): Promise<any> {
  const url = index !== undefined ? `${API_BASE}/hr-admin/employees/${employeeId}/file/${field}?index=${index}` : `${API_BASE}/hr-admin/employees/${employeeId}/file/${field}`
  const resp = await fetch(url, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Field alerts
export async function fetchHrAdminEmployeeAlerts(employeeId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/employees/${employeeId}/field-alerts`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Profile documents
export async function fetchHrAdminProfileDocs(profileId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${profileId}/documents`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function generateHrAdminOnboardingLink(profileId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${profileId}/generate-onboarding-link`, { method: 'POST' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// ---------------------------------------------------------------------------
// HR Form Definitions (drag-drop form builder — "Forms" sub-tab)
//
// Application forms (employee-application, subcontractor-application, …) are
// stored as versioned `schema_json` documents on the HR side. The builder
// edits a draft, then publishes a new immutable version. Endpoints:
//   GET    /api/hr-admin/form-definitions            → list of form keys
//   GET    /api/hr-admin/form-definitions/:key       → one form (published + draft)
//   PUT    /api/hr-admin/form-definitions/:key/draft → save the working draft
//   POST   /api/hr-admin/form-definitions/:key/publish → cut a new version
// ---------------------------------------------------------------------------

/** A single field inside a step. */
export type HrFormField = {
  /** Stable field key — used as the submission column / answer key. */
  key: string
  type:
    | 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'radio'
    | 'checkbox' | 'checkboxMulti' | 'tagInput' | 'date' | 'file' | 'rosterRows'
  label: string
  placeholder?: string
  required?: boolean
  helpText?: string
  /** select / radio / checkboxMulti option list. */
  options?: { label: string; value: string }[]
  /** Free-form validation hints (pattern, min, max, maxLength, accept, …). */
  validation?: Record<string, any>
  /** Conditional show-if rule: render this field only when another field's
   *  value matches. `{ field, op, value }`. */
  showIf?: { field: string; op: 'eq' | 'neq' | 'in' | 'truthy'; value?: any } | null
  /** System fields are structural / locked — the inspector renders a lock and
   *  forbids deleting or retyping them. */
  locked?: boolean
  /** Editor-internal passthrough: the ORIGINAL raw field object as it was
   *  loaded from the published/draft schema (renderer "bare-array" shape, keyed
   *  by `id`). Re-serialization (publishForm/saveFormDraft) spreads this first
   *  and overlays only the props the visual editor models, so complex renderer
   *  options (grouped checkboxMulti, RadioOption booleans, documentStep/filePair
   *  configs) survive a round-trip untouched and never break the live form. */
  _raw?: Record<string, any>
}

export type HrFormStep = {
  id: string
  title: string
  description?: string
  fields: HrFormField[]
  /** Editor-internal passthrough: original raw step object (renderer shape,
   *  carrying `stepIndex` and any extra keys) preserved across edits. */
  _raw?: Record<string, any>
}

export type HrFormSchema = {
  title?: string
  steps: HrFormStep[]
}

/** Which population a form targets. The API returns this verbatim. */
export type HrFormAudience = 'employee' | 'subcontractor' | string

export type HrFormDefinition = {
  /** Stable form_key (e.g. "subcontractor-application"). */
  key: string
  /** Human-friendly display name (derived from form_key when absent). */
  name: string
  /** Currently published (live) version number, or 0 if never published. */
  version: number
  /** Real lifecycle status from the API: 'published' | 'draft' | 'none'. */
  status?: 'published' | 'draft' | 'none' | string
  /** Target audience — drives the employee vs subcontractor split. */
  audience?: HrFormAudience
  /** True when an unpublished draft row exists. */
  hasDraft?: boolean
  updated_at?: string
  /** Live, published schema. */
  schema_json?: HrFormSchema | string
  /** Unpublished working copy (preferred by the builder when present). */
  draft_schema?: HrFormSchema | string | null
}

/** Turn a form_key like "subcontractor-application" into "Subcontractor Application". */
export function prettyFormName(key: string): string {
  if (!key) return 'Untitled form'
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Map a raw API form row (camelCase, formKey/latestVersion/status/audience) into HrFormDefinition. */
function normalizeFormDefinition(r: any): HrFormDefinition {
  const key = r?.key ?? r?.formKey ?? r?.form_key ?? ''
  const version = r?.version ?? r?.latestPublishedVersion ?? r?.latest_published_version ?? r?.latestVersion ?? r?.latest_version ?? 0
  return {
    key,
    name: r?.name || r?.title || prettyFormName(key),
    version: typeof version === 'number' ? version : (version ? Number(version) : 0),
    status: r?.status,
    audience: r?.audience,
    hasDraft: r?.hasDraft ?? (r?.has_draft != null ? Number(r.has_draft) > 0 : undefined),
    updated_at: r?.updatedAt ?? r?.updated_at,
    schema_json: r?.schema_json ?? r?.schema,
    draft_schema: r?.draft_schema,
  }
}

/** List the available form definitions, normalized to HrFormDefinition[]. */
export async function fetchFormDefinitions(): Promise<{ success?: boolean; data: HrFormDefinition[]; forms: HrFormDefinition[] }> {
  const resp = await fetch(`${API_BASE}/hr-admin/form-definitions`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const json: any = await resp.json()
  const raw: any[] = Array.isArray(json?.forms) ? json.forms : Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
  const list = raw.map(normalizeFormDefinition)
  return { success: json?.success, data: list, forms: list }
}

/** Fetch one form definition (published schema + any draft), normalized for the builder. */
export async function fetchFormDefinition(key: string): Promise<{ success?: boolean; data: HrFormDefinition }> {
  const resp = await fetch(`${API_BASE}/hr-admin/form-definitions/${encodeURIComponent(key)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const json: any = await resp.json()
  // API shape: { success, formKey, draft: {schema,...}|null, published: {schema,...}|null, history }
  const published = json?.published ?? null
  const draft = json?.draft ?? null
  const base = published || draft || json?.data || json || {}
  const def: HrFormDefinition = {
    key: json?.formKey ?? base?.formKey ?? base?.form_key ?? key,
    name: base?.name || prettyFormName(json?.formKey ?? key),
    version: published?.version ?? base?.version ?? 0,
    status: draft ? 'draft' : (published ? 'published' : 'none'),
    audience: published?.audience ?? draft?.audience ?? base?.audience,
    hasDraft: !!draft,
    updated_at: base?.updatedAt ?? base?.updated_at,
    schema_json: published?.schema ?? base?.schema ?? base?.schema_json,
    draft_schema: draft?.schema ?? base?.draft_schema ?? null,
  }
  return { success: json?.success, data: def }
}

/**
 * Serialize the editor's working schema back to the CANONICAL renderer shape:
 * a bare array of step-groups `[{ stepIndex, title, fields: [{ id, label, type,
 * required, stepIndex, placeholder?, helpText?, options?, validation?,
 * conditionalOn? }] }]`. This is the exact shape the public renderer
 * (FormRenderer, GET /api/hr/form-definitions/:key) reads, so publishing from
 * the editor never breaks the live forms.
 *
 * Round-trip safety: each field/step spreads its `_raw` passthrough FIRST, then
 * overlays only the props the visual editor actually models. Complex renderer
 * options (grouped checkboxMulti, RadioOption booleans, documentStep/filePair)
 * and any extra keys the editor doesn't surface are preserved verbatim.
 */
/** Project any renderer option structure into the flat {label,value}[] the
 *  visual inspector shows. MUST mirror `toSimpleOption` in Forms.tsx so an
 *  unchanged round-trip is detected and the rich raw options are preserved. */
function simplifyOptions(raw: any): { label: string; value: string }[] | undefined {
  if (!Array.isArray(raw)) return undefined
  return raw.map((o: any) => {
    if (typeof o === 'string') return { label: o, value: o }
    const label = o?.label ?? o?.category ?? ''
    const value = o?.value ?? o?.label ?? ''
    return { label: String(label), value: typeof value === 'boolean' ? String(value) : String(value) }
  })
}

/** True when the editor's flat option list equals the flattened raw list (i.e.
 *  the user did not touch the options, so we keep the rich raw version). */
function sameSimpleOptions(a?: { label: string; value: string }[], b?: { label: string; value: string }[]): boolean {
  if (!a || !b) return false
  if (a.length !== b.length) return false
  return a.every((o, i) => o.label === b[i].label && o.value === b[i].value)
}

// Field types the visual editor can actually model (mirrors FIELD_TYPES in
// Forms.tsx). The renderer also stores SYSTEM types the editor doesn't model
// (ssn, directDepositRoster, profilePhoto, filePair, rosterWithPhoto,
// documentStep, …) — those fields load locked and their raw `type` must survive
// a round-trip verbatim, never coerced to 'text'.
const EDITOR_FIELD_TYPES = new Set([
  'text', 'email', 'tel', 'textarea', 'select', 'radio',
  'checkbox', 'checkboxMulti', 'tagInput', 'date', 'file', 'rosterRows',
])

/** Does the editor's showIf, projected back, equal the raw conditionalOn? When
 *  true we keep the raw conditionalOn verbatim (preserving its exact encoding,
 *  e.g. `{field, equals: v}` vs `{field, operator, value}`). */
function showIfMatchesRawCond(showIf: any, rawCond: any): boolean {
  if (!showIf || !rawCond || typeof rawCond !== 'object') return false
  if (String(rawCond.field) !== String(showIf.field)) return false
  // Pull the operator+value out of whatever encoding the raw used.
  let rawOp: string | undefined; let rawVal: any
  if ('operator' in rawCond) { rawOp = rawCond.operator; rawVal = rawCond.value }
  else if ('equals' in rawCond) { rawOp = 'equals'; rawVal = rawCond.equals }
  else if ('notEquals' in rawCond) { rawOp = 'notEquals'; rawVal = rawCond.notEquals }
  else if ('in' in rawCond) { rawOp = 'in'; rawVal = rawCond.in }
  else if ('truthy' in rawCond) { rawOp = 'truthy'; rawVal = rawCond.truthy }
  const opMap: Record<string, string> = { eq: 'equals', neq: 'notEquals', in: 'in', truthy: 'truthy' }
  const editorOp = opMap[showIf.op] || showIf.op
  if (editorOp !== rawOp) return false
  if (editorOp === 'truthy') return true
  return JSON.stringify(rawVal) === JSON.stringify(showIf.value)
}

export function serializeFormSchema(schema: HrFormSchema): any[] {
  const editorModelsOptions = (t: string) => t === 'select' || t === 'radio' || t === 'checkboxMulti'
  return (schema.steps || []).map((st, si) => {
    const raw = st._raw || {}
    const stepIndex = typeof raw.stepIndex === 'number' ? raw.stepIndex : si
    const fields = (st.fields || []).map((f) => {
      const fr = f._raw || {}
      // Preserve a system/raw type the editor can't model (so it isn't coerced
      // to 'text' on save). Only take the editor's type when it's one the editor
      // actually models AND the raw type was itself editor-modeled.
      const rawType = fr.type
      const keepRawType = rawType && !EDITOR_FIELD_TYPES.has(rawType)
      const out: Record<string, any> = {
        ...fr,
        id: f.key,
        label: f.label,
        type: keepRawType ? rawType : f.type,
        required: !!f.required,
        stepIndex,
      }
      if (f.placeholder) out.placeholder = f.placeholder; else delete out.placeholder
      if (f.helpText) out.helpText = f.helpText; else delete out.helpText
      // Validation: keep editor edits when present, else fall back to raw.
      if (f.validation && Object.keys(f.validation).length) out.validation = f.validation
      else if (fr.validation) out.validation = fr.validation
      else delete out.validation
      // showIf (editor) maps to conditionalOn (renderer). Keep the raw
      // conditionalOn verbatim when the user did not change the rule; only
      // re-emit a normalized `{field, operator, value}` when it genuinely
      // changed. Clearing the rule drops conditionalOn.
      if (f.showIf) {
        if (showIfMatchesRawCond(f.showIf, fr.conditionalOn)) {
          out.conditionalOn = fr.conditionalOn
        } else {
          const opMap: Record<string, string> = { eq: 'equals', neq: 'notEquals', in: 'in', truthy: 'truthy' }
          out.conditionalOn = { field: f.showIf.field, operator: opMap[f.showIf.op] || f.showIf.op, value: f.showIf.value }
        }
      } else {
        delete out.conditionalOn
      }
      // Options. The visual inspector can only model a flat {label,value}[] list,
      // but the renderer also stores RICH option structures (grouped
      // checkboxMulti, allOption lists, RadioOption booleans, documentStep /
      // filePair configs). To avoid lossily flattening those on a round-trip,
      // re-emit the editor's flat list ONLY when the user actually changed it;
      // otherwise keep the raw options verbatim.
      const rawIsRich = fr.options !== undefined && !Array.isArray(fr.options)
      const rawSimplified = simplifyOptions(fr.options)
      const editorOptions = Array.isArray(f.options) ? f.options : undefined
      // The synthetic placeholder normalizeField injects for an option-type
      // field that had NO options in the source — must not leak into the output.
      const isPlaceholderOnly = !!editorOptions && editorOptions.length === 1 &&
        editorOptions[0].label === 'Option 1' && editorOptions[0].value === 'option_1'
      if (rawIsRich) {
        // Raw options are a rich object the inspector can't model (grouped /
        // allOption / documentStep / filePair). Always keep them verbatim.
        out.options = fr.options
      } else if (editorModelsOptions(f.type) && editorOptions && !isPlaceholderOnly && !sameSimpleOptions(editorOptions, rawSimplified)) {
        // User edited a flat option list in the inspector → take their version.
        out.options = editorOptions
      } else if (fr.options !== undefined) {
        out.options = fr.options
      } else if (editorModelsOptions(f.type) && editorOptions && !isPlaceholderOnly) {
        out.options = editorOptions
      } else {
        delete out.options
      }
      return out
    })
    return { ...raw, stepIndex, title: st.title, fields }
  })
}

/** Save the working draft (does NOT publish — applicants still see the live version). */
export async function saveFormDraft(key: string, schema: HrFormSchema): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/form-definitions/${encodeURIComponent(key)}/draft`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ schema_json: serializeFormSchema(schema) }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

/** Publish the current draft as a new immutable version (becomes the live form). */
export async function publishForm(key: string, schema?: HrFormSchema): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/form-definitions/${encodeURIComponent(key)}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(schema ? { schema_json: serializeFormSchema(schema) } : {}),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Templates
export async function fetchHrAdminTemplates(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function uploadHrAdminTemplate(formData: FormData): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates`, { method: 'POST', body: formData })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function downloadHrAdminTemplate(id: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates/${id}/download`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}

export async function updateHrAdminTemplate(id: string, data: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteHrAdminTemplate(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates/${id}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function updateHrAdminTemplateFields(id: string, fieldPositions: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/templates/${id}/fields`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fieldPositions }) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Assignments
export async function fetchHrAdminAssignments(status?: string): Promise<any> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const resp = await fetch(`${API_BASE}/hr-admin/assignments?${params}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function createHrAdminAssignment(data: { template_id: number; assignee_employee_ids: number[]; priority?: string; due_date?: string; notes?: string }): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function fetchHrAdminAssignmentSigners(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments/${id}/signers`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateHrAdminAssignment(id: string, fields: { due_date?: string | null; priority?: string; notes?: string }): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function deleteHrAdminAssignment(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments/${id}`, { method: 'DELETE' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// PDF signing (native pdf-lib)
export async function fetchHrAdminAssignmentDetails(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments/${id}/details`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminAssignmentPdf(id: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr-admin/assignments/${id}/pdf`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}

export async function signHrAdminDocument(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/signatures/sign-native`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Caller-scoped "My Documents" — same shape as the admin endpoints but only returns/touches assignments owned by the authenticated user.
export async function fetchMyAssignments(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr/my-assignments`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function fetchMyAssignmentDetails(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr/my-assignments/${id}/details`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function fetchMyAssignmentPdf(id: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr/my-assignments/${id}/pdf`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}
export async function signMyDocument(data: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr/signatures/sign-native`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Employer signing
export async function fetchHrAdminPendingEmployer(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/pending-employer`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function fetchHrAdminOnboardingDocPdf(id: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr-admin/onboarding-docs/${id}/pdf`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}
export async function submitHrAdminEmployerSign(id: string, body: any): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/onboarding-docs/${id}/employer-sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// Profile detail (application data, documents, files)
export async function fetchHrAdminProfileDetail(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/profiles/${id}/detail`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function fetchHrAdminFileDownload(key: string): Promise<Blob> {
  const resp = await fetch(`${API_BASE}/hr-admin/files/download?key=${encodeURIComponent(key)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.blob()
}

// Onboarding
export async function fetchHrAdminOnboardingTokens(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/onboarding/tokens`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminOnboardingDocs(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/onboarding/documents`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchHrAdminDashboard(): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/dashboard`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Helper to get CRM userId from localStorage (for employer signature resolution)
function _getCrmUserId(): string {
  try { const p = JSON.parse(localStorage.getItem('prefsSettings') || '{}'); return p.userId ? String(p.userId) : '8' } catch { return '8' }
}

export async function fetchHrAdminEmployerSignature(): Promise<any> {
  const userId = _getCrmUserId()
  const resp = await fetch(`${API_BASE}/hr-admin/employer-signature?userId=${userId}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function saveHrAdminEmployerSignature(data: { signature?: string; initials?: string }): Promise<any> {
  const userId = _getCrmUserId()
  const resp = await fetch(`${API_BASE}/hr-admin/employer-signature`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...data, userId }) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteHrAdminEmployerSignature(type: 'signature' | 'initials'): Promise<any> {
  const userId = _getCrmUserId()
  const resp = await fetch(`${API_BASE}/hr-admin/employer-signature?type=${type}&userId=${userId}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateCrmUser(id: string, data: Record<string, any>): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/crm-users/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function deleteCrmUser(id: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/hr-admin/crm-users/${id}`, { method: 'DELETE' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Deactivate a CRM user (soft delete). Optionally reassign their open deals/leads
// to another active user. Admin-only — backend verifies role from CF Access JWT.
export async function deactivateCrmUser(id: string | number, reassignToUserId?: number | null): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/users/${id}/deactivate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reassignToUserId: reassignToUserId ?? null }),
  })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

export async function reactivateCrmUser(id: string | number): Promise<any> {
  const resp = await fetch(`${API_BASE}/crm/users/${id}/reactivate`, { method: 'POST' })
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error((e as any).error || `API error: ${resp.status}`) }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Activity notification preferences (email delivery)
// ---------------------------------------------------------------------------
// Delivery is email-only (bp-no-ms-v1); 'none' opts out.

export type EmailNotifChannel = 'email' | 'none'

export async function updateMyNotificationChannel(
  channel: EmailNotifChannel,
): Promise<{ success: boolean; channel: EmailNotifChannel }> {
  const resp = await fetch(`${API_BASE}/crm/me/notification-channel`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Per-user "new inbound email -> in-app bell" scope (migration 121).
export type EmailNotifyScope = 'off' | 'matched' | 'all'

export async function updateMyEmailNotifyScope(
  scope: EmailNotifyScope,
): Promise<{ success: boolean; scope: EmailNotifyScope }> {
  const resp = await fetch(`${API_BASE}/crm/me/email-notify-scope`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Per-user calling mode (migration 124): how inbound calls reach me and how
// outbound is placed. 'smart' resolves by capability at runtime.
export type CallingMode = 'smart' | 'softphone' | 'deskphone' | 'notify'

export async function updateMyCallingMode(
  mode: CallingMode,
): Promise<{ success: boolean; mode: CallingMode }> {
  const resp = await fetch(`${API_BASE}/crm/me/calling-mode`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// Per-user RingCentral OAuth (softphone "connect your own extension" — PR #62
// backend, migration 123). `authorize` is a full-page navigation (302 to RC),
// so it's a plain URL (/api/crm/rc/oauth/authorize), not a fetch.
export type RcOauthStatus = {
  configured: boolean
  connected: boolean
  needsReconnect: boolean
  extensionNumber: string | null
  connectedAt: string | null
}

export async function fetchRcOauthStatus(): Promise<RcOauthStatus> {
  const resp = await fetch(`${API_BASE}/crm/rc/oauth/status`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function disconnectRcOauth(): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/rc/oauth`, { method: 'DELETE', credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function sendNotifDeliveryTest(): Promise<{ success: boolean; error?: string; code?: string }> {
  const resp = await fetch(`${API_BASE}/crm/notifications/test`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
  // 500 returns a JSON error payload we want to show the user, so
  // don't throw — return whatever the server sent.
  return resp.json()
}

// ---------------------------------------------------------------------------
// Deal "moves to Won" automation chain
// ---------------------------------------------------------------------------
// Admin config + per-deal runs. See api/src/index.ts `runDealWonAutomation`.
// The chain fires from both PATCH /api/crm/deals/:id (generic) and the
// dedicated /stage endpoint — whichever path changes stage_id.

export type DealWonStepStatus = 'ok' | 'skipped' | 'error'
export type DealWonStep = {
  step: string
  status: DealWonStepStatus
  output?: Record<string, any>
  error?: string
}

export type DealAutomationRun = {
  id: number
  deal_id: number
  trigger_reason: string
  triggered_by_user_id: number | null
  started_at: string
  completed_at: string | null
  status: 'running' | 'success' | 'partial' | 'failed'
  steps: DealWonStep[]
}

export type DealWonSettings = {
  enabled: boolean
  stageIds: string[]
}

export async function fetchDealAutomationRuns(dealId: number, limit = 20): Promise<{ runs: DealAutomationRun[] }> {
  const resp = await fetch(
    `${API_BASE}/crm/deal-automation/runs?deal_id=${encodeURIComponent(String(dealId))}&limit=${limit}`,
    { credentials: 'include' },
  )
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function runDealAutomationManually(dealId: number): Promise<{
  runId: number
  status: 'running' | 'success' | 'partial' | 'failed'
  steps: DealWonStep[]
  skippedReason?: string
}> {
  const resp = await fetch(`${API_BASE}/crm/deal-automation/run`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deal_id: dealId }),
  })
  if (!resp.ok) {
    const t = await resp.text()
    throw new Error(`Run failed: ${resp.status} ${t}`)
  }
  return resp.json()
}

export async function fetchDealAutomationSettings(): Promise<{ resolved: DealWonSettings }> {
  const resp = await fetch(`${API_BASE}/crm/deal-automation/settings`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function updateDealAutomationSettings(
  updates: Partial<DealWonSettings>,
): Promise<{ success: boolean; resolved: DealWonSettings }> {
  const resp = await fetch(`${API_BASE}/crm/deal-automation/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  if (!resp.ok) {
    const t = await resp.text()
    throw new Error(`Update failed: ${resp.status} ${t}`)
  }
  return resp.json()
}

// ---------------------------------------------------------------------------
// Ticket lifecycle (spec: 2026-04-21-ticket-lifecycle-design.md)
// ---------------------------------------------------------------------------

export interface ContactSummary {
  open_deals: { count: number; total: number }
  active_projects: { count: number; total: number }
  lifetime_won: { total: number; crm_signed: number; legacy_paid: number }
  open_tickets: { count: number }
}

export async function fetchContactSummary(unifiedClientId: string): Promise<ContactSummary> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(unifiedClientId)}/summary`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchContactTickets(unifiedClientId: string): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(unifiedClientId)}/tickets`)
  if (!resp.ok) return []
  return resp.json()
}

export async function fetchDealTickets(dealId: number): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/tickets`)
  if (!resp.ok) return []
  return resp.json()
}

export async function fetchProjectTickets(projectId: string): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/crm/projects/${encodeURIComponent(projectId)}/tickets`)
  if (!resp.ok) return []
  return resp.json()
}

export async function fetchDealDeletePreview(dealId: number): Promise<{ linked_projects: number; linked_tickets: number }> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/delete-preview`)
  if (!resp.ok) return { linked_projects: 0, linked_tickets: 0 }
  return resp.json()
}

export async function fetchProjectDeletePreview(projectId: string): Promise<{ linked_tickets: number }> {
  const resp = await fetch(`${API_BASE}/crm/projects/${encodeURIComponent(projectId)}/delete-preview`)
  if (!resp.ok) return { linked_tickets: 0 }
  return resp.json()
}

export interface CreateTicketPayload {
  title: string
  description?: string | null
  unifiedClientId: string
  dealId?: number | null
  projectId?: string | null     // portal_projects.id is TEXT (e.g. "crm-701-02687b59")
  priority?: string
  category?: string | null
  dueDate?: string | null
  assignedTo?: number | null
}

// NOTE: createTicket(fields) and updateTicket(id, fields) already exist earlier
// in this file (lines ~1495 / ~1508) with a generic Record<string, unknown>
// signature. They POST/PATCH to the same /api/crm/tickets[/:id] endpoints, so
// the Task-9 callers can pass a CreateTicketPayload-shaped object into the
// existing createTicket without a new function. The CreateTicketPayload
// interface above documents the expected shape for ticket-lifecycle callers.

// ---------------------------------------------------------------------------
// Time tracking — /api/crm/time/*
// ---------------------------------------------------------------------------

export type TimeCategory = 'field' | 'admin' | 'drive-time' | 'pto' | 'overtime' | 'other'

export interface TimeEntry {
  id: number
  actor_type: 'employee' | 'subcontractor'
  actor_id: number
  actor_email: string | null
  actor_name: string | null
  deal_id: number | null
  project_id: number | null
  subtask_id: number | null
  clock_in_at: string
  clock_in_lat: number | null
  clock_in_lng: number | null
  clock_in_accuracy_m: number | null
  clock_out_at: string | null
  clock_out_lat: number | null
  clock_out_lng: number | null
  clock_out_accuracy_m: number | null
  duration_minutes: number | null
  notes: string | null
  payroll_category: TimeCategory
  ms_shift_id: string | null
  approved_by: number | null
  approved_at: string | null
  approved_notes: string | null
  billable: number
  created_at: string
  updated_at: string
  deal_title?: string | null
}

export interface LaborSummary {
  deal_id: number
  employeeHours: number
  subcontractorHours: number
  totalHours: number
  weekHours: number
  byUser: Array<{ actor_id: any; actor_type: string; actor_name: string; minutes: number; entries: number; hours: number }>
  byCategory: Array<{ category: string; minutes: number; entries: number; hours: number }>
  entryCount: number
  pendingApproval: number
}

export interface TimeListFilters {
  deal_id?: number | null
  project_id?: number | null
  actor_id?: number | null
  actor_type?: 'employee' | 'subcontractor' | null
  from?: string | null
  to?: string | null
  approved?: 'all' | 'pending' | 'approved'
  payroll_category?: TimeCategory | null
  limit?: number
  offset?: number
}

function timeFiltersToParams(f: TimeListFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.deal_id != null) p.set('deal_id', String(f.deal_id))
  if (f.project_id != null) p.set('project_id', String(f.project_id))
  if (f.actor_id != null) p.set('actor_id', String(f.actor_id))
  if (f.actor_type) p.set('actor_type', f.actor_type)
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  if (f.approved && f.approved !== 'all') p.set('approved', f.approved)
  if (f.payroll_category) p.set('payroll_category', f.payroll_category)
  if (f.limit != null) p.set('limit', String(f.limit))
  if (f.offset != null) p.set('offset', String(f.offset))
  return p
}

export async function clockInTime(body: {
  deal_id?: number | null
  project_id?: number | null
  payroll_category?: TimeCategory
  notes?: string | null
  lat?: number | null
  lng?: number | null
  accuracy_m?: number | null
}): Promise<{ id: number; clock_in_at: string }> {
  const resp = await fetch(`${API_BASE}/crm/time/clock-in`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  })
  if (!resp.ok) {
    const j: any = await resp.json().catch(() => ({}))
    throw Object.assign(new Error(j.error || `API error: ${resp.status}`), { openId: j.openId })
  }
  return resp.json()
}

export async function clockOutTime(id: number, body: {
  lat?: number | null
  lng?: number | null
  accuracy_m?: number | null
  notes?: string | null
} = {}): Promise<TimeEntry> {
  const resp = await fetch(`${API_BASE}/crm/time/${id}/clock-out`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({ error: null })) as any).error || `API error: ${resp.status}`)
  return resp.json()
}

export async function fetchOpenTimeEntry(): Promise<TimeEntry | null> {
  const resp = await fetch(`${API_BASE}/crm/time/open`, { credentials: 'include' })
  if (!resp.ok) return null
  const j: any = await resp.json()
  return j.entry || null
}

export async function listTimeEntries(filters: TimeListFilters = {}): Promise<{ entries: TimeEntry[]; total: number; limit: number; offset: number }> {
  const p = timeFiltersToParams(filters)
  const resp = await fetch(`${API_BASE}/crm/time?${p.toString()}`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function fetchDealLaborSummary(dealId: number): Promise<LaborSummary> {
  const resp = await fetch(`${API_BASE}/crm/deals/${dealId}/labor-summary`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function patchTimeEntry(id: number, fields: { notes?: string | null; payroll_category?: TimeCategory; billable?: boolean | number }): Promise<TimeEntry> {
  const resp = await fetch(`${API_BASE}/crm/time/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({ error: null })) as any).error || `API error: ${resp.status}`)
  return resp.json()
}

export async function approveTimeEntry(id: number, notes?: string | null): Promise<TimeEntry> {
  const resp = await fetch(`${API_BASE}/crm/time/${id}/approve`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ approved_notes: notes || null }),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({ error: null })) as any).error || `API error: ${resp.status}`)
  return resp.json()
}

export async function unapproveTimeEntry(id: number): Promise<TimeEntry> {
  const resp = await fetch(`${API_BASE}/crm/time/${id}/unapprove`, {
    method: 'PATCH',
    credentials: 'include',
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({ error: null })) as any).error || `API error: ${resp.status}`)
  return resp.json()
}

export async function deleteTimeEntry(id: number): Promise<void> {
  const resp = await fetch(`${API_BASE}/crm/time/${id}`, { method: 'DELETE', credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
}

export async function bulkApproveTime(ids: number[]): Promise<{ success: boolean; count: number }> {
  const resp = await fetch(`${API_BASE}/crm/time/bulk-approve`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({ error: null })) as any).error || `API error: ${resp.status}`)
  return resp.json()
}

export function timeExportCsvUrl(filters: TimeListFilters = {}): string {
  const p = timeFiltersToParams(filters)
  return `${API_BASE}/crm/time/export.csv?${p.toString()}`
}

// ---------------------------------------------------------------------------
// Cross-feature integration wrappers
// ---------------------------------------------------------------------------
//
// These endpoints are built by the parallel `integrations-api` team. At UI
// merge time they are expected to live under /api/crm/hr/..., /api/crm/calls/...,
// /api/crm/email/... and /api/crm/users/.... Each wrapper resolves to `null`
// on 404 so UI code can simply branch on "got a profile / didn't".
// ---------------------------------------------------------------------------

export type ActivityRollupEmployee = {
  employeeId: number
  fullName: string
  email: string | null
  title: string | null
  department: string | null
  managerName?: string | null
  directReportCount?: number | null
  orgNodeId?: number | null
}

export type ActivityRollupCrmUser = {
  id: number
  name: string
  email: string | null
}

export type ActivityRollupRcExtension = {
  extensionNumber: string
  phoneNumber: string | null
} | null

export type ActivityRollupDeal = {
  id: number
  title: string
  stage: string | null
  stageLabel: string | null
  stageColor?: string | null
  opportunity: number | null
  lastActivityAt: string | null
  pipelineName?: string | null
}

export type ActivityRollupLead = {
  id: number
  title: string
  statusLabel: string | null
  statusColor?: string | null
  lastActivityAt: string | null
}

export type ActivityRollupTimeEntry = {
  id: number
  dealId: number | null
  dealTitle: string | null
  category: string | null
  date: string
  hours: number
  approved: boolean | number
}

export type ActivityRollupActivity = {
  id: number
  providerType: string | null
  subject: string | null
  direction?: number | null
  linkedType?: 'deal' | 'lead' | 'contact' | 'project' | null
  linkedId?: number | null
  linkedLabel?: string | null
  createdAt: string
}

export type ActivityRollupStats = {
  hoursThisWeek: number
  hoursThisMonth: number
  dealsOwnedCount: number
  leadsAssignedCount: number
  openTasksCount: number
  callsLast7Days: number
}

export type ActivityRollup = {
  employee: ActivityRollupEmployee
  crmUser: ActivityRollupCrmUser | null
  rcExtension: ActivityRollupRcExtension
  orgNode: {
    managerName: string | null
    directReports: number
  } | null
  stats: ActivityRollupStats
  deals: ActivityRollupDeal[]
  estimatorDeals?: ActivityRollupDeal[]
  leads: ActivityRollupLead[]
  timeEntries: ActivityRollupTimeEntry[]
  activities: ActivityRollupActivity[]
}

export async function fetchHrEmployeeActivityRollup(employeeId: string | number): Promise<ActivityRollup | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/hr/employees/${encodeURIComponent(String(employeeId))}/activity-rollup`, {
      credentials: 'include',
    })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return await r.json() as ActivityRollup
  } catch (e) {
    console.error('fetchHrEmployeeActivityRollup:', e)
    return null
  }
}

export type LinkedContext = {
  type: 'deal' | 'lead' | 'contact' | 'project'
  id: number
  label: string
  hashUrl: string
} | null

export type CallLinkedContextResp = {
  call: { id: number; owner_type_id?: number | null; owner_id?: number | null } | null
  linked: LinkedContext
}

export async function fetchCallLinkedContext(activityId: number | string): Promise<CallLinkedContextResp | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/calls/${encodeURIComponent(String(activityId))}/linked-context`, {
      credentials: 'include',
    })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return await r.json() as CallLinkedContextResp
  } catch (e) {
    console.error('fetchCallLinkedContext:', e)
    return null
  }
}

export async function fetchEmailThreadLinkedEntity(conversationId: string): Promise<{ linked: LinkedContext } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(conversationId)}/linked-entity`, {
      credentials: 'include',
    })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return await r.json() as { linked: LinkedContext }
  } catch (e) {
    console.error('fetchEmailThreadLinkedEntity:', e)
    return null
  }
}

export type HrEmployeeRcExtension = {
  extensionNumber: string
  phoneNumber: string | null
} | null

export async function fetchHrEmployeeRcExtension(employeeId: string | number): Promise<HrEmployeeRcExtension> {
  try {
    const r = await fetch(`${API_BASE}/crm/hr/employees/${encodeURIComponent(String(employeeId))}/rc-extension`, {
      credentials: 'include',
    })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const j = await r.json()
    // API returns `null` body when no mapping; guard for that shape too.
    if (!j || !j.extensionNumber) return null
    return { extensionNumber: String(j.extensionNumber), phoneNumber: j.phoneNumber ?? null }
  } catch (e) {
    console.error('fetchHrEmployeeRcExtension:', e)
    return null
  }
}

export type UserHrProfile = {
  employeeId: number
  fullName: string
  email: string | null
  department: string | null
  position: string | null
  photoUrl: string | null
  phone?: string | null
} | null

export async function fetchUserHrProfile(userId: number | string): Promise<UserHrProfile> {
  try {
    const r = await fetch(`${API_BASE}/crm/users/${encodeURIComponent(String(userId))}/hr-profile`, {
      credentials: 'include',
    })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return await r.json() as UserHrProfile
  } catch (e) {
    console.error('fetchUserHrProfile:', e)
    return null
  }
}

// ---------------------------------------------------------------------------
// wave-2 client helpers
// ---------------------------------------------------------------------------

// Counts powering the Timesheets sidebar chip + view header. `myPendingCount`
// is what the chip shows; admins also receive `totalPendingIfAdmin` so they
// can decide if they want to dive into the pile beyond their own deals.
export type TimeCountsByOwner = {
  myPendingCount: number
  myApprovedTodayCount: number
  totalPendingIfAdmin?: number
}

export async function fetchTimeCountsByOwner(): Promise<TimeCountsByOwner | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/time/counts-by-owner`, { credentials: 'include' })
    if (r.status === 401 || r.status === 404) return null
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return await r.json() as TimeCountsByOwner
  } catch (e) {
    console.error('fetchTimeCountsByOwner:', e)
    return null
  }
}

// Lightweight contact-create used by the inline form on the Calls deep-link
// landing path. Returns the new client's unified_client_id on success.
// Body shape matches POST /api/crm/clients (camelCase keys).
export async function createContactFromCall(input: {
  name: string
  phone: string
  email?: string | null
}): Promise<{ unified_client_id: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/clients`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        preferredName: input.name,
        preferredPhone: input.phone,
        preferredEmail: input.email || null,
        clientType: 'person',
      }),
    })
    if (!r.ok) return null
    return await r.json() as { unified_client_id: string }
  } catch (e) {
    console.error('createContactFromCall:', e)
    return null
  }
}

// ---------------------------------------------------------------------------
// Email reader UI enhancements (Team: email-reader-ui)
// Multi-entity thread links, snooze, AI summarize, follow-up reminders,
// tracking stats. Each wrapper soft-fails (null) when the backend endpoint
// isn't live yet so parallel backend dev doesn't block the UI.
// ---------------------------------------------------------------------------

export type EmailThreadEntityType = 'deal' | 'lead' | 'contact' | 'project' | 'client'

export type EmailThreadLink = {
  entity_type: EmailThreadEntityType
  entity_id: number | string
  is_primary: 0 | 1 | boolean
  source?: string | null
  confidence?: number | null
  linked_at?: string | null
  label?: string | null
}

export type EmailThreadLinksResponse = {
  links: EmailThreadLink[]
  primary_entity?: { type: EmailThreadEntityType; id: number | string } | null
}

// GET current links on a thread. Returns null on 404 so the caller can hide
// the feature when the backend endpoint hasn't deployed.
export async function fetchEmailThreadLinks(threadId: string): Promise<EmailThreadLinksResponse | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/links`, { credentials: 'include' })
    if (r.status === 404) return null
    if (!r.ok) return null
    const data = await r.json() as Partial<EmailThreadLinksResponse>
    return { links: data.links || [], primary_entity: data.primary_entity ?? null }
  } catch (e) {
    console.error('fetchEmailThreadLinks:', e)
    return null
  }
}

export async function addEmailThreadLink(threadId: string, body: {
  entity_type: EmailThreadEntityType
  entity_id: number | string
  make_primary?: boolean
}): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/links`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    return { ok: true }
  } catch (e: any) {
    console.error('addEmailThreadLink:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

export async function removeEmailThreadLink(threadId: string, entityType: EmailThreadEntityType, entityId: number | string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/links/${encodeURIComponent(entityType)}/${encodeURIComponent(String(entityId))}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    return { ok: true }
  } catch (e: any) {
    console.error('removeEmailThreadLink:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

// GET email threads linked to a given entity. Returns null when the endpoint
// hasn't shipped yet — callers should render a quiet empty state on null.
export type EmailThreadSummaryLite = {
  id: string
  subject: string
  preview?: string
  participants?: Array<{ name?: string; email: string }>
  lastMessageAt: string
  messageCount: number
  isRead?: boolean
}
export async function fetchEmailThreadsByEntity(entityType: EmailThreadEntityType, entityId: number | string): Promise<EmailThreadSummaryLite[] | null> {
  try {
    const qs = new URLSearchParams({ type: entityType, id: String(entityId) })
    const r = await fetch(`${API_BASE}/crm/email/threads/by-entity?${qs}`, { credentials: 'include' })
    if (r.status === 404) return null
    if (!r.ok) return null
    const data = await r.json() as { threads?: EmailThreadSummaryLite[] }
    return Array.isArray(data.threads) ? data.threads : []
  } catch (e) {
    console.error('fetchEmailThreadsByEntity:', e)
    return null
  }
}

// --- Snooze ---------------------------------------------------------------

export type SnoozedThread = {
  id: string
  subject?: string
  snoozed_until: string
  [key: string]: unknown
}

export async function snoozeEmailThread(threadId: string, untilIso: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/snooze`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ until: untilIso }),
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    return { ok: true }
  } catch (e: any) {
    console.error('snoozeEmailThread:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

export async function unsnoozeEmailThread(threadId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/snooze`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    return { ok: true }
  } catch (e: any) {
    console.error('unsnoozeEmailThread:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

export async function fetchSnoozedThreads(): Promise<SnoozedThread[] | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads?folder=snoozed&pageSize=100`, { credentials: 'include' })
    if (r.status === 404) return null
    if (!r.ok) return null
    const data = await r.json() as { threads?: SnoozedThread[] }
    const rows = Array.isArray(data.threads) ? data.threads : []
    return rows.map((t: any) => ({ ...t, snoozed_until: t.snoozed_until ?? t.snoozedUntil ?? null }))
  } catch (e) {
    console.error('fetchSnoozedThreads:', e)
    return null
  }
}

// --- AI Summarize ---------------------------------------------------------

export type EmailThreadSummary = {
  summary: string
  key_points: string[]
  action_items: string[]
}

export async function summarizeEmailThread(conversationId: string): Promise<{
  ok: boolean
  data?: EmailThreadSummary
  notConfigured?: boolean
  error?: string
}> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/ai/summarize-thread`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
    })
    if (r.status === 503) return { ok: false, notConfigured: true }
    if (r.status === 404) return { ok: false, notConfigured: true }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    const data = await r.json() as Partial<EmailThreadSummary>
    return {
      ok: true,
      data: {
        summary: data.summary || '',
        key_points: Array.isArray(data.key_points) ? data.key_points : [],
        action_items: Array.isArray(data.action_items) ? data.action_items : [],
      },
    }
  } catch (e: any) {
    console.error('summarizeEmailThread:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

// --- Follow-up Reminder ---------------------------------------------------

export type EmailReminderCondition = 'always' | 'if_no_reply'

export async function createEmailThreadReminder(threadId: string, body: {
  remind_at: string
  note?: string
  condition?: EmailReminderCondition
}): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/reminders`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      return { ok: false, error: d.error || `HTTP ${r.status}` }
    }
    return { ok: true }
  } catch (e: any) {
    console.error('createEmailThreadReminder:', e)
    return { ok: false, error: e?.message || 'Request failed' }
  }
}

// --- Tracking Stats -------------------------------------------------------

export type EmailTrackingRow = {
  internet_message_id: string
  subject?: string | null
  sent_at?: string | null
  open_count: number
  first_opened_at?: string | null
  last_opened_at?: string | null
  click_count: number
}

export async function fetchEmailThreadTracking(threadId: string): Promise<EmailTrackingRow[] | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/threads/${encodeURIComponent(threadId)}/tracking`, { credentials: 'include' })
    if (r.status === 404) return null
    if (!r.ok) return null
    const data = await r.json()
    if (Array.isArray(data)) return data as EmailTrackingRow[]
    if (Array.isArray((data as any)?.tracking)) return (data as any).tracking as EmailTrackingRow[]
    return []
  } catch (e) {
    console.error('fetchEmailThreadTracking:', e)
    return null
  }
}

// --- Entity search (for multi-link popover autocomplete) ------------------
// Lightweight unified search used by the inbox "Linked to" popover. Queries
// the existing single-entity endpoints in parallel and flattens the results.
export type EntitySearchHit = {
  type: EmailThreadEntityType
  id: number | string
  label: string
  sub?: string
}
export async function searchEntitiesForLinking(query: string, limit = 5): Promise<EntitySearchHit[]> {
  const q = query.trim()
  if (q.length < 2) return []
  const hits: EntitySearchHit[] = []
  // Fire all three in parallel; soft-fail each independently.
  const jobs: Promise<unknown>[] = []

  jobs.push(
    fetch(`${API_BASE}/crm/deals?search=${encodeURIComponent(q)}&pageSize=${limit}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        const arr = Array.isArray(d?.deals) ? d.deals : Array.isArray(d?.data) ? d.data : []
        for (const row of arr.slice(0, limit)) {
          hits.push({ type: 'deal', id: row.id ?? row.deal_id, label: row.title || `Deal #${row.id}`, sub: row.customer_name || row.client_name || undefined })
        }
      }).catch(() => {})
  )
  jobs.push(
    fetch(`${API_BASE}/crm/leads?search=${encodeURIComponent(q)}&pageSize=${limit}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        const arr = Array.isArray(d?.leads) ? d.leads : Array.isArray(d?.data) ? d.data : []
        for (const row of arr.slice(0, limit)) {
          hits.push({ type: 'lead', id: row.id ?? row.lead_id, label: row.title || row.customer_name || `Lead #${row.id}`, sub: row.email || undefined })
        }
      }).catch(() => {})
  )
  jobs.push(
    fetch(`${API_BASE}/crm/clients?search=${encodeURIComponent(q)}&pageSize=${limit}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        const arr = Array.isArray(d?.data) ? d.data : Array.isArray(d?.clients) ? d.clients : []
        for (const row of arr.slice(0, limit)) {
          const id = row.unified_client_id || row.id
          hits.push({ type: 'contact', id, label: row.preferred_name || row.name || `Contact ${id}`, sub: row.preferred_email || undefined })
        }
      }).catch(() => {})
  )
  await Promise.all(jobs)
  return hits
}


// ===========================================================================
// Email compose-side features (Team 2 backend — graceful 404 fallback).
// Features: signatures, templates, scheduled send, AI draft reply, BCC token.
// Every function returns a sentinel ({ unavailable: true } or null / []) on
// 404/503 so the UI can hide the feature silently rather than throw.
// ===========================================================================

export type EmailSignature = {
  id: number
  name: string
  html: string
  is_default?: 0 | 1 | boolean
  created_at?: string
  updated_at?: string
}

// Naming note: the legacy sequence-side `EmailTemplate` (id + body_html +
// template_type + stats_json) already exists above. The compose-side feature
// needs a narrower shape with { category, is_shared, owner_email } — keep it
// under CrmEmailTemplate to avoid colliding with that older type.
export type CrmEmailTemplate = {
  id: number
  name: string
  subject: string
  body_html: string
  category?: string | null
  is_shared?: 0 | 1 | boolean
  owner_id?: number | null
  owner_email?: string | null
  created_at?: string
  updated_at?: string
}

export type ScheduledEmail = {
  id: number
  to: string[] | string
  cc?: string[] | string
  bcc?: string[] | string
  subject: string
  body_html?: string
  send_at: string
  status?: 'pending' | 'sent' | 'failed' | 'cancelled' | string
  related_entity?: { type?: string; id?: string | number } | null
  created_at?: string
  sent_at?: string | null
  error?: string | null
}

// ----- Signatures ---------------------------------------------------------

export async function fetchEmailSignatures(): Promise<{ signatures: EmailSignature[]; unavailable?: boolean }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/signatures`, { credentials: 'include' })
    if (r.status === 404) return { signatures: [], unavailable: true }
    if (!r.ok) throw new Error(`signatures ${r.status}`)
    const data = await r.json()
    return { signatures: Array.isArray(data?.signatures) ? data.signatures : [] }
  } catch (e) {
    console.warn('fetchEmailSignatures:', e)
    return { signatures: [], unavailable: true }
  }
}

export async function fetchDefaultEmailSignature(): Promise<EmailSignature | null> {
  const { signatures, unavailable } = await fetchEmailSignatures()
  if (unavailable) return null
  return signatures.find(s => !!s.is_default) || null
}

export async function createEmailSignature(input: { name: string; html: string; is_default?: boolean }): Promise<EmailSignature | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/signatures`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) return null
    const data = await r.json()
    return (data?.signature ?? data ?? null) as EmailSignature | null
  } catch (e) { console.warn('createEmailSignature:', e); return null }
}

export async function updateEmailSignature(id: number, input: Partial<{ name: string; html: string; is_default: boolean }>): Promise<EmailSignature | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/signatures/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) return null
    const data = await r.json()
    return (data?.signature ?? data ?? null) as EmailSignature | null
  } catch (e) { console.warn('updateEmailSignature:', e); return null }
}

export async function deleteEmailSignature(id: number): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/signatures/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return r.ok
  } catch (e) { console.warn('deleteEmailSignature:', e); return false }
}

// ----- System email signature (per-user, single signature) ---------------
// Distinct from EmailSignature above (compose-time multi-sig). This one is
// auto-appended by the worker when it sends programmatic emails on the
// user's behalf — send-application-link, public-proposal "send to
// homeowner", etc. See migration 101 + the appendUserSignature helper.

export type UserEmailSignature = {
  user_id: number
  html: string | null
  plain: string | null
  image_r2_key: string | null
  updated_at: string
}

export async function fetchMyEmailSignature(): Promise<UserEmailSignature | null> {
  try {
    const r = await fetch(`${API_BASE}/me/email-signature`, { credentials: 'include' })
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`me/email-signature ${r.status}`)
    const data = await r.json()
    return (data?.signature as UserEmailSignature | null) || null
  } catch (e) {
    console.warn('fetchMyEmailSignature:', e)
    return null
  }
}

export async function saveMyEmailSignature(input: {
  html?: string | null
  plain?: string | null
  image_r2_key?: string | null
}): Promise<{ success: boolean; updated_at?: string }> {
  const resp = await fetch(`${API_BASE}/me/email-signature`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return jsonOrThrow<{ success: boolean; updated_at?: string }>(resp)
}

export async function deleteMyEmailSignature(): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/me/email-signature`, {
    method: 'DELETE',
    credentials: 'include',
  })
  return jsonOrThrow<{ success: boolean }>(resp)
}

export async function uploadMyEmailSignatureImage(file: File): Promise<{ key: string; filename: string }> {
  const resp = await fetch(`${API_BASE}/me/email-signature/image`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type || 'image/png' },
    body: file,
  })
  return jsonOrThrow<{ key: string; filename: string }>(resp)
}

// ----- Templates ----------------------------------------------------------

// Naming note: there's an older `fetchEmailTemplates` above (marketing-email
// feature, hits /crm/email-templates). These compose-side calls target
// /crm/email/templates and are prefixed `crmEmail*` to avoid the collision.
export async function fetchCrmEmailTemplates(category?: string): Promise<{ templates: CrmEmailTemplate[]; unavailable?: boolean }> {
  try {
    const url = category ? `${API_BASE}/crm/email/templates?category=${encodeURIComponent(category)}` : `${API_BASE}/crm/email/templates`
    const r = await fetch(url, { credentials: 'include' })
    if (r.status === 404) return { templates: [], unavailable: true }
    if (!r.ok) throw new Error(`templates ${r.status}`)
    const data = await r.json()
    return { templates: Array.isArray(data?.templates) ? data.templates : [] }
  } catch (e) {
    console.warn('fetchCrmEmailTemplates:', e)
    return { templates: [], unavailable: true }
  }
}

export async function renderCrmEmailTemplate(
  id: number,
  ctx: { dealId?: string | number; leadId?: string | number; contactId?: string | number },
): Promise<{ subject: string; body_html: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/templates/${id}/render`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ctx),
    })
    if (!r.ok) return null
    const data = await r.json()
    return { subject: String(data?.subject || ''), body_html: String(data?.body_html || '') }
  } catch (e) { console.warn('renderCrmEmailTemplate:', e); return null }
}

export async function createCrmEmailTemplate(input: { name: string; subject: string; body_html: string; category?: string; is_shared?: boolean }): Promise<CrmEmailTemplate | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/templates`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) return null
    const data = await r.json()
    return (data?.template ?? data ?? null) as CrmEmailTemplate | null
  } catch (e) { console.warn('createCrmEmailTemplate:', e); return null }
}

export async function updateCrmEmailTemplate(id: number, input: Partial<{ name: string; subject: string; body_html: string; category: string; is_shared: boolean }>): Promise<CrmEmailTemplate | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/templates/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) return null
    const data = await r.json()
    return (data?.template ?? data ?? null) as CrmEmailTemplate | null
  } catch (e) { console.warn('updateCrmEmailTemplate:', e); return null }
}

export async function deleteCrmEmailTemplate(id: number): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/templates/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return r.ok
  } catch (e) { console.warn('deleteCrmEmailTemplate:', e); return false }
}

// ----- Schedule send ------------------------------------------------------

export async function scheduleSendEmail(input: {
  message: { to: string[]; cc?: string[]; bcc?: string[]; subject: string; body_html: string }
  send_at: string
  related_entity?: { type?: string; id?: string | number }
}): Promise<{ id: number; send_at: string } | { error: string; status?: number } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/send-later`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) return { error: data?.error || `schedule failed (${r.status})`, status: r.status }
    return { id: data?.id, send_at: data?.send_at || input.send_at }
  } catch (e) {
    console.warn('scheduleSendEmail:', e)
    return null
  }
}

export async function fetchScheduledEmails(): Promise<{ scheduled: ScheduledEmail[]; unavailable?: boolean }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/send-later`, { credentials: 'include' })
    if (r.status === 404) return { scheduled: [], unavailable: true }
    if (!r.ok) throw new Error(`scheduled ${r.status}`)
    const data = await r.json()
    const list = Array.isArray(data?.scheduled) ? data.scheduled
      : Array.isArray(data?.items) ? data.items
      : Array.isArray(data) ? data : []
    return { scheduled: list }
  } catch (e) {
    console.warn('fetchScheduledEmails:', e)
    return { scheduled: [], unavailable: true }
  }
}

export async function cancelScheduledEmail(id: number): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/send-later/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return r.ok
  } catch (e) { console.warn('cancelScheduledEmail:', e); return false }
}

// ----- AI draft reply -----------------------------------------------------

export async function generateAiDraftReply(input: {
  conversation_id: string
  prompt?: string
}): Promise<{ body_html: string } | { error: 'unavailable' | 'rate_limited' | 'failed'; status?: number }> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/ai/draft-reply`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (r.status === 503 || r.status === 404) return { error: 'unavailable', status: r.status }
    if (r.status === 429) return { error: 'rate_limited', status: 429 }
    if (!r.ok) return { error: 'failed', status: r.status }
    const data = await r.json()
    return { body_html: String(data?.body_html || '') }
  } catch (e) {
    console.warn('generateAiDraftReply:', e)
    return { error: 'failed' }
  }
}

// ----- BCC token ----------------------------------------------------------

export async function fetchBccToken(): Promise<{ address: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/bcc-token`, { credentials: 'include' })
    if (!r.ok) return null
    const data = await r.json()
    return { address: String(data?.address || '') }
  } catch (e) { console.warn('fetchBccToken:', e); return null }
}

export async function rotateBccToken(): Promise<{ address: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/crm/email/bcc-token/rotate`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!r.ok) return null
    const data = await r.json()
    return { address: String(data?.address || '') }
  } catch (e) { console.warn('rotateBccToken:', e); return null }
}

// ---------------------------------------------------------------------------
// Permission capabilities — mirrors Carly's 4/28 matrix. The backend at
// /api/me/capabilities is authoritative; this client cache is purely for
// UX (disable buttons that would 403). Keep the union in sync with
// Capability in api/src/index.ts and the rows in
// src/views/preferences/admin/Permissions.tsx.
// ---------------------------------------------------------------------------

export type Capability =
  | 'see_unassigned_leads'
  | 'see_cross_territory_deals'
  | 'reassign_leads_deals'
  | 'export_contacts'
  | 'merge_contacts'
  | 'archive_contacts'
  | 'assign_subcontractors'
  | 'edit_accounting_fields'
  | 'edit_non_accounting_fields'
  | 'approve_margin_exceptions'
  | 'set_actual_schedule'
  | 'set_estimated_start'
  | 'access_hr_center'
  | 'access_settings'
  | 'manage_company_holidays'
  | 'delete_deal'
  | 'delete_lead'
  | 'delete_contact'
  | 'delete_organization'
  | 'delete_project'
  | 'calendar_add_event'
  | 'calendar_edit_event'
  | 'calendar_delete_event'
  | 'view_all_calls'

export type MyCapabilitiesResponse = {
  role: string
  capabilities: Record<Capability, boolean>
  // Effective hidden nav keys for this role (e.g. 'accounting',
  // 'crm.tickets'). Always [] for super_admin. Optional so a stale worker
  // that predates the field doesn't break callers.
  hiddenNav?: string[]
  // Per-entity record scope: 'everyone' (default) or 'mine' (lists only
  // return records owned by / assigned to the user — enforced server-side).
  // Keys: contacts, companies, leads, deals, projects, tickets, tasks.
  recordScope?: Record<string, 'everyone' | 'mine'>
}

/** Fetch the authenticated user's role + per-capability allow map.
 *  Returns null on 401 / network error so callers can fall back to a
 *  deny-all map without breaking the UI. The /api/me/capabilities
 *  endpoint sets Cache-Control: no-store, but we also pass cache:
 *  'no-store' here to be defensive against any browser-side cache. */
export async function fetchMyCapabilities(): Promise<MyCapabilitiesResponse | null> {
  try {
    const r = await fetch(`${API_BASE}/me/capabilities`, {
      credentials: 'include',
      cache: 'no-store',
    })
    if (!r.ok) return null
    const data = await r.json() as MyCapabilitiesResponse
    return data
  } catch (e) { console.warn('fetchMyCapabilities:', e); return null }
}

// ── Roles & Permissions editor (Settings → Roles & Permissions) ──────
// Grids keyed capability→role→allowed and navKey→role→visible. The four
// editable roles only; super_admin is implicit (always-all) server-side.

export type PermissionsMatrixResponse = {
  roles: string[]
  capabilities: Record<string, Record<string, boolean>>
  nav: Record<string, Record<string, boolean>>
  // entity → role → 'everyone' | 'mine'. Optional so a stale worker that
  // predates record scope doesn't break the editor.
  scope?: Record<string, Record<string, string>>
}

export async function fetchPermissionsMatrix(): Promise<PermissionsMatrixResponse> {
  const r = await fetch(`${API_BASE}/crm/permissions/matrix`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (!r.ok) throw new Error(`fetchPermissionsMatrix ${r.status}`)
  return await r.json() as PermissionsMatrixResponse
}

export async function savePermissionsMatrix(payload: {
  capabilities?: Array<{ role: string; capability: string; allowed: boolean }>
  nav?: Array<{ role: string; navKey: string; visible: boolean }>
  scope?: Array<{ role: string; entity: string; scope: 'everyone' | 'mine' }>
}): Promise<{ success: boolean; applied: number }> {
  const r = await fetch(`${API_BASE}/crm/permissions/matrix`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error || `savePermissionsMatrix ${r.status}`)
  }
  return await r.json() as { success: boolean; applied: number }
}

// ── Per-USER permission overrides (Settings → Users → Permissions drawer) ──
// Layered on top of the role matrix. super_admin > user override > role.

export type UserPermissionsResponse = {
  userId: number
  role: string
  /** The user's ROLE default capability map (no per-user overlay) — shown
   *  beside each override control as the inherited value. */
  roleDefault: Record<string, boolean>
  /** Per-user capability overrides: capability → true(allow)/false(deny).
   *  Absent key = inherit the role default. */
  overrides: Record<string, boolean>
  /** Nav keys hidden for the user's ROLE by default (the inherited baseline). */
  navDefaultHidden: string[]
  /** Per-user nav overrides: navKey → true(show)/false(hide). Absent = inherit. */
  navOverrides: Record<string, boolean>
}

export async function fetchUserPermissions(userId: number): Promise<UserPermissionsResponse> {
  const r = await fetch(`${API_BASE}/crm/permissions/user/${userId}`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error || `fetchUserPermissions ${r.status}`)
  }
  return await r.json() as UserPermissionsResponse
}

export async function saveUserPermissions(userId: number, payload: {
  capChanges?: Array<{ capability: string; value: 'allow' | 'deny' | 'inherit' }>
  navChanges?: Array<{ nav_key: string; value: 'show' | 'hide' | 'inherit' }>
}): Promise<{ success: boolean; applied: number }> {
  const r = await fetch(`${API_BASE}/crm/permissions/user/${userId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error || `saveUserPermissions ${r.status}`)
  }
  return await r.json() as { success: boolean; applied: number }
}

// ── Time Clock (shift attendance — separate from deal-based time_entries) ──

export type TimeClockKind = 'work' | 'lunch' | 'break'
export type TimeClockAction = 'in' | 'out' | 'lunch' | 'break' | 'resume'
export type TimeClockOpenSegment = { id: number; clock_in: string; in_flag?: string | null; kind: TimeClockKind }
export type TimeClockStatus = {
  open: TimeClockOpenSegment | null
  todayMinutes: number
}
export type TimeClockPunch = {
  id: number; user_id: number; clock_in: string; clock_out: string | null
  in_flag: string | null; out_flag: string | null; note: string | null
  kind: TimeClockKind
  edited_by: number | null; edited_at: string | null; user_name?: string | null
  // Mandatory geolocation per shift punch (migration 183). In-coords stamp the
  // clock-in event; out-coords stamp the clock-out event. Raw lat/lng + accuracy
  // (meters); no reverse-geocode. Old rows (pre-183) are null.
  clock_in_lat?: number | null; clock_in_lng?: number | null; clock_in_accuracy_m?: number | null
  clock_out_lat?: number | null; clock_out_lng?: number | null; clock_out_accuracy_m?: number | null
}
export type TimeClockScheduleRow = { user_id: number; weekday: number; start_time: string | null; end_time: string | null }

/** Coords captured for a shift-clock punch. Required on every event. */
export type TimeClockGeo = { lat: number; lng: number; accuracy_m: number | null }

export async function tcPunch(action: TimeClockAction, geo: TimeClockGeo): Promise<{ status: 'clocked_in' | 'clocked_out' | 'on_lunch' | 'on_break'; open: TimeClockOpenSegment | null }> {
  // JSON body always — the global CSRF guard 415s POSTs that don't carry a
  // JSON or multipart Content-Type. Geolocation is MANDATORY (server 400s
  // without valid coords); the caller blocks on the browser geo prompt first.
  const r = await fetch(`${API_BASE}/crm/time-clock/punch`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, lat: geo.lat, lng: geo.lng, accuracy_m: geo.accuracy_m }),
  })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcPunch ${r.status}`)
  return await r.json()
}

export async function tcStatus(): Promise<TimeClockStatus> {
  const r = await fetch(`${API_BASE}/crm/time-clock/status`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) throw new Error(`tcStatus ${r.status}`)
  return await r.json()
}

export async function tcOverview(fromIso: string, toIso: string): Promise<{ punches: TimeClockPunch[]; schedules: TimeClockScheduleRow[] }> {
  const r = await fetch(`${API_BASE}/crm/time-clock/overview?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) throw new Error(`tcOverview ${r.status}`)
  return await r.json()
}

export async function tcUpdateEntry(id: number, body: { clockIn?: string; clockOut?: string | null; note?: string | null }): Promise<void> {
  const r = await fetch(`${API_BASE}/crm/time-clock/entries/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcUpdateEntry ${r.status}`)
}

export async function tcDeleteEntry(id: number): Promise<void> {
  const r = await fetch(`${API_BASE}/crm/time-clock/entries/${id}`, { method: 'DELETE', credentials: 'include' })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcDeleteEntry ${r.status}`)
}

export async function tcSaveSchedule(userId: number, days: Array<{ weekday: number; start: string | null; end: string | null }> | null): Promise<void> {
  const r = await fetch(`${API_BASE}/crm/time-clock/schedules/${userId}`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ days }),
  })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcSaveSchedule ${r.status}`)
}

// ── Time Clock: My Time (own entries) + correction requests ──

export type TimeClockCorrectionRequest = {
  id: number; punch_id: number | null
  requested_clock_in: string; requested_clock_out: string | null
  reason: string; status: 'pending' | 'approved' | 'denied'
  reviewed_at: string | null; review_note: string | null; created_at: string
  user_id?: number; user_name?: string | null
  punch_clock_in?: string | null; punch_clock_out?: string | null; punch_kind?: string | null
}

export async function tcMyEntries(fromIso: string, toIso: string): Promise<{ punches: TimeClockPunch[] }> {
  const r = await fetch(`${API_BASE}/crm/time-clock/my-entries?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) throw new Error(`tcMyEntries ${r.status}`)
  return await r.json()
}

export async function tcMyCorrections(): Promise<{ requests: TimeClockCorrectionRequest[] }> {
  const r = await fetch(`${API_BASE}/crm/time-clock/my-corrections`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) throw new Error(`tcMyCorrections ${r.status}`)
  return await r.json()
}

export async function tcCreateCorrection(body: { punchId?: number | null; clockIn: string; clockOut?: string | null; reason: string }): Promise<{ success: boolean; id: number }> {
  const r = await fetch(`${API_BASE}/crm/time-clock/corrections`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcCreateCorrection ${r.status}`)
  return await r.json()
}

export async function tcListCorrections(status: 'pending' | 'approved' | 'denied' | 'all' = 'pending'): Promise<{ requests: TimeClockCorrectionRequest[] }> {
  const r = await fetch(`${API_BASE}/crm/time-clock/corrections?status=${status}`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) throw new Error(`tcListCorrections ${r.status}`)
  return await r.json()
}

export async function tcReviewCorrection(id: number, approve: boolean, note?: string): Promise<void> {
  const r = await fetch(`${API_BASE}/crm/time-clock/corrections/${id}/review`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approve, note: note || null }),
  })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || `tcReviewCorrection ${r.status}`)
}

// ---------------------------------------------------------------------------
// Financials Hub
// ---------------------------------------------------------------------------

import type { FinancialDoc, FinancialGroup, FinancialProjectId } from './views/financials/types'

export async function listFinancialsByProject(
  subProjectId: FinancialProjectId,
  opts: { sort?: string; order?: string; limit?: number } = {},
): Promise<{ items: FinancialDoc[]; warnings: { source: string; message: string }[] }> {
  const q = new URLSearchParams(opts as Record<string, string>).toString()
  const r = await fetch(`${API_BASE}/financials/project/${encodeURIComponent(String(subProjectId))}${q ? '?' + q : ''}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`listFinancialsByProject ${r.status}`)
  return r.json()
}

export async function listFinancialsByClient(
  clientId: string,
  opts: { limit?: number } = {},
): Promise<{ groups: FinancialGroup[]; warnings: { source: string; message: string }[] }> {
  const q = new URLSearchParams(opts as Record<string, string>).toString()
  const r = await fetch(`${API_BASE}/financials/by-client/${encodeURIComponent(clientId)}${q ? '?' + q : ''}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`listFinancialsByClient ${r.status}`)
  return r.json()
}

export async function listFinancialsByDeal(dealId: number): Promise<{ items: FinancialDoc[]; warnings: { source: string; message: string }[] }> {
  const r = await fetch(`${API_BASE}/financials/by-deal/${dealId}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`listFinancialsByDeal ${r.status}`)
  return r.json()
}

export async function uploadFinancialDoc(form: FormData): Promise<{ id: number; source: string; doc_type: string }> {
  const r = await fetch(`${API_BASE}/financials/upload`, { method: 'POST', body: form, credentials: 'include' })
  if (!r.ok) throw new Error(`uploadFinancialDoc ${r.status}`)
  return r.json()
}

export async function reassignFinancialProject(
  source: string,
  id: number,
  sub_project_id: FinancialProjectId,
): Promise<{ ok: true }> {
  const r = await fetch(`${API_BASE}/financials/${source}/${id}/reassign`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sub_project_id }),
  })
  if (!r.ok) throw new Error(`reassignFinancialProject ${r.status}`)
  return r.json()
}

export async function listSubAdminProjects(opts: { limit?: number; search?: string } = {}): Promise<Array<{ id: FinancialProjectId; name: string; client_id?: number | string | null }>> {
  const params = new URLSearchParams()
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.search) params.set('search', opts.search)
  const qs = params.toString()
  const r = await fetch(`${API_BASE}/sub-admin/projects${qs ? '?' + qs : ''}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`listSubAdminProjects ${r.status}`)
  const j: any = await r.json()
  return j.projects ?? j.items ?? j.data ?? []
}

// ═══════════════════════════════════════════════════════════════════════════
// Error tickets (Preferences → Administration → Error Center; admin-only)
//
// Crash/console-error tickets captured by src/reliability.ts, deduped by
// signature in D1 `error_tickets`, forwarded to the upstream AI solver, and
// reviewed here. Endpoints are requireAdmin server-side. marker: error-center-tab
// ═══════════════════════════════════════════════════════════════════════════

/** error_tickets.upstream_status CHECK values (contract). */
export type ErrorTicketStatus = 'pending' | 'solved' | 'needs_human' | 'failed' | 'disabled'

/** One row of D1 `error_tickets`. The list endpoint may omit the heavy
 *  columns (stack/breadcrumbs/solution) — the detail endpoint always
 *  includes them. `breadcrumbs` is the raw stored JSON string; parse with
 *  `parseTicketBreadcrumbs` (src/lib/errorCenter/ticketPresentation.ts). */
export type ErrorTicket = {
  id: number
  created_at: string
  /** Hash of message + route + top stack frame (dedup key). */
  signature: string
  user_email: string | null
  route: string | null
  title: string | null
  error: string | null
  stack?: string | null
  /** JSON: Array<{t:number, kind:'route'|'click'|'fetch-fail'|'visibility', detail:string}> */
  breadcrumbs?: string | null
  /** The R2 key itself is never returned by list/detail — they expose the
   *  `has_snapshot` 0|1 flag instead (the key stays server-side). */
  snapshot_r2_key?: string | null
  /** 1 when a sanitized page snapshot exists for this ticket. */
  has_snapshot?: number | boolean
  /** 'auto' = reliability pipeline capture; 'user' = Support-hub report
   *  (migration 194, wishlist-haiku-v1). */
  source?: 'auto' | 'user' | string
  /** The reporter's own words (source='user' submissions; null otherwise). */
  user_description?: string | null
  occurrences: number
  last_seen: string | null
  upstream_status: ErrorTicketStatus | string
  /** AI-written resolution (plaintext/markdown-ish — render SAFELY, never as HTML). */
  solution?: string | null
  model: string | null
  forwarded_at: string | null
  attempts: number
}

/** GET /api/admin/error-tickets — paged list, newest first.
 *  `status` filters by upstream_status when provided. */
export async function fetchErrorTickets(
  opts: { page?: number; pageSize?: number; status?: string } = {},
): Promise<{ tickets: ErrorTicket[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  const page = opts.page ?? 1
  const pageSize = opts.pageSize ?? 25
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  if (opts.status && opts.status !== 'all') params.set('status', opts.status)
  const r = await fetch(`${API_BASE}/admin/error-tickets?${params.toString()}`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    // Status code stays in the message — the tab detects 401/403 = admin-only.
    throw new Error(err.error ? `${err.error} [${r.status}]` : `fetchErrorTickets ${r.status}`)
  }
  const j: any = await r.json()
  return {
    tickets: j.tickets ?? j.data ?? [],
    total: typeof j.total === 'number' ? j.total : (j.tickets ?? j.data ?? []).length,
    page: typeof j.page === 'number' ? j.page : page,
    pageSize: typeof j.pageSize === 'number' ? j.pageSize : pageSize,
  }
}

/** GET /api/admin/error-tickets/:id — full ticket incl. stack, breadcrumbs,
 *  and the AI solution. */
export async function fetchErrorTicket(id: number): Promise<ErrorTicket> {
  const r = await fetch(`${API_BASE}/admin/error-tickets/${id}`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `fetchErrorTicket ${r.status}`)
  }
  const j: any = await r.json()
  return (j.ticket ?? j) as ErrorTicket
}

/** POST /api/admin/error-tickets/:id/retry — re-forward a failed/needs_human
 *  ticket to the AI solver. Returns the updated ticket state. */
export async function retryErrorTicket(id: number): Promise<{
  success?: boolean
  ticket?: ErrorTicket
  upstream_status?: string
  solution?: string | null
  error?: string
}> {
  const r = await fetch(`${API_BASE}/admin/error-tickets/${id}/retry`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `retryErrorTicket ${r.status}`)
  }
  return await r.json()
}

/** GET /api/admin/error-tickets/:id/snapshot — the sanitized page snapshot
 *  as a Blob. The server serves it as a DOWNLOAD attachment (never inline
 *  active HTML — file-serve XSS guard); the caller saves it via an object
 *  URL + <a download>. */
export async function downloadErrorTicketSnapshot(id: number): Promise<Blob> {
  const r = await fetch(`${API_BASE}/admin/error-tickets/${id}/snapshot`, {
    credentials: 'include',
  })
  if (!r.ok) throw new Error(`downloadErrorTicketSnapshot ${r.status}`)
  return await r.blob()
}

// ── Feature requests (Wish List) — admin review surface ────────────────────
// Lives beside the error-ticket admin wrappers: both feed the same
// Preferences → Administration → Error Center tab (segmented Errors |
// Feature Requests — marker: error-center-segments).

/** feature_requests.status enum (contract, wishlist-haiku-v1). */
export type FeatureRequestStatus = 'submitted' | 'forwarded' | 'acknowledged' | 'failed'

/** One row of GET /api/admin/feature-requests. `conversation` arrives
 *  already JSON.parsed server-side ([] on junk) but is typed unknown —
 *  always run it through parseFeatureRequestConversation
 *  (src/lib/errorCenter/featureRequestPresentation.ts) before rendering.
 *  `solution` is the upstream reply/acknowledgement text (render SAFELY via
 *  solutionToBlocks, never as HTML). */
export type FeatureRequestAdmin = {
  id: number
  created_at: string
  user_email: string | null
  route: string | null
  title: string | null
  /** The refined request writeup produced at intake (pre-wrap text). */
  summary: string | null
  /** Parsed intake transcript: Array<{role:'user'|'assistant', content:string}>. */
  conversation: unknown
  status: FeatureRequestStatus | string
  upstream_status: string | null
  solution?: string | null
  model: string | null
  forwarded_at: string | null
  attempts: number
}

/** GET /api/admin/feature-requests — paged list, newest first (requireAdmin
 *  server-side; mirrors the error-tickets list shape {data,total,page,
 *  pageSize}). `status` filters on the enum above when provided. */
export async function fetchFeatureRequestsAdmin(
  opts: { page?: number; pageSize?: number; status?: string } = {},
): Promise<{ requests: FeatureRequestAdmin[]; total: number; page: number; pageSize: number }> {
  const params = new URLSearchParams()
  const page = opts.page ?? 1
  const pageSize = opts.pageSize ?? 25
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  if (opts.status && opts.status !== 'all') params.set('status', opts.status)
  const r = await fetch(`${API_BASE}/admin/feature-requests?${params.toString()}`, {
    credentials: 'include',
    cache: 'no-store',
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    // Status code stays in the message — the tab detects 401/403 = admin-only.
    throw new Error(err.error ? `${err.error} [${r.status}]` : `fetchFeatureRequestsAdmin ${r.status}`)
  }
  const j: any = await r.json()
  return {
    requests: j.data ?? [],
    total: typeof j.total === 'number' ? j.total : (j.data ?? []).length,
    page: typeof j.page === 'number' ? j.page : page,
    pageSize: typeof j.pageSize === 'number' ? j.pageSize : pageSize,
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Support & feedback hub (topbar icon → modal; ANY authed user)
//
// USER-facing wrappers for the Support Hub (src/shared/SupportHub*.tsx,
// marker: support-hub-modal). Issues land in `error_tickets` (source=user)
// and ride the existing AI-solver forward path; wishes land in
// `feature_requests` and forward to Bot Pros tagged FEATURE REQUEST. The
// wishlist chat proxies Haiku server-side; a 501 {fallback:true} (no
// ANTHROPIC_API_KEY configured) is a NORMAL degraded mode, not an error —
// the modal swaps to a guided form.
// ═══════════════════════════════════════════════════════════════════════════

/** One row of GET /api/support/mine — the caller's own submissions, both
 *  kinds, newest first, capped at 50. `status` carries upstream_status for
 *  issues and feature_requests.status for features (normalized here). */
export type MySupportSubmission = {
  kind: 'issue' | 'feature'
  id: number
  created_at: string
  title: string | null
  status: string | null
  /** AI/Bot Pros answer (plaintext/markdown-ish — render SAFELY via
   *  solutionToBlocks, never as HTML). */
  solution: string | null
  model: string | null
}

/** POST /api/support/issues — user-described problem → error_tickets row
 *  (source=user) + upstream forward. Breadcrumbs ride the err-breadcrumbs-v1
 *  shape from src/reliability.ts (listActionBreadcrumbs). */
export async function submitSupportIssue(input: {
  description: string
  route?: string
  breadcrumbs?: Array<{ t: number; kind: string; detail: string }>
}): Promise<{ id: number }> {
  const r = await fetch(`${API_BASE}/support/issues`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `submitSupportIssue ${r.status}`)
  }
  const j: any = await r.json()
  return { id: Number(j.id) }
}

/** GET /api/support/mine — my submissions (issues + feature requests),
 *  newest first. Defensive: junk rows are dropped, status normalized from
 *  either `status` or `upstream_status`. */
export async function fetchMySupportSubmissions(opts: { signal?: AbortSignal } = {}): Promise<MySupportSubmission[]> {
  const r = await fetch(`${API_BASE}/support/mine`, {
    credentials: 'include',
    cache: 'no-store',
    signal: opts.signal,
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `fetchMySupportSubmissions ${r.status}`)
  }
  const j: any = await r.json()
  const rows: any[] = Array.isArray(j?.submissions) ? j.submissions : Array.isArray(j) ? j : []
  const out: MySupportSubmission[] = []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const kind = row.kind === 'feature' ? 'feature' : 'issue'
    const id = Number(row.id)
    if (!Number.isFinite(id)) continue
    out.push({
      kind,
      id,
      created_at: typeof row.created_at === 'string' ? row.created_at : '',
      title: typeof row.title === 'string' ? row.title : null,
      status:
        typeof row.status === 'string' && row.status
          ? row.status
          : typeof row.upstream_status === 'string'
            ? row.upstream_status
            : null,
      solution: typeof row.solution === 'string' ? row.solution : null,
      model: typeof row.model === 'string' ? row.model : null,
    })
  }
  return out.slice(0, 50)
}

/** POST /api/support/feature-requests — refined wish → feature_requests row
 *  + upstream forward tagged FEATURE REQUEST (no diagnostics attached). */
export async function submitFeatureRequest(input: {
  title: string
  summary: string
  /** Refinement transcript (chat) or guided-form answers. */
  conversation: Array<{ role: string; content: string }>
}): Promise<{ id: number }> {
  const r = await fetch(`${API_BASE}/support/feature-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `submitFeatureRequest ${r.status}`)
  }
  const j: any = await r.json()
  return { id: Number(j.id) }
}

export type WishlistChatMessage = { role: 'user' | 'assistant'; content: string }

export type WishlistChatResult =
  /** Haiku proxy not configured (worker has no ANTHROPIC_API_KEY yet) —
   *  degrade to the guided form. NOT an error. */
  | { fallback: true }
  | {
      fallback?: false
      reply: string
      /** true when the model emitted its final structured summary. */
      done: boolean
      refined?: { title: string; summary: string }
    }

/** POST /api/support/wishlist-chat — server-side Haiku refinement proxy.
 *  Returns {fallback:true} on 501; throws on other failures with the status
 *  embedded (the modal detects [429] = rate-limited and says so nicely).
 *  `opts.finalize` (wishlist-submit-btn) tells the server to append its
 *  finalize directive so the model wraps up into a DONE summary NOW — used by
 *  the "Submit request" button when the user is ready before the model volunteers
 *  its summary. Guardrails are unchanged server-side. */
export async function wishlistChat(
  messages: WishlistChatMessage[],
  opts?: { finalize?: boolean },
): Promise<WishlistChatResult> {
  const r = await fetch(`${API_BASE}/support/wishlist-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(opts?.finalize ? { messages, finalize: true } : { messages }),
  })
  if (r.status === 501) return { fallback: true }
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ? `${err.error} [${r.status}]` : `wishlistChat [${r.status}]`)
  }
  const j: any = await r.json()
  if (j && j.fallback === true) return { fallback: true }
  const refined =
    j && typeof j.refined === 'object' && j.refined !== null &&
    typeof j.refined.title === 'string' && typeof j.refined.summary === 'string'
      ? { title: j.refined.title, summary: j.refined.summary }
      : undefined
  return {
    reply: typeof j?.reply === 'string' ? j.reply : '',
    done: j?.done === true,
    ...(refined ? { refined } : {}),
  }
}

// ===========================================================================
// Contacts: multi-contact-person model + legacy name-fix admin job
// (feature marker: cp-name-normalize-v1). Appended as ONE end-of-file block so
// the FE-scaffold cherry-pick stays disjoint from the create-panel + detail
// agents. Pure API wrappers — all validation logic lives in
// src/lib/contacts/nameNormalize.ts.
// ===========================================================================

// ── Contact emails / phones (typed wrappers for the existing child endpoints) ──
// The old inline raw fetches stay untouched; new code uses these. person_id is
// nullable: NULL = client-level (100% back-compat), a number = attributed to a
// contact_persons row.

export type ContactEmail = {
  id: number
  contact_id: string
  address: string
  is_primary: number
  is_billing: number
  created_at: string
  person_id?: number | null
}
export type ContactPhone = {
  id: number
  contact_id: string
  number: string
  label: string | null
  ext: string | null
  is_primary: number
  created_at: string
  person_id?: number | null
}

export async function listContactEmails(contactId: string): Promise<ContactEmail[]> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/emails`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json() as Promise<ContactEmail[]>
}
export async function createContactEmail(
  contactId: string,
  input: { address: string; is_primary?: boolean; is_billing?: boolean },
): Promise<{ success: boolean; id?: number }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}
export async function updateContactEmail(
  contactId: string,
  emailId: number,
  patch: { address?: string; is_primary?: boolean; is_billing?: boolean },
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/emails/${emailId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function deleteContactEmail(contactId: string, emailId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/emails/${emailId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

export async function listContactPhones(contactId: string): Promise<ContactPhone[]> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/phones`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json() as Promise<ContactPhone[]>
}
export async function createContactPhone(
  contactId: string,
  input: { number: string; label?: string; ext?: string; is_primary?: boolean },
): Promise<{ success: boolean; id?: number }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/phones`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}
export async function updateContactPhone(
  contactId: string,
  phoneId: number,
  patch: { number?: string; label?: string; ext?: string; is_primary?: boolean },
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/phones/${phoneId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
export async function deleteContactPhone(contactId: string, phoneId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/phones/${phoneId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ── Contact persons (child rows on the client record) ──
// A named person on a client. NULL person_id emails/phones are client-level;
// GET .../persons returns each person's attributed emails/phones joined in.

export type ContactPerson = {
  id: number
  contact_id: string
  full_name: string
  first_name: string | null
  last_name: string | null
  suffix: string | null
  role_label: string | null
  is_primary: number
  /** manual | split | import */
  source: string
  note: string | null
  created_at: string
  created_by_email: string | null
  emails?: ContactEmail[]
  phones?: ContactPhone[]
}

export type CreateContactPersonInput = {
  full_name: string
  first_name?: string
  last_name?: string
  suffix?: string
  role_label?: string
  is_primary?: boolean
  emails?: string[]
  phones?: { number: string; label?: string; ext?: string }[]
}

export type UpdateContactPersonInput = {
  full_name?: string
  first_name?: string
  last_name?: string
  suffix?: string
  role_label?: string
  is_primary?: boolean
  note?: string
}

export async function listContactPersons(contactId: string): Promise<ContactPerson[]> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/persons`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json() as Promise<ContactPerson[]>
}
export async function createContactPerson(
  contactId: string,
  input: CreateContactPersonInput,
): Promise<{ success: boolean; id?: number }> {
  const resp = await fetch(`${API_BASE}/crm/contacts/${encodeURIComponent(contactId)}/persons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}
export async function updateContactPerson(
  personId: number,
  patch: UpdateContactPersonInput,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contact-persons/${personId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}
export async function deleteContactPerson(personId: number): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/contact-persons/${personId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}

// ── Legacy name-fix admin job (dry-run / apply / undo / review) ──
// All routes are requireAdmin('name-fix') gated. dry-run WRITES NOTHING.
// Originals are never destroyed — old values live in name_fix_log (undo).

export type NameFixTier = 'A' | 'B' | 'C' | 'D'
export type NameFixScope = 'contacts' | 'leads' | 'both'

export type NameFixDryRunRow = {
  id: string
  old: string
  tier: NameFixTier
  proposed?: string
  persons?: string[]
  reason?: string
  siblingIds?: string[]
}
export type NameFixDryRunResult = {
  runId: string
  counts: { A: number; B: number; C: number; D: number }
  rows: NameFixDryRunRow[]
}

export type NameFixApplyResult = {
  runId: string
  counts: { A: number; B: number; C: number; D?: number }
  /** rows whose name/persons were actually written (Tier A + B). */
  applied: number
  /** Tier C rows inserted into name_fix_review. */
  reviewQueued: number
  /** contact_persons rows created by Tier B splits. */
  personsCreated: number
  /** phone_favorites rows resynced (favorites_resync post-pass). */
  favoritesResynced?: number
  /** leads.customer_name rows swapped when includeLeads. */
  leadsUpdated?: number
}

// nf-review-render-fix-v1 — pure, unit-tested response-shape parsing for the review queue.
import { parseReviewResponse } from './lib/nameFixReview'

export type NameFixReviewRow = {
  id: number
  entity_type: string
  contact_id: string
  reason: string
  original_name: string
  suggestion_json: string | null
  // The worker (GET /api/crm/admin/name-fix/review) ALSO pre-parses suggestion_json
  // server-side and attaches it here — prefer this over re-parsing suggestion_json.
  suggestion?: { proposed?: string | null; persons?: string[] | null; display?: string | null } | null
  sibling_ids: string[] | null
  status: 'pending' | 'accepted' | 'dismissed'
  run_id: string | null
  created_at: string
  resolved_by_email: string | null
  resolved_at: string | null
}

export async function nameFixDryRun(input?: { scope?: NameFixScope; limit?: number }): Promise<NameFixDryRunResult> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/dry-run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input || {}),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json() as Promise<NameFixDryRunResult>
}
export async function nameFixApply(input: {
  tiers: Array<'A' | 'B'>
  scope?: NameFixScope
  limit?: number
  includeLeads?: boolean
}): Promise<NameFixApplyResult> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json() as Promise<NameFixApplyResult>
}
export async function nameFixUndo(input: { runId?: string; entityId?: string }): Promise<{ success: boolean; restored?: number }> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/undo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}
export async function listNameFixReview(status: 'pending' | 'accepted' | 'dismissed' = 'pending'): Promise<NameFixReviewRow[]> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/review?status=${encodeURIComponent(status)}`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const j: unknown = await resp.json()
  // nf-review-render-fix-v1 — the worker responds `{ reviews: [...] }`, NOT `{ rows: [...] }`;
  // parsing is a unit-tested pure helper (src/lib/nameFixReview.ts) so this contract can't
  // silently drift again (was rendering "(0) Nothing pending review" with 162 rows live).
  return parseReviewResponse(j) as NameFixReviewRow[]
}
export async function resolveNameFixReview(id: number, action: 'accept' | 'dismiss'): Promise<{ success: boolean }> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/review/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json()
}

// ── Extended review-only scan (nf-scan-extended-v1) ──
// Queues two NEW candidate classes into the SAME name_fix_review queue: CLASS 1
// no-comma "Last First" swaps ("Friedsam Ron"→"Ron Friedsam") and CLASS 2 couples
// mis-typed as organizations ("Craig & Lisa Lake"). WRITES ONLY name_fix_review
// (idempotent INSERT OR IGNORE) — never changes a name. requireKnownSurname raises
// Class-1 precision (~92%, fewer rows). dryRun previews without writing.
export type NameFixExtClass = 'nocomma_swap' | 'org_couple'
export type NameFixScanExtendedRow = {
  id: string
  old: string
  class: NameFixExtClass
  proposed?: string
  persons?: string[]
  display?: string
  surnameHits?: number
  surnameConfirmed?: boolean
}
export type NameFixScanExtendedResult = {
  runId: string
  dryRun: boolean
  requireKnownSurname: boolean
  counts: { nocomma_swap: number; org_couple: number }
  /** name_fix_review rows newly inserted (0 when dryRun). */
  inserted: number
  rows: NameFixScanExtendedRow[]
}
export async function nameFixScanExtended(input?: {
  requireKnownSurname?: boolean
  dryRun?: boolean
  includeAnd?: boolean
}): Promise<NameFixScanExtendedResult> {
  const resp = await fetch(`${API_BASE}/crm/admin/name-fix/scan-extended`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input || {}),
  })
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({}) as { error?: string })).error || `API error: ${resp.status}`)
  return resp.json() as Promise<NameFixScanExtendedResult>
}

export async function uploadOrgLogo(id: string, file: File, shape: 'circle' | 'rect' = 'circle'): Promise<{ ok: boolean; logo_url: string | null; logo_shape: 'circle' | 'rect' }> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('shape', shape)
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(String(id))}/logo`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })
  if (!resp.ok) {
    const body = await resp.text().catch(() => '')
    throw new Error(body || `API error: ${resp.status}`)
  }
  return resp.json()
}

export async function deleteOrgLogo(id: string): Promise<{ ok: boolean; logo_shape?: 'circle' }> {
  const resp = await fetch(`${API_BASE}/crm/clients/${encodeURIComponent(String(id))}/logo`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!resp.ok) {
    const body = await resp.text().catch(() => '')
    throw new Error(body || `API error: ${resp.status}`)
  }
  return resp.json()
}

export type PortalOrgFile = { id: number; filename: string; size_bytes: number | null; object_type: string; record_id: string | number; portal_shared: number; portal_shared_at: string | null }

export async function fetchPortalOrgFiles(orgId: string): Promise<PortalOrgFile[]> {
  const resp = await fetch(`${API_BASE}/crm/portal/org-files/${encodeURIComponent(orgId)}`)
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  const body = await resp.json() as { files?: PortalOrgFile[] }
  return body.files || []
}

export async function togglePortalFileShare(fileId: number): Promise<{ ok: boolean; portal_shared: boolean; portal_shared_at: string | null }> {
  const resp = await fetch(`${API_BASE}/crm/files/${fileId}/portal-share`, { method: 'POST' })
  if (!resp.ok) throw new Error(`API error: ${resp.status}`)
  return resp.json()
}
